import React, { useEffect, useMemo, useRef, useState } from 'react';
import Avatar from '../../Components/DesignSystem/Avatar/Avatar';
import Typography from '../../Components/DesignSystem/Typography/Typography';
import Pagination from "../../Components/DesignSystem/Pagination/Pagination";
import Button from '../../Components/DesignSystem/Button/Button';
import Badge from '../../Components/DesignSystem/Badge/Badge';
import Divider from '../../Components/DesignSystem/Divider/Divider';
import FieldTitle from '../../Components/DesignSystem/FieldTitle/FieldTitle';
import TextField from '../../Components/DesignSystem/TextField/TextField';
import Dropdown from '../../Components/DesignSystem/Dropdown/Dropdown';
import Combobox from '../../Components/DesignSystem/Combobox/Combobox';
import { ComboboxOption } from '../../types/DesignSystem/Combobox';
import Progress from '../../Components/DesignSystem/Progress/Progress';
import Sparkline from '../../Components/DesignSystem/Sparkline/Sparkline';
import Delta from '../../Components/DesignSystem/Delta/Delta';
import Stat from '../../Components/DesignSystem/Stat/Stat';
import DistributionBar from '../../Components/DesignSystem/DistributionBar/DistributionBar';
import IconButton from '../../Components/DesignSystem/IconButton/IconButton';
import ButtonLink from '../../Components/DesignSystem/Button/ButtonLink';
import Link from '../../Components/DesignSystem/Link/Link';
import FileUpload from '../../Components/DesignSystem/FileUpload/FileUpload';
import { Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon, Copy as CopyIcon } from '../../Components/DesignSystem/Iconography/Icons';
import Toggle from '../../Components/DesignSystem/Toggle/Toggle';
import Checkbox from '../../Components/DesignSystem/Checkbox/Checkbox';
import Radio from '../../Components/DesignSystem/Radio/Radio';
import Tab from '../../Components/DesignSystem/Tab/Tab';
import Search from '../../Components/DesignSystem/Search/Search';
import Menu from '../../Components/DesignSystem/Menu/Menu';
import Kbd from '../../Components/DesignSystem/Kbd/Kbd';
import Popover from '../../Components/DesignSystem/Popover/Popover';
import DropdownButton from '../../Components/DesignSystem/DropdownButton/DropdownButton';
import NumberField from '../../Components/DesignSystem/NumberField/NumberField';
import ConfirmDialog from '../../Components/DesignSystem/ConfirmDialog/ConfirmDialog';
import CommandPalette from '../../Components/DesignSystem/CommandPalette/CommandPalette';
import { CommandPaletteCommand } from '../../types/DesignSystem/CommandPalette';
import Dialog from '../../Components/DesignSystem/Dialog/Dialog';
import SidePanel from '../../Components/DesignSystem/SidePanel/SidePanel';
import { NewGearIcon } from '../../assests/GmsIcons';
import { CrossCircle, Cross, Settings, ArrowLeft, ArrowRight, ExternalLink, MoreVertical } from '../../Components/DesignSystem/Iconography/Icons';
import Status from '../../Components/DesignSystem/Status/Status';
import { toast } from '../../Components/DesignSystem/Toast/useToast';
import Card from '../../Components/DesignSystem/Card/Card';
import Alert from '../../Components/DesignSystem/Alert/Alert';
import ErrorSummary, { ErrorSummaryScope } from '../../Components/DesignSystem/ErrorSummary/ErrorSummary';
import Textarea from '../../Components/DesignSystem/Textarea/Textarea';
import Breadcrumbs from '../../Components/DesignSystem/Breadcrumbs/Breadcrumbs';
import Tag from '../../Components/DesignSystem/Tag/Tag';
import PageSkeleton from '../../Components/DesignSystem/PageSkeleton/PageSkeleton';
import EmptyState from '../../Components/DesignSystem/EmptyState/EmptyState';
import NoDataIllustration from '../../Components/DesignSystem/EmptyState/NoDataIllustration';
import ImagePlaceholder from '../../Components/DesignSystem/ImagePlaceholder/ImagePlaceholder';
import SegmentedControl from '../../Components/DesignSystem/SegmentedControl/SegmentedControl';
import Slider from '../../Components/DesignSystem/Slider/Slider';
import ChoiceChip from '../../Components/DesignSystem/ChoiceChip/ChoiceChip';
import Accordion from '../../Components/DesignSystem/Accordion/Accordion';
import Tooltip from '../../Components/DesignSystem/Tooltip/Tooltip';
import DatePicker from '../../Components/DesignSystem/DatePicker/DatePicker';
import TimePicker from '../../Components/DesignSystem/TimePicker/TimePicker';
import ErrorState from '../../Components/DesignSystem/ErrorState/ErrorState';
import Spinner from '../../Components/DesignSystem/Spinner/Spinner';
import Skeleton from '../../Components/DesignSystem/Skeleton/Skeleton';
import * as DsIcons from '../../Components/DesignSystem/Iconography/Icons';
import * as AppIconActions from '../../Components/DesignSystem/Iconography/app/actions';
import * as AppIconNavigation from '../../Components/DesignSystem/Iconography/app/navigation';
import * as AppIconStatus from '../../Components/DesignSystem/Iconography/app/status';
import * as AppIconFields from '../../Components/DesignSystem/Iconography/app/fields';
import * as AppIconDomain from '../../Components/DesignSystem/Iconography/app/domain';
import * as AppIconLayout from '../../Components/DesignSystem/Iconography/app/layout';
import * as AppIconCharts from '../../Components/DesignSystem/Iconography/app/charts';
import * as AppIconMedia from '../../Components/DesignSystem/Iconography/app/media';

/* § 333 — the app icon set, by job, in the order a reader scans for one. */
const APP_ICON_GROUPS: { id: string; label: string; icons: Record<string, unknown> }[] = [
  { id: 'actions', label: 'Actions', icons: AppIconActions },
  { id: 'navigation', label: 'Navigation', icons: AppIconNavigation },
  { id: 'status', label: 'Status', icons: AppIconStatus },
  { id: 'fields', label: 'Field types', icons: AppIconFields },
  { id: 'domain', label: 'Catalogue & models', icons: AppIconDomain },
  { id: 'layout', label: 'Layout & builder', icons: AppIconLayout },
  { id: 'charts', label: 'Charts', icons: AppIconCharts },
  { id: 'media', label: 'Media', icons: AppIconMedia },
];
const iconEntries = (mod: Record<string, unknown>) =>
  Object.entries(mod)
    .filter(([, value]) => typeof value === 'function')
    .sort(([a], [b]) => a.localeCompare(b)) as [string, React.ComponentType<Record<string, unknown>>][];
import { SEMANTIC_TONES } from '../../types/DesignSystem/tone';
import { StatusTone, StatusVariant } from '../../types/DesignSystem/Status';
import { ControlSize } from '../../types/DesignSystem/controlSize';
import Table from '../../Components/DesignSystem/Table/Table';
import SelectionBar from '../../Components/DesignSystem/SelectionBar/SelectionBar';
import RadioGroup from '../../Components/DesignSystem/RadioGroup/RadioGroup';
import CheckboxGroup from '../../Components/DesignSystem/CheckboxGroup/CheckboxGroup';
import Stepper from '../../Components/DesignSystem/Stepper/Stepper';
import TreeView from '../../Components/DesignSystem/TreeView/TreeView';
import type { TreeNode } from '../../types/DesignSystem/TreeView';
import { DEBOUNCE } from '../../Components/DesignSystem/debounce';
import { TableColumn, SortDirection, TableSortState } from '../../types/DesignSystem/Table';
import { SidePanelTabConfig } from '../../types/DesignSystem/SidePanel';
import { MenuItem } from '../../types/DesignSystem/Menu';
import { applyResolvedTheme, readThemePreference, resolveTheme, systemPrefersDark } from '../../Components/DesignSystem/theme';
import type { ThemePreference } from '../../Components/DesignSystem/theme';
import {
  PALETTE, FAMILY_ORDER, FAMILY_LABEL, ROLE, ROLES, GROUND, SAFETY_LABEL,
  DATAVIZ_SERIES,
  contrast, textSafety,
} from '../../Components/DesignSystem/palette';
import { LAYERS, LAYER_ORDER } from '../../Components/DesignSystem/layers';
import DocBlock, { FoundationBlock } from './DocBlock';
import DensityProvider from '../../Components/DesignSystem/DensityProvider/DensityProvider';

/**
 * The page's information architecture (§ 171).
 *
 * It used to be a flat scroll of 27 sections in the order they happened to be written:
 * Textarea sat between Alert and Toast, five screens away from the text field it is a
 * variant of; Card came after Overlays; Divider between Status and Form Inputs. Nothing
 * was wrong with any individual section and the page was still unusable, because finding
 * anything meant scrolling 3,000 lines and knowing the commit history.
 *
 * These seven groups are the ONE source the contents rail renders from, and
 * `showcase.ia.test.tsx` asserts every `id` here resolves to a section actually on the
 * page — so a section cannot be added, renamed or dropped without the rail following.
 */
export type ShowcaseSection = { id: string; label: string };
export type ShowcaseGroup = {
  id: string;
  label: string;
  blurb: string;
  sections: ShowcaseSection[];
};

export const SHOWCASE_GROUPS: ShowcaseGroup[] = [
  {
    id: 'foundations',
    label: 'Foundations',
    blurb:
      'The values every component is built from. Change one of these and the whole app moves.',
    sections: [
      { id: 'colour', label: 'Colour' },
      { id: 'typography', label: 'Typography' },
      { id: 'elevation', label: 'Elevation & shape' },
      { id: 'iconography', label: 'Iconography' },
      { id: 'control-sizing', label: 'Control sizing' },
      { id: 'density', label: 'Density' },
      { id: 'motion', label: 'Keyboard, focus & motion' },
      { id: 'api-conventions', label: 'Component API' }
    ],
  },
  {
    id: 'actions',
    label: 'Actions',
    blurb:
      'Things a user presses. Exactly one primary action per surface.',
    sections: [
      { id: 'buttons', label: 'Buttons' },
      { id: 'icon-button', label: 'Icon button' },
      { id: 'dropdown-button', label: 'Dropdown button' },
      { id: 'segmented-control', label: 'Segmented control' }
    ],
  },
  {
    id: 'inputs',
    label: 'Inputs',
    blurb:
      'Everything that collects a value. All of it shares one control grid and one field chrome.',
    sections: [
      { id: 'text-fields', label: 'Text fields' },
      { id: 'textarea', label: 'Textarea' },
      { id: 'search', label: 'Search' },
      { id: 'combobox', label: 'Combobox' },
      { id: 'choice-controls', label: 'Toggle, checkbox & radio' },
      { id: 'slider', label: 'Slider' },
      { id: 'date-time', label: 'Date & time' },
      { id: 'file-upload', label: 'File upload' }
    ],
  },
  {
    id: 'navigation',
    label: 'Navigation',
    blurb:
      'Moving between views, pages and records.',
    sections: [
      { id: 'tabs', label: 'Tabs' },
      { id: 'menu', label: 'Menu' },
      { id: 'kbd', label: 'Kbd' },
      { id: 'breadcrumbs', label: 'Breadcrumbs' },
      { id: 'tree-view', label: 'Tree view' },
      { id: 'link', label: 'Link' },
      { id: 'pagination', label: 'Pagination' }
    ],
  },
  {
    id: 'data',
    label: 'Data display',
    blurb:
      'Presenting records the user reads rather than edits.',
    sections: [
      { id: 'table', label: 'Table' },
      { id: 'card', label: 'Card' },
      { id: 'accordion', label: 'Accordion' },
      { id: 'divider', label: 'Divider' },
      { id: 'tag', label: 'Tag & choice chip' },
      { id: 'avatar', label: 'Avatar' },
      { id: 'data-expression', label: 'Data expression' }
    ],
  },
  {
    id: 'feedback',
    label: 'Status & feedback',
    blurb:
      'Telling the user what happened, what is happening, and what is missing.',
    sections: [
      { id: 'status', label: 'Status' },
      { id: 'badge', label: 'Badge' },
      { id: 'alert', label: 'Alert' },
      { id: 'error-summary', label: 'Error summary' },
      { id: 'toast', label: 'Toast' },
      { id: 'tooltip', label: 'Tooltip' },
      { id: 'loading', label: 'Loading' },
      { id: 'skeleton', label: 'Spinner & skeleton' },
      { id: 'progress', label: 'Progress & stepper' },
      { id: 'empty-states', label: 'Empty states' },
      { id: 'error-state', label: 'Error state' }
    ],
  },
  {
    id: 'overlays',
    label: 'Overlays',
    blurb:
      'Surfaces that sit above the page and take focus with them.',
    sections: [
      { id: 'overlays', label: 'Dialog & side panel' },
      { id: 'popover', label: 'Popover' },
      { id: 'command-palette', label: 'Command palette' }
    ],
  },
  {
    id: 'appendix',
    label: 'Appendix',
    blurb:
      'Choices still open, kept where they can be judged by eye instead of argued from memory.',
    sections: [
      { id: 'open-decisions', label: 'Open decisions' }
    ],
  },
];

/**
 * Section chrome, in DS tokens.
 *
 * Every one of the 27 sections used to carry `tw-rounded-lg tw-shadow-sm` — Tailwind
 * stock: a radius outside the semantic ramp and a pure-black shadow, where DS
 * elevation is tinted with grey-900 because black over blue-grey neutrals reads
 * muddy. The page demonstrating the system was its loudest counter-example, 54
 * classes deep. (Since § 217 re-differentiated the ramp, `srf` happens to BE 8px —
 * but it gets there through the token, which is the entire point.)
 *
 * Border, not shadow. These are flat surfaces on a sunken ground, and 27 stacked
 * shadows is noise — elevation is reserved for things that genuinely float, which on
 * this page means Dialog, SidePanel, Menu and Toast.
 */
const SECTION =
  'tw-scroll-mt-4 tw-mb-6 tw-rounded-srf tw-border tw-border-edge tw-bg-surface tw-p-8';

/** § 395 — a product record's views, more of them than a 384px column holds. */
const OVERFLOW_TAB_ITEMS = ['Overview', 'Attributes', 'Relationships', 'Media', 'Pricing', 'Inventory', 'Channels', 'History', 'Settings']
  .map((label) => ({ id: label.toLowerCase(), label, 'data-testid': `showcase-tabs-overflow-${label.toLowerCase()}` }));

/**
 * Air above a section once it has been jumped to. `scroll-mt-4` on the section and this
 * are the SAME 16px on purpose: the class serves the native anchor fallback, this serves
 * the JS path, and a spec that lands differently depending on which one ran is a bug
 * nobody would think to look for.
 */
const SCROLL_OFFSET = 16;

/*
 * The SidePanel demo's tabs, passed explicitly (design-system § 348). The demo used to
 * pass none and show the Environment editor's Basic Info / Commend / Guide, which were
 * the drawer's default until then — a product's tab row demonstrated as the component's.
 */
type ShowcasePanelTab = 'details' | 'activity';
const SHOWCASE_PANEL_TABS: SidePanelTabConfig<ShowcasePanelTab>[] = [
  { id: 'details', label: 'Details' },
  { id: 'activity', label: 'Activity' },
];

/* The Menu submenu demo — one parent, two children. */
const SHOWCASE_SUBMENU_ITEMS: MenuItem[] = [
  { id: 'rename', label: 'Rename', 'data-testid': 'showcase-submenu-rename' },
  {
    id: 'export',
    label: 'Export',
    'data-testid': 'showcase-submenu-export',
    submenu: [
      { id: 'csv', label: 'CSV', 'data-testid': 'showcase-submenu-csv' },
      { id: 'xlsx', label: 'Excel workbook', 'data-testid': 'showcase-submenu-xlsx' },
    ],
  },
];

const prefersReducedMotion = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const ALL_SECTION_IDS = SHOWCASE_GROUPS.flatMap((group) => group.sections.map((s) => s.id));

/** The eyebrow + rule that opens a group. Not a heading level — see § 171. */
/* § 249 — sample portrait for the Avatar exhibit: an inline SVG data URI, so the
   showcase fetches nothing and the "src loads" case cannot flake on a network. The
   two fills are the brand navy-800 and navy-150 — artwork, not chrome. */
const AVATAR_SAMPLE = `data:image/svg+xml;utf8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" fill="#0a1f80"/><circle cx="20" cy="15" r="7" fill="#c2d4fe"/><path d="M6 36c2-9 8-13 14-13s12 4 14 13z" fill="#c2d4fe"/></svg>',
)}`;

const GroupHeading = ({
  id, label, blurb, first,
}: { id: string; label: string; blurb: string; first?: boolean }) => (
  <div
    id={`group-${id}`}
    data-testid={`showcase-group-${id}`}
    className={`tw-mb-4 ${first ? '' : 'tw-mt-14'}`}
  >
    <div className="tw-flex tw-items-center tw-gap-3">
      <span className="tw-text-sub-title-label tw-uppercase tw-tracking-wider tw-text-ink-tertiary">
        {label}
      </span>
      <span className="tw-h-px tw-flex-1 tw-bg-edge" aria-hidden="true" />
    </div>
    <p className="tw-mt-2 tw-max-w-[68ch] tw-text-body-primary tw-text-ink-secondary">{blurb}</p>
  </div>
);

/*
 * ── The role layer's display (§§ 205-207) ─────────────────────────────────────
 * Every demo paints from the LIVE `--ds-*` variable (inline style, the same
 * scanner reasoning as the palette swatches: a class cannot be built from a
 * template string), so the page's Light/Dark toggle flips these blocks with
 * everything else. The printed hexes come from `ROLES`, which
 * `semanticTokens.test.ts` pins to tokens.css and the config in every direction.
 */
const roleVar = (role: string) => `var(--ds-${role})`;

/** An ink on a ground — the pairing IS the demo. */
const RolePair = ({ bg, fg }: { bg: string; fg: string }) => (
  <div
    className="tw-h-full tw-flex tw-items-center tw-justify-center"
    style={{ backgroundColor: roleVar(bg), color: roleVar(fg) }}
  >
    <span className="tw-text-title">Aa</span>
  </div>
);

const roleDemo = (role: string): React.ReactNode => {
  if (role === 'scrim') {
    return (
      <div className="tw-relative tw-h-full">
        <div
          className="tw-h-full tw-flex tw-items-center tw-justify-center tw-text-caption-metadata"
          style={{ backgroundColor: 'var(--ds-surface)', color: 'var(--ds-ink)' }}
        >
          the page behind
        </div>
        <div
          className="tw-absolute tw-inset-0"
          style={{ backgroundColor: roleVar(role), backdropFilter: 'blur(4px)' }}
        />
      </div>
    );
  }
  /* § 207 — the selection pair itself, so the retune is visible as it is used. */
  if (role === 'accent-tint') return <RolePair bg="accent-tint" fg="accent" />;
  if (role === 'accent-bold' || role === 'accent-bold-hover') return <RolePair bg={role} fg="ink-inverse" />;
  if (role === 'ink-inverse') return <RolePair bg="surface-inverse" fg="ink-inverse" />;
  /* § 254 — the code well, shown as the pair it is used as. */
  if (role === 'code-surface' || role === 'code-ink') return <RolePair bg="code-surface" fg="code-ink" />;
  /* The Embed code snippet viewer's fixed dark terminal, shown as the pair it is used
     as — the one card in this row that looks the same with the Light/Dark toggle off,
     which is the point (see the roles' own note in palette.ts). */
  if (role === 'code-embed-surface' || role === 'code-embed-ink') return <RolePair bg="code-embed-surface" fg="code-embed-ink" />;
  /* § 301 — the tooltip bubble, drawn AS the bubble on the page surface: ground, ink and
     the chrome token together, because the ring and the elevation are what separate a
     raised bubble from a raised page — a flat swatch of the pair would show nothing. */
  if (role === 'tooltip-surface' || role === 'tooltip-ink') {
    return (
      <div className="tw-h-full tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: 'var(--ds-surface)' }}>
        <span className="tw-bg-tooltip-surface tw-text-tooltip-ink tw-shadow-ds-tooltip tw-rounded-ctl tw-px-2 tw-py-1 tw-text-caption-metadata">
          Explains the field
        </span>
      </div>
    );
  }
  /* § 295 — the Skeleton's two stops, shown as the sweep they draw: the band frozen
     mid-bar, so the card answers the question that mattered — is the middle LIGHTER
     than the ends in this theme? (It was not, in dark.) */
  if (role === 'skeleton-base' || role === 'skeleton-sheen') {
    return (
      <div className="tw-h-full tw-flex tw-items-center tw-px-4" style={{ backgroundColor: 'var(--ds-surface)' }}>
        <div
          className="tw-h-3 tw-w-full tw-rounded-sm"
          style={{ backgroundImage: 'linear-gradient(to right, var(--ds-skeleton-base), var(--ds-skeleton-sheen), var(--ds-skeleton-base))' }}
        />
      </div>
    );
  }
  /* § 310 — a series colour is only ever seen as a MARK on a ground: a 1.5px line, a
     6px bar, a 5px dot. It is never a field of colour. So the card draws it at the
     weight a chart actually uses, on `surface` — the ground its 3:1 floor was measured
     against. A filled swatch would flatter a value that has to survive as a hairline,
     which is the whole question this card exists to answer. */
  if (role.startsWith('dataviz-')) {
    return (
      <div className="tw-h-full tw-flex tw-items-center tw-px-3 tw-gap-3" style={{ backgroundColor: 'var(--ds-surface)' }}>
        <svg width="56" height="22" viewBox="0 0 56 22" aria-hidden="true" className="tw-shrink-0">
          <path
            d="M2 17 L13 12 L24 14 L35 6 L46 8 L54 3"
            fill="none"
            stroke={roleVar(role)}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="54" cy="3" r="2.5" fill={roleVar(role)} />
        </svg>
        <span className="tw-h-1.5 tw-w-8 tw-rounded-full tw-shrink-0" style={{ backgroundColor: roleVar(role) }} />
      </div>
    );
  }
  if (role.startsWith('ink') || role === 'accent' || role === 'accent-hover') return <RolePair bg="surface" fg={role} />;
  if (role.startsWith('edge')) {
    return (
      <div className="tw-h-full tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: 'var(--ds-surface)' }}>
        <div className="tw-h-8 tw-w-24 tw-rounded-ctl" style={{ border: `2px solid ${roleVar(role)}` }} />
      </div>
    );
  }
  return <div className="tw-h-full" style={{ backgroundColor: roleVar(role) }} />;
};

const ROLE_GROUPS: { label: string; roles: string[] }[] = [
  { label: 'Ink', roles: ['ink', 'ink-secondary', 'ink-tertiary', 'ink-disabled', 'ink-inverse'] },
  { label: 'Grounds', roles: ['surface', 'surface-raised', 'surface-sunken', 'surface-hover', 'surface-hover-strong', 'surface-recess', 'surface-inverse'] },
  { label: 'Edges', roles: ['edge', 'edge-strong'] },
  { label: 'Interactive', roles: ['accent', 'accent-hover', 'accent-tint', 'accent-bold', 'accent-bold-hover'] },
  /* § 366 — the focus indicator's own role: the accent's value today, its own name. */
  { label: 'Focus', roles: ['focus'] },
  { label: 'Code', roles: ['code-surface', 'code-ink', 'code-embed-surface', 'code-embed-ink'] },
  { label: 'Tooltip', roles: ['tooltip-surface', 'tooltip-ink'] },
  { label: 'Skeleton', roles: ['skeleton-base', 'skeleton-sheen'] },
  /* § 310 — the categorical series, in the order `DATAVIZ_SERIES` states, because the
     order IS the token: consecutive entries are the pair a two-series chart shows. */
  { label: 'Data series', roles: [...DATAVIZ_SERIES] },
  { label: 'Overlay', roles: ['scrim'] },
];

const RoleCard = ({ role }: { role: string }) => (
  <div
    data-testid={`showcase-role-${role}`}
    className="tw-w-44 tw-rounded-ctl tw-border tw-border-edge tw-overflow-hidden"
  >
    <div className="tw-h-16">{roleDemo(role)}</div>
    <div className="tw-p-2 tw-border-t tw-border-edge">
      <code className="tw-block tw-text-sub-title-label tw-text-ink">{role}</code>
      <code
        className="tw-block tw-mt-1 tw-text-caption-metadata tw-text-ink-secondary"
        data-testid={`showcase-role-${role}-hex`}
      >
        {ROLES[role].hex} · {ROLES[role].dark}
      </code>
    </div>
  </div>
);

/* The tone matrix (§ 206): six tones × four jobs, each cell the real pairing. */
const MATRIX_TONES = [...SEMANTIC_TONES, 'accent'];
const MATRIX_STEPS = ['tint', 'ink', 'glyph', 'bold'] as const;

/* § 304 — the portal demo's Dropdown options. */
const PORTAL_SUPPLIERS = [
  { value: 'acme', label: 'Acme Fasteners' },
  { value: 'north', label: 'Northfield Hardware' },
  { value: 'baltic', label: 'Baltic Bolt Co.' },
];

/* Combobox (§ 210) demo data — realistic parts, per the sample-data house rule. */
const PART_OPTIONS: ComboboxOption[] = [
  { value: 'hex-bolt', label: 'Hex Bolt M8×40' },
  { value: 'lock-nut', label: 'Nylon Lock Nut M8' },
  { value: 'flat-washer', label: 'Flat Washer M8' },
  { value: 'wedge-anchor', label: 'Wedge Anchor 3/8"' },
  { value: 'deck-screw', label: 'Deck Screw #10×3' },
  { value: 'drop-anchor', label: 'Drop-In Anchor M10', disabled: true },
  { value: 'threaded-rod', label: 'Threaded Rod M12×1m' },
  { value: 'carriage-bolt', label: 'Carriage Bolt M6×50' },
];

/*
 * Error summary demo: a two-field invite form. Nothing is shown until the first submit; after
 * it, the list follows the fields as they are corrected (without taking focus), and each
 * failed submit moves focus back to the summary.
 */
const ROLE_OPTIONS = [
  { value: 'editor', label: 'Editor' },
  { value: 'viewer', label: 'Viewer' },
];
const ErrorSummaryDemo = () => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string | number | undefined>(undefined);
  const [submitCount, setSubmitCount] = useState(0);
  const emailError = email.includes('@') ? undefined : 'Enter a work email, like name@company.com';
  const roleError = role ? undefined : 'Choose a role';
  const errors = submitCount === 0
    ? []
    : [
      ...(emailError ? [{ id: 'showcase-es-email', message: emailError }] : []),
      ...(roleError ? [{ id: 'showcase-es-role', message: roleError }] : []),
    ];
  const shown = (message: string | undefined) => (submitCount > 0 ? message : undefined);
  return (
    <ErrorSummaryScope>
      <form
        noValidate
        className="tw-max-w-md tw-space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitCount((count) => count + 1);
          if (!emailError && !roleError) toast.success('Invitation sent');
        }}
        data-testid="showcase-es-form"
      >
        <ErrorSummary errors={errors} submitCount={submitCount} data-testid="showcase-error-summary" />
        <TextField
          id="showcase-es-email"
          label="Work email"
          type="email"
          helperText="The invite goes to this address."
          value={email}
          onChange={setEmail}
          errorMessage={shown(emailError)}
          data-testid="showcase-es-email"
        />
        <RadioGroup
          id="showcase-es-role"
          label="Role"
          options={ROLE_OPTIONS}
          value={role}
          onChange={setRole}
          errorMessage={shown(roleError)}
          data-testid="showcase-es-role"
        />
        <Button type="submit" variant="primary" data-testid="showcase-es-submit">Send invite</Button>
      </form>
    </ErrorSummaryScope>
  );
};

/* § 372 — the Stepper demo: a three-step wizard the page can walk, and the same steps as a rail. */
const WIZARD_STEPS = [
  { id: 'target', label: 'Choose target', description: 'Bearings › Radial' },
  { id: 'attributes', label: 'Map attributes', description: '12 of 14 mapped' },
  { id: 'review', label: 'Review', description: 'Confirm and recategorize' },
];
const StepperDemo = () => {
  const [step, setStep] = useState('attributes');
  const index = WIZARD_STEPS.findIndex((s) => s.id === step);
  return (
    <div className="tw-space-y-6">
      <div className="tw-space-y-4">
        <Stepper steps={WIZARD_STEPS} current={step} onStepChange={setStep} data-testid="showcase-stepper" />
        <div className="tw-flex tw-gap-3">
          <Button variant="secondary" size="sm" disabled={index === 0} onClick={() => setStep(WIZARD_STEPS[index - 1].id)} data-testid="showcase-stepper-back">
            Back
          </Button>
          <Button size="sm" disabled={index === WIZARD_STEPS.length - 1} onClick={() => setStep(WIZARD_STEPS[index + 1].id)} data-testid="showcase-stepper-next">
            Next
          </Button>
        </div>
      </div>
      <div className="tw-max-w-xs">
        <Stepper
          steps={WIZARD_STEPS}
          current="attributes"
          orientation="vertical"
          aria-label="Progress, as a side rail"
          data-testid="showcase-stepper-vertical"
        />
      </div>
    </div>
  );
};

/* § 373 — the TreeView demo: a category hierarchy with one branch that loads when opened,
   and one category you cannot pick that still opens. The load is simulated, and only runs
   when a user opens the branch, so the page renders the same every time. */
const CATEGORY_TREE: TreeNode[] = [
  {
    id: 'bearings', label: 'Bearings', children: [
      {
        id: 'radial', label: 'Radial', children: [
          { id: 'deep-groove', label: 'Deep groove ball bearings' },
          { id: 'angular-contact', label: 'Angular contact bearings' },
        ],
      },
      { id: 'thrust', label: 'Thrust', children: [{ id: 'thrust-ball', label: 'Thrust ball bearings' }] },
      { id: 'mounted', label: 'Mounted units (not sold)', disabled: true, children: [{ id: 'pillow-block', label: 'Pillow block units' }] },
    ],
  },
  { id: 'fasteners', label: 'Fasteners', hasChildren: true },
  { id: 'seals', label: 'Seals', children: [{ id: 'o-rings', label: 'O-rings' }, { id: 'lip-seals', label: 'Lip seals' }] },
];
const FASTENER_CHILDREN: TreeNode[] = [
  { id: 'bolts', label: 'Bolts' },
  { id: 'nuts', label: 'Nuts' },
  { id: 'washers', label: 'Washers' },
];
const TreeViewDemo = () => {
  const [open, setOpen] = useState<string[]>(['bearings', 'radial']);
  const [selected, setSelected] = useState<string | null>('deep-groove');
  const [fastenersLoaded, setFastenersLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const nodes = useMemo(
    () => CATEGORY_TREE.map((node) => (node.id === 'fasteners' && fastenersLoaded ? { ...node, children: FASTENER_CHILDREN } : node)),
    [fastenersLoaded],
  );
  const onExpandedChange = (keys: string[]) => {
    setOpen(keys);
    if (keys.includes('fasteners') && !fastenersLoaded && !loading) {
      setLoading(true);
      timer.current = window.setTimeout(() => { setFastenersLoaded(true); setLoading(false); }, 900);
    }
  };
  return (
    <div className="tw-flex tw-flex-wrap tw-items-start tw-gap-6">
      <div className="tw-w-72">
        <h4 id="showcase-tree-title" className="tw-text-title-header tw-text-ink-tertiary tw-mb-2">Categories</h4>
        <div className="tw-border tw-border-edge tw-rounded-srf tw-p-1">
          <TreeView
            aria-labelledby="showcase-tree-title"
            nodes={nodes}
            expandedKeys={open}
            onExpandedChange={onExpandedChange}
            loadingKeys={loading ? ['fasteners'] : []}
            selectedKey={selected}
            onSelectionChange={setSelected}
            data-testid="showcase-tree"
          />
        </div>
      </div>
      <Typography variant="caption-metadata" className="tw-block tw-max-w-sm tw-text-ink-tertiary">
        Selected: <code data-testid="showcase-tree-selected">{selected}</code>. Tab lands on one node;
        arrows walk the open branches, Right and Left open and close them, Enter or Space picks,
        a letter jumps to the next category starting with it, and * opens every sibling.
        Fasteners loads its children when it first opens. Mounted units cannot be picked,
        and still opens.
      </Typography>
    </div>
  );
};

const toneCellStyle = (tone: string, step: (typeof MATRIX_STEPS)[number]): React.CSSProperties => {
  const v = (s: string) => `var(--ds-tone-${tone}-${s})`;
  switch (step) {
    case 'tint': return { backgroundColor: v('tint'), color: v('ink') };
    case 'ink': return { backgroundColor: 'var(--ds-surface)', color: v('ink') };
    case 'glyph': return { backgroundColor: 'var(--ds-surface)', color: v('glyph') };
    case 'bold': return { backgroundColor: v('bold'), color: 'var(--ds-ink-inverse)' };
  }
};

const DesignSystemShowcase = () => {
  const [chosenValues, setChosenValues] = useState<string[]>(['acorn']);
  const toggleChosen = (id: string) =>
    setChosenValues((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  const [sortDirection, setSortDirection] = useState<string | number>('asc');
  const [productTypeDemo, setProductTypeDemo] = useState<string | number>('all');
  const [groupedCategory, setGroupedCategory] = useState<string | number>('mobile');
  const [textareaValue, setTextareaValue] = React.useState('');
  const [textareaCounted, setTextareaCounted] = React.useState('Ships in 2-3 weeks.');
  /* Combobox (§ 210) demo state. The async demo simulates a 450ms server round
     trip; the timer ref debounces it the way a real caller would. */
  const [comboValue, setComboValue] = React.useState<string | null>(null);
  const [comboValues, setComboValues] = React.useState<string[]>(['hex-bolt', 'flat-washer']);
  const [comboAsyncOptions, setComboAsyncOptions] = React.useState<ComboboxOption[]>(PART_OPTIONS);
  const [comboAsyncLoading, setComboAsyncLoading] = React.useState(false);
  const comboTimerRef = React.useRef<number | undefined>(undefined);
  /* FileUpload (§ 214) demo state — the component delivers File[]; the DEMO
     keeps names so the handoff is visible. */
  const [uploadNames, setUploadNames] = React.useState<string[]>([]);
  /* Progress (§ 211) demo — a fake import that ticks while the section is watched. */
  const [importPct, setImportPct] = React.useState(35);
  const handleComboSearch = (query: string) => {
    setComboAsyncLoading(true);
    window.clearTimeout(comboTimerRef.current);
    comboTimerRef.current = window.setTimeout(() => {
      const q = query.trim().toLowerCase();
      setComboAsyncOptions(q ? PART_OPTIONS.filter((o) => o.label.toLowerCase().includes(q)) : PART_OPTIONS);
      setComboAsyncLoading(false);
    }, 450);
  };
  /* Search `loading` (§ 288) demo — a search-as-you-type that actually waits, so the
     spinner can be seen TAKING the glyph's slot, and the field can be typed in while
     it spins. Same timer shape as `handleComboSearch` above. */
  const [asyncSearchValue, setAsyncSearchValue] = React.useState('');
  const [searchBusy, setSearchBusy] = React.useState(false);
  const searchTimerRef = React.useRef<number | undefined>(undefined);
  const handleAsyncSearch = (next: string) => {
    setAsyncSearchValue(next);
    setSearchBusy(true);
    window.clearTimeout(searchTimerRef.current);
    searchTimerRef.current = window.setTimeout(() => setSearchBusy(false), 900);
  };
  React.useEffect(() => () => window.clearTimeout(searchTimerRef.current), []);
  /* § 333 — the icon catalogue's name filter. */
  const [iconQuery, setIconQuery] = React.useState('');
  const matchesIconQuery = (name: string) => name.toLowerCase().includes(iconQuery.trim().toLowerCase());
  /* Button `loading` keeps focus (§ 330) — a Save that actually waits, so a keyboard
     user can press Enter and watch the ring stay on the button while it spins. */
  const [demoSaving, setDemoSaving] = React.useState(false);
  const saveTimerRef = React.useRef<number | undefined>(undefined);
  const handleDemoSave = () => {
    setDemoSaving(true);
    window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => setDemoSaving(false), 1500);
  };
  React.useEffect(() => () => window.clearTimeout(saveTimerRef.current), []);
  const [textFieldValue, setTextFieldValue] = useState('');
  const [dropdownValue, setDropdownValue] = useState<string | number>('');
  const [toggleChecked, setToggleChecked] = useState(false);
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  // Starts true: this row exists to show the mixed state, and it rendered an
  // empty box until it was clicked.
  const [checkboxIndeterminate, setCheckboxIndeterminate] = useState(true);
  const [radioValue, setRadioValue] = useState<string | number>('option1');
  const [trafficShare, setTrafficShare] = useState<number>(50);
  const [spacing, setSpacing] = useState<number>(12);
  const [richRadioValue, setRichRadioValue] = useState<string | number>('recommended');
  const [stackedToggle, setStackedToggle] = useState(true);
  /* § 303 — Round B demos. */
  const [groupValue, setGroupValue] = useState<string | number>('immediate');
  const [smToggle, setSmToggle] = useState(true);
  const [clearableDropdown, setClearableDropdown] = useState<string | number | undefined>('p');
  const [clearableCombo, setClearableCombo] = useState<string | null>('mpn');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [lastSearched, setLastSearched] = useState<string | null>(null);
  const [activeTabId, setActiveTabId] = useState<string | number>('tab1');
  const [searchValue, setSearchValue] = useState('');
  const [shortcutSearchValue, setShortcutSearchValue] = useState('');
  const [selectedMenuId, setSelectedMenuId] = useState<string | number>('menu1');
  const [selectedDropdownOption, setSelectedDropdownOption] = useState<string | number>('');
  const [tableSortState, setTableSortState] = useState<TableSortState | undefined>(undefined);
  /* § 120's expandable rows are CONTROLLED — the demo has to hold the open set itself,
     exactly as a real caller does. */
  const [showcaseExpandedRows, setShowcaseExpandedRows] = useState<Array<string | number>>([]);
  const [showcaseExpandLoadingRow, setShowcaseExpandLoadingRow] = useState<string | number | null>(null);
  /*
    Theme PREVIEW (§ 54, three-way since § 190). It was a binary "Dark mode
    (preview)" Toggle, which stopped making sense the day the app could resolve
    dark on its own (§ 176/§ 187): on a machine whose app resolution was dark,
    BOTH positions of the toggle rendered dark — there was no way to preview
    light — and its unchecked state sat beside a dark page, reading as a control
    that lies. Three positions now: System is the app's own gated resolution;
    Light and Dark force a ladder. Forcing is deliberately ungated — this page
    exists to show both ladders to anyone who can open it.
  */
  const [themePreview, setThemePreview] = useState<ThemePreference>('system');
  const [viewModeDemo, setViewModeDemo] = useState<string | number>('all');
  /* § 299 — the selectable Table demo's selection. Controlled here, as every consumer's is. */
  const [selectedRowKeys, setSelectedRowKeys] = useState<Array<string | number>>([]);
  /* § 300 — the Badge ticker demo's count. */
  const [tickerCount, setTickerCount] = useState(12);
  const [demoDate, setDemoDate] = useState<string>('2026-09-15');
  const [demoTime, setDemoTime] = useState<string>('09:30');

  /*
    Two scrollers, and the machinery that keeps them in step.

    The page used to lean on `position: sticky` and `calc(100vh - 4rem)`. Both were
    wrong for the same reason: this page is not mounted in the document scroller. The
    app shell (`Templates/Layout.tsx`) puts every route inside a `<main>` that is
    `overflow-auto` at `calc(100vh - 60px)`, so THAT is the scrolling ancestor sticky
    resolves against, and `100vh` overshoots the space actually available by the 60px
    shell header plus its 12px of padding. The rail came out taller than its own
    scrollport and rode up with the content instead of holding still.

    So the page owns its height (`flex-1 min-h-0` against the shell's flex column, no
    viewport arithmetic anywhere) and the rail and the content column are each their own
    `overflow-y-auto`. `overscroll-contain` on both is what makes them independent in the
    way you feel rather than the way you measure: without it, reaching the end of the
    rail hands the wheel to the shell and the whole app moves under you.
  */
  const contentRef = React.useRef<HTMLElement | null>(null);
  const railRef = React.useRef<HTMLElement | null>(null);
  const [activeSection, setActiveSection] = useState<string>(ALL_SECTION_IDS[0]);

  const scrollToSection = React.useCallback((id: string, behavior?: ScrollBehavior) => {
    const container = contentRef.current;
    const target = document.getElementById(id);
    if (!container || !target) return;
    /*
      Measured as a rect delta, not `offsetTop`. `offsetTop` is relative to the nearest
      positioned ancestor, which here is whatever a section happens to contain — the
      delta is right regardless of what the offset parent turns out to be.
    */
    const top = target.getBoundingClientRect().top
      - container.getBoundingClientRect().top
      + container.scrollTop
      - SCROLL_OFFSET;
    const how: ScrollBehavior = behavior ?? (prefersReducedMotion() ? 'auto' : 'smooth');
    // jsdom implements neither `scrollTo` nor smooth behaviour; the assignment does the
    // same job there, and the tests assert the landing position rather than the easing.
    if (typeof container.scrollTo === 'function') container.scrollTo({ top, behavior: how });
    else container.scrollTop = top;
    setActiveSection(id);
    // `replaceState`, not `pushState`: the hash should be shareable without turning the
    // back button into a list of every section you glanced at.
    window.history?.replaceState?.(null, '', `#${id}`);
  }, []);

  /* A deep link lands before React has mounted the sections, so the browser's own anchor
     scroll has nothing to find. Instant, not smooth — animating up from the top on load
     is a second of nothing happening. */
  React.useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id || !ALL_SECTION_IDS.includes(id)) return undefined;
    const frame = requestAnimationFrame(() => scrollToSection(id, 'auto'));
    return () => cancelAnimationFrame(frame);
  }, [scrollToSection]);

  /* Scroll spy. `root` is the content column, not the viewport — the viewport does not
     scroll on this page. The -70% bottom margin means only sections in the top third of
     the column count as current, so the highlight tracks what you are reading rather
     than flipping to whatever has just crept in at the bottom. */
  React.useEffect(() => {
    const container = contentRef.current;
    if (!container || typeof IntersectionObserver === 'undefined') return undefined;
    const elements = ALL_SECTION_IDS
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (!elements.length) return undefined;

    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        });
        // First in DOM order wins. Reading it off ALL_SECTION_IDS rather than off the
        // entries avoids trusting `boundingClientRect` from an older callback.
        const current = ALL_SECTION_IDS.find((id) => visible.has(id));
        if (current) setActiveSection(current);
      },
      { root: container, rootMargin: '0px 0px -70% 0px', threshold: 0 },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  /* Keep the active link inside the rail's own scrollport. `nearest` moves it the minimum
     distance and does nothing when it is already visible, so scrolling the content does
     not yank the rail around. */
  React.useEffect(() => {
    const link = railRef.current?.querySelector<HTMLElement>(
      `[data-testid="showcase-nav-${activeSection}"]`,
    );
    if (link && typeof link.scrollIntoView === 'function') link.scrollIntoView({ block: 'nearest' });
  }, [activeSection]);
  /*
    Restores the SAVED preference on unmount, not light.

    This effect used to `delete` the attribute both when toggled off and on unmount,
    which was correct while nothing else wrote it. Since 2026-08-30 the profile menu
    owns a persisted preference (`DesignSystem/theme.ts`), and clearing the attribute
    would have stranded a user whose preference is Dark in light the moment they left
    this page — a preview silently overwriting a setting.

    Still a PREVIEW: the control does not write to storage, so it shows another theme
    without changing what the user chose.

    The System position and the restore used to resolve through a role gate, because dark
    was admin-only and a non-admin with a dark OS would otherwise have left this page
    stranded in dark. That gate is gone — theme switching is open to every signed-in user
    — so the restore simply re-derives what `Layout` would.
  */
  React.useEffect(() => {
    const restore = () =>
      applyResolvedTheme(resolveTheme(readThemePreference(), systemPrefersDark()));
    if (themePreview === 'system') restore();
    else applyResolvedTheme(themePreview);
    return restore;
  }, [themePreview]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverDraft, setPopoverDraft] = useState('');
  /* § 304 — the four portalled surfaces inside one overflow:hidden box. */
  const [portalSupplier, setPortalSupplier] = useState<string | number>('acme');
  const [portalPart, setPortalPart] = useState<string | null>(null);
  const [portalPopoverOpen, setPortalPopoverOpen] = useState(false);
  /* § 305 — Round D primitives. */
  const [quantity, setQuantity] = useState<number | null>(12);
  const [weight, setWeight] = useState<number | null>(2.5);
  const [creatableOptions, setCreatableOptions] = useState<ComboboxOption[]>([
    { value: 'steel', label: 'Steel' },
    { value: 'brass', label: 'Brass' },
    { value: 'nylon', label: 'Nylon' },
  ]);
  const [creatableValue, setCreatableValue] = useState<string | null>('steel');
  const [pageSizePage, setPageSizePage] = useState(1);
  const [pageSizeDemo, setPageSizeDemo] = useState(25);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteLast, setPaletteLast] = useState<string | null>(null);
  const paletteCommands: CommandPaletteCommand[] = [
    { id: 'go-products', label: 'Go to Products', description: 'Catalog › Products', group: 'Navigate', icon: <DsIcons.Cart size={16} />, keywords: ['catalog', 'items'] },
    { id: 'go-rules', label: 'Go to Rules', description: 'Guide › Rules queue', group: 'Navigate', icon: <DsIcons.Filter size={16} /> },
    { id: 'go-settings', label: 'Open Settings', group: 'Navigate', icon: <DsIcons.Settings size={16} />, shortcut: 'Meta+,' },
    { id: 'new-product', label: 'Create product', description: 'Opens the create dialog', group: 'Actions', icon: <DsIcons.Add size={16} />, shortcut: 'Meta+N' },
    { id: 'import', label: 'Import CSV', group: 'Actions', icon: <DsIcons.Upload size={16} /> },
    { id: 'refresh', label: 'Refresh hierarchy', description: 'Re-reads categories and attributes', group: 'Actions', icon: <DsIcons.Refresh size={16} />, keywords: ['reload'] },
    { id: 'promote', label: 'Promote to production', description: 'Needs the environment lock', group: 'Actions', disabled: true },
    { id: 'theme-dark', label: 'Switch to dark theme', group: 'Theme', keywords: ['appearance', 'night'] },
    { id: 'theme-light', label: 'Switch to light theme', group: 'Theme', keywords: ['appearance', 'day'] },
  ];
  const [dangerDialogOpen, setDangerDialogOpen] = useState(false);
  const [demoPage, setDemoPage] = useState(6);
  const [demoTags, setDemoTags] = useState(['Voltage', 'Amperage', 'Phase', 'Frame material']);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [sidePanelTab, setSidePanelTab] = useState<ShowcasePanelTab>('details');
  const [dismissibleMenuOpen, setDismissibleMenuOpen] = useState(false);
  const [smallBoxChecked, setSmallBoxChecked] = useState(true);
  const [mediumBoxChecked, setMediumBoxChecked] = useState(true);

  /** Every tone, in ascending loudness. Labels are demo copy, not defaults. */
  const STATUS_TONES: { tone: StatusTone; label: string }[] = [
    { tone: 'neutral', label: 'Not Generated' },
    { tone: 'info', label: 'Generating' },
    { tone: 'accent', label: 'Promoting' },
    { tone: 'success', label: 'Promoted' },
    { tone: 'warning', label: 'Generated' },
    { tone: 'danger', label: 'Failed' },
  ];

  /* eslint-disable-next-line @typescript-eslint/no-deprecated -- demonstrating the
     deprecated alias IS the point of that showcase row. */
  const STATUS_VARIANTS: StatusVariant[] = [
    'generated', 'notGenerated', 'notPromoted', 'generating', 'promoting', 'promoted',
  ];

  /** The control grid, for the row-height demo. Mirrors `CONTROL_PX` in controlSize.ts. */
  const CONTROL_SIZES: { size: ControlSize; px: number }[] = [
    { size: 'sm', px: 28 },
    { size: 'md', px: 36 },
    { size: 'lg', px: 44 },
  ];

  // Sample table data
  interface TableRowData {
    id: number;
    name: string;
    email: string;
    role: string;
    status: StatusVariant;
    active: boolean;
    revenue: number;
  }

  const tableData: TableRowData[] = [
    { id: 1, name: 'John Doe', email: 'john@example.com', role: 'Admin', status: 'promoted', active: true, revenue: 15000 },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com', role: 'Editor', status: 'generated', active: true, revenue: 12500 },
    { id: 3, name: 'Bob Wilson', email: 'bob@example.com', role: 'Viewer', status: 'generating', active: false, revenue: 8000 },
    { id: 4, name: 'Alice Brown', email: 'alice@example.com', role: 'Editor', status: 'notGenerated', active: true, revenue: 21000 },
    { id: 5, name: 'Charlie Davis', email: 'charlie@example.com', role: 'Admin', status: 'promoting', active: false, revenue: 18500 },
  ];

  const tableColumns: TableColumn<TableRowData>[] = [
    { id: 'name', header: 'Name', accessor: 'name', sortable: true },
    { id: 'email', header: 'Email', accessor: 'email' },
    { id: 'role', header: 'Role', accessor: 'role', sortable: true },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      cellType: 'tag',
      render: (value) => <Status variant={value} />,
    },
    {
      id: 'active',
      header: 'Active',
      accessor: 'active',
      cellType: 'toggle',
      align: 'center',
      /* § 337 — `label=""` hides the visible label, so the switch needs `aria-label`: without
         it every row's switch had no accessible name (axe `button-name`). Named per row, so
         five switches are five different controls to a screen reader. */
      render: (value, row) => (
        <Toggle label="" aria-label={`Active — ${row.name}`} checked={value} onChange={() => {}} />
      ),
    },
    {
      id: 'revenue',
      header: 'Revenue',
      accessor: 'revenue',
      cellType: 'numerical',
      align: 'right',
      sortable: true,
      render: (value) => `$${value.toLocaleString()}`,
    },
  ];

  const handleTableSort = (columnId: string, direction: SortDirection) => {
    if (direction === null) {
      setTableSortState(undefined);
    } else {
      setTableSortState({ columnId, direction });
    }
  };

  const dropdownOptions = [
    { value: 'option1', label: 'Option 1' },
    { value: 'option2', label: 'Option 2' },
    { value: 'option3', label: 'Option 3' },
    { value: 'option4', label: 'Option 4' },
  ];

  const radioOptions = [
    { value: 'option1', label: 'Option 1' },
    { value: 'option2', label: 'Option 2' },
    { value: 'option3', label: 'Option 3' },
  ];

  const tabItems = [
    {
      id: 'tab1',
      label: 'Tab 1',
      content: 'This is the content for Tab 1',
    },
    {
      id: 'tab2',
      label: 'Tab 2',
      content: 'This is the content for Tab 2',
    },
    {
      id: 'tab3',
      label: 'Tab 3',
      content: 'This is the content for Tab 3',
    },
    {
      id: 'tab4',
      label: 'Disabled Tab',
      content: 'This tab is disabled',
      disabled: true,
    },
  ];

  // Tab items with icons
  const tabItemsWithIcons = [
    {
      id: 'settings',
      label: 'Settings',
      icon: <Settings />,
      content: 'Settings tab content',
    },
    {
      id: 'config',
      label: 'Configuration',
      icon: <NewGearIcon />,
      content: 'Configuration tab content',
    },
    {
      id: 'disabled',
      label: 'Disabled',
      icon: <CrossCircle />,
      content: 'Disabled tab content',
      disabled: true,
    },
  ];

  // Tab items with badges
  const tabItemsWithBadges = [
    {
      id: 'inbox',
      label: 'Inbox',
      badge: 12,
      content: 'Inbox with 12 unread messages',
    },
    {
      id: 'drafts',
      label: 'Drafts',
      badge: 3,
      content: 'Drafts with 3 items',
    },
    {
      id: 'sent',
      label: 'Sent',
      badge: 'New',
      content: 'Sent items',
    },
  ];

  // Tab items with icons and badges combined
  const tabItemsWithIconsAndBadges = [
    {
      id: 'notifications',
      label: 'Notifications',
      icon: <Settings />,
      badge: 5,
      content: '5 new notifications',
    },
    {
      id: 'messages',
      label: 'Messages',
      icon: <NewGearIcon />,
      badge: 24,
      content: '24 unread messages',
    },
    {
      id: 'alerts',
      label: 'Alerts',
      icon: <CrossCircle />,
      rightIcon: <Cross />,
      badge: 'Hot',
      content: 'Important alerts',
    },
  ];

  const menuItems = [
    {
      id: 'menu1',
      label: 'New',
      icon: <Settings />,
    },
    {
      id: 'menu2',
      label: 'Edit',
      icon: <Settings />,
    },
    {
      id: 'menu3',
      label: 'Delete',
      icon: <Settings />,
    },
    {
      id: 'menu4',
      divider: true,
    },
    {
      id: 'menu5',
      label: 'Settings',
      icon: <Settings />,
    },
    {
      id: 'menu6',
      label: 'Disabled Option',
      icon: <Settings />,
      disabled: true,
    },
  ];

  const dropdownButtonOptions = [
    {
      id: 'option1',
      label: 'Save',
      icon: <Settings />,
    },
    {
      id: 'option2',
      label: 'Save As',
      icon: <Settings />,
    },
    {
      id: 'option3',
      label: 'Export',
      icon: <Settings />,
    },
    {
      id: 'option4',
      label: 'Disabled Option',
      icon: <Settings />,
      disabled: true,
    },
  ];

  return (
    /*
      A DEFINITE height, and this is the second attempt at it.

      `min-h-screen` was wrong (the page is not the viewport). `flex-1 min-h-0` against
      the shell's wrapper was also wrong, and more subtly: `Layout` wraps every route in
      `min-h-full flex flex-col`, whose own height is `auto`. A `flex-1` child of an
      auto-height column only ends up definite if the whole min-height chain above it
      resolves, and when it does not the two inner `overflow-y-auto` columns get no
      height to scroll within — which is exactly what happened: nothing scrolled at all.

      So the height is stated. 72px is `Layout`'s own contract: a 60px header row plus
      the 12px `pt-3` on the `<main>` this route renders inside. `showcase.ia.test.tsx`
      reads both numbers out of `Layout.tsx` and fails if either moves, which is the only
      thing that makes a hardcoded calc safe to keep.
    */
    <div className="tw-flex tw-h-[calc(100vh-72px)] tw-flex-col tw-overflow-hidden tw-bg-surface-sunken tw-text-ink">
      {/* Masthead. Outside both scrollers, so it holds still without `position: sticky`
          and without overlapping anything a rail link jumps to. */}
      <header
        className="tw-shrink-0 tw-border-b tw-border-edge tw-bg-surface"
        data-testid="showcase-masthead"
      >
        <div className="tw-flex tw-h-16 tw-items-center tw-justify-between tw-gap-4 tw-px-8">
          <div className="tw-flex tw-items-baseline tw-gap-3">
            <Typography variant="display" as="h1" className="tw-text-ink">
              Design System
            </Typography>
            <Typography variant="caption-metadata" className="tw-text-ink-tertiary">
              Compatio GMS
            </Typography>
          </div>
          {/* § 190: three positions, because a binary "Dark mode" toggle cannot
              preview LIGHT on a machine whose app resolution is already dark — and
              its unchecked state beside a dark page read as a control that lies.
              Mirrors the profile menu's control (templates § 20), minus persistence. */}
          <div className="tw-flex tw-items-center tw-gap-2">
            <Typography variant="sub-title-label" as="span" className="tw-text-ink-tertiary">
              Preview
            </Typography>
            <SegmentedControl
              data-testid="showcase-theme-preview"
              aria-label="Theme preview"
              value={themePreview}
              options={[
                { id: 'system', label: 'System' },
                { id: 'light', label: 'Light' },
                { id: 'dark', label: 'Dark' },
              ]}
              onChange={(id) => setThemePreview(id as ThemePreference)}
            />
          </div>
        </div>
      </header>

      {/*
        No `mx-auto max-w-*` on this row, deliberately. Capping and centring it leaves a
        dead gutter down each side of a wide screen that belongs to no scroller, so the
        wheel does nothing when the pointer happens to be over it. The scrollers span the
        full column; the prose inside caps itself at `max-w-[68ch]`, which is where a
        reading measure belongs.
      */}
      <div className="tw-flex tw-min-h-0 tw-flex-1">
        {/*
          Contents rail — its own scroller. The right border spans the full row because
          the nav stretches to it, which is also why the divider does not stop where the
          links do. Hidden below `lg`, where the content column needs the width more.
        */}
        <nav
          ref={railRef}
          aria-label="Design system contents"
          data-testid="showcase-nav"
          className="tw-hidden tw-w-56 tw-shrink-0 tw-overflow-y-auto tw-overscroll-contain tw-border-r tw-border-edge tw-py-8 tw-pl-8 tw-pr-4 lg:tw-block"
        >
          {SHOWCASE_GROUPS.map((group) => (
            <div key={group.id} className="tw-mb-6">
              <span className="tw-mb-2 tw-block tw-text-sub-title-label tw-uppercase tw-tracking-wider tw-text-ink-tertiary">
                {group.label}
              </span>
              <ul className="tw-space-y-px">
                {group.sections.map((section) => {
                  const isActive = activeSection === section.id;
                  return (
                    <li key={section.id}>
                      <a
                        href={`#${section.id}`}
                        aria-current={isActive ? 'location' : undefined}
                        data-testid={`showcase-nav-${section.id}`}
                        onClick={(event) => {
                          /* Take over from the native jump so the scroll can be animated
                             and land on the same offset the fallback would. The `href`
                             stays real: middle-click and copy-link still work. */
                          event.preventDefault();
                          scrollToSection(section.id);
                        }}
                        className={`tw-block tw-rounded-ctl tw-px-2 tw-py-1 tw-text-title motion-safe:tw-transition-colors motion-safe:tw-duration-150 ${
                          isActive
                            ? 'tw-bg-accent-tint tw-text-accent'
                            : 'tw-text-ink-secondary hover:tw-bg-surface-hover hover:tw-text-ink'
                        }`}
                      >
                        {section.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <main
          ref={contentRef}
          className="tw-min-w-0 tw-flex-1 tw-overflow-y-auto tw-overscroll-contain tw-px-8 tw-py-8"
          data-testid="showcase-main"
        >
        <GroupHeading
          id="foundations"
          label="Foundations"
          blurb="The values every component is built from. Change one of these and the whole app moves."
          first={true}
        />

        {/* ── Colour Section ───────────────────────────────────────────────────
            This page demoed every component but never the tokens underneath them,
            so the only way to learn what `grey-550` was for was to read a comment
            in tailwind.config.js. Data comes from
            `Components/DesignSystem/palette.ts`, which `paletteSync.test.ts` pins to
            the config in both directions — a swatch here cannot show a colour the
            app does not actually use.
        */}
        <section id="colour" data-testid="showcase-palette" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Colour
          </Typography>
          <FoundationBlock section="colour" />
          <Divider className="tw-mb-6" />
          <Typography variant="body-primary" className="tw-mb-2 tw-text-ink-secondary">
            Each family is a semantic triple, not a ramp: <strong>100</strong> is a tint ground,
            <strong> 500</strong> is the signal or fill, <strong>900</strong> is the text-safe
            variant. That is why <strong>500</strong> means something different in every family —
            green-success-500 is 2.66:1 and can never be text, while purple-500 is 6.97:1.
          </Typography>
          <Typography variant="caption-metadata" className="tw-mb-6 tw-text-ink-tertiary">
            Two ratios per swatch: against white, then against grey-100 — the tinted surface under a
            table head or a dialog footer. They disagree often enough to matter, and the verdict
            below each swatch is judged on the stricter of the two.
          </Typography>

          {FAMILY_ORDER.map((family) => (
            <div key={family} className="tw-mb-8" data-testid={`showcase-palette-${family}`}>
              <Typography variant="title-header" className="tw-mb-3 tw-text-ink">
                {FAMILY_LABEL[family]}
              </Typography>
              <div className="tw-flex tw-flex-wrap tw-gap-3">
                {Object.entries(PALETTE[family]).map(([step, hex]) => {
                  const onWhite = contrast(hex, GROUND.white);
                  const onTint = contrast(hex, GROUND.tint);
                  const safety = textSafety(hex);
                  const role = ROLE[`${family}-${step}`];
                  const base = `showcase-palette-${family}-${step}`;
                  return (
                    <div
                      key={step}
                      id={base}
                      data-testid={base}
                      className="tw-w-44 tw-rounded-ctl tw-border tw-border-edge tw-overflow-hidden"
                    >
                      {/*
                        Inline style, deliberately, and the one place on this page it is
                        right: a Tailwind class cannot be built from a template string —
                        the content scanner only sees literal classes, so
                        `tw-bg-${family}-${step}` would be purged and render nothing.
                        The hex comes from palette.ts, which the sync test pins to the
                        config, so this shows the real token either way.
                      */}
                      <div
                        className="tw-h-16 tw-flex tw-items-end tw-justify-end tw-p-2"
                        style={{ backgroundColor: hex }}
                      >
                        {/* Sample glyph in whichever of the two inks is legible on it. */}
                        <span
                          className="tw-text-sub-title-label"
                          style={{ color: onWhite >= 4.5 ? GROUND.white : PALETTE.grey['900'] }}
                          aria-hidden="true"
                        >
                          Aa
                        </span>
                      </div>
                      <div className="tw-p-2 tw-border-t tw-border-edge">
                        <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-1">
                          <Typography variant="title" className="tw-text-ink">
                            {step}
                          </Typography>
                          <Typography
                            variant="caption-metadata"
                            as="code"
                            className="tw-text-ink-secondary"
                          >
                            <span data-testid={`${base}-hex`}>{hex}</span>
                          </Typography>
                        </div>
                        <Typography
                          variant="caption-metadata"
                          className="tw-text-ink-tertiary tw-block tw-mt-1"
                        >
                          <span data-testid={`${base}-contrast`}>
                            {onWhite.toFixed(2)} white · {onTint.toFixed(2)} tint
                          </span>
                        </Typography>
                        {/*
                          Glyph AND word, never colour alone — SC 1.4.1. The colour is
                          reinforcement for people who can see it, not the message.
                        */}
                        {/*
                          § 321 — the tones, not the steps they happen to equal. This
                          caption named `green-success-900` and `yellow-warning-950`,
                          which are BYTE-IDENTICAL to `tone-success-ink` / `tone-warning-ink`
                          in light — so light renders unchanged — but a step has one value
                          and a role has two. In dark the steps stayed #115b3c / #6b5200,
                          which measure 2.1:1 and 2.3:1 on the page ground: the safety
                          verdict on the palette page went unreadable in the theme the
                          palette page exists to explain. The roles carry #8be6b3 / #d8b225
                          there (§ 316's retuned band).
                        */}
                        <Typography
                          variant="caption-metadata"
                          className={`tw-block tw-mt-1 ${
                            safety === 'aa'
                              ? 'tw-text-tone-success-ink'
                              : safety === 'non-text'
                                ? 'tw-text-tone-warning-ink'
                                : 'tw-text-ink-tertiary'
                          }`}
                        >
                          <span data-testid={`${base}-safety`} data-safety={safety}>
                            {safety === 'aa' ? '\u25cf' : safety === 'non-text' ? '\u25d0' : '\u25cb'}
                            {' '}
                            {SAFETY_LABEL[safety]}
                          </span>
                        </Typography>
                        {role && (
                          <Typography
                            variant="caption-metadata"
                            className="tw-block tw-mt-1 tw-text-ink-secondary"
                          >
                            <span data-testid={`${base}-role`}>{role}</span>
                          </Typography>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* ── The role layer (§§ 54, 205-207): the seam components actually wear ── */}
          <div className="tw-mb-8" data-testid="showcase-roles">
            <Typography variant="title-header" as="h3" className="tw-mb-2 tw-text-ink">
              Role tokens &mdash; colour by job
            </Typography>
            <Typography variant="body-primary" className="tw-mb-4 tw-max-w-[68ch] tw-text-ink-secondary">
              Components never name a palette step; they name a JOB &mdash; <code>tw-text-ink</code>,{' '}
              <code>tw-bg-surface</code>, <code>tw-border-edge</code> &mdash; and the theme decides the
              value. Every card below paints from its live <code>--ds-*</code> variable, so the
              Light/Dark toggle flips this block with the page; the two hexes are the light and dark
              values. <code>accent-tint</code> is the selection ground: it reads against
              white (1.30) and against the neutral hover ground (1.19), where blue-100 measured 1.10
              and 1.01. <code>scrim</code> carries its own alpha &mdash; the one 8-digit
              value in the seam.
            </Typography>
            {ROLE_GROUPS.map((group) => (
              <div key={group.label} className="tw-mb-4" data-testid={`showcase-roles-${group.label.toLowerCase()}`}>
                <Typography variant="caption-metadata" className="tw-block tw-mb-2 tw-text-ink-tertiary">
                  {group.label}
                </Typography>
                <div className="tw-flex tw-flex-wrap tw-gap-3">
                  {group.roles.map((role) => <RoleCard key={role} role={role} />)}
                </div>
              </div>
            ))}
          </div>

          {/* ── The tone matrix (§ 206) ── */}
          <div data-testid="showcase-tone-matrix">
            <Typography variant="title-header" as="h3" className="tw-mb-2 tw-text-ink">
              The tone matrix
            </Typography>
            <Typography variant="body-primary" className="tw-mb-4 tw-max-w-[68ch] tw-text-ink-secondary">
              Six tones &times; four jobs, complete &mdash; so the next person
              needing a warning fill does not invent a hex. <strong>tint</strong> is the tone&rsquo;s
              ground and <strong>ink</strong> its text on that ground; <strong>glyph</strong> is the
              tone&rsquo;s mark on a plain surface; <strong>bold</strong> is the saturated fill and
              always carries ink-inverse. Every pairing shown is the measured one.
            </Typography>
            <div className="tw-overflow-x-auto">
              <div className="tw-inline-grid tw-grid-cols-[6.5rem_repeat(4,5.5rem)] tw-gap-px tw-rounded-srf tw-border tw-border-edge tw-bg-edge tw-overflow-hidden">
                <div className="tw-bg-surface tw-p-2" aria-hidden="true" />
                {MATRIX_STEPS.map((step) => (
                  <div key={step} className="tw-bg-surface tw-p-2 tw-text-center tw-text-sub-title-label tw-text-ink-tertiary">
                    {step}
                  </div>
                ))}
                {MATRIX_TONES.map((tone) => (
                  <React.Fragment key={tone}>
                    <div className="tw-bg-surface tw-p-2 tw-flex tw-items-center tw-text-sub-title-label tw-text-ink">
                      {tone}
                    </div>
                    {MATRIX_STEPS.map((step) => (
                      <div
                        key={step}
                        data-testid={`showcase-tone-${tone}-${step}`}
                        className="tw-flex tw-items-center tw-justify-center tw-p-2 tw-text-title"
                        style={toneCellStyle(tone, step)}
                      >
                        {step === 'glyph' ? '●' : 'Aa'}
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Typography Section */}
        <section id="typography" data-testid="showcase-section-typography" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Typography
          </Typography>
          <DocBlock section="typography" />
          <Divider className="tw-mb-6" />
          <div className="tw-space-y-6">
            <div>
              <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-2">
                Display 2 — the page-level moment
              </Typography>
              <Typography className="tw-text-ink" variant="display-2">Display 2 (28px, 600)</Typography>
            </div>

            <div>
              <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-2">
                Display
              </Typography>
              <Typography className="tw-text-ink" variant="display">Display Text (24px, 600)</Typography>
            </div>

            <Divider />

            <div>
              <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-2">
                Heading 1
              </Typography>
              <Typography className="tw-text-ink" variant="heading-1">Heading 1 (18px, 700)</Typography>
            </div>

            <div>
              <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-2">
                Heading 2
              </Typography>
              <Typography className="tw-text-ink" variant="heading-2">Heading 2 (18px, 500)</Typography>
            </div>

            <div>
              <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-2">
                Heading 3 — the section heading between 18 and 14
              </Typography>
              <Typography className="tw-text-ink" variant="heading-3">Heading 3 (16px, 600)</Typography>
            </div>

            <div>
              <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-2">
                Title Header
              </Typography>
              <Typography className="tw-text-ink" variant="title-header">Title Header (14px, 600)</Typography>
            </div>

            <div>
              <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-2">
                Title
              </Typography>
              <Typography variant="title">Title (14px, 500)</Typography>
            </div>

            <div>
              <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-2">
                Body Large — lead text, untracked
              </Typography>
              <Typography variant="body-large" className="tw-text-ink">Body Large (16px, 400): the one paragraph a page opens with.</Typography>
            </div>

            <div>
              <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-2">
                Body Primary
              </Typography>
              <Typography variant="body-primary">Body Primary (14px, 400)</Typography>
            </div>

            <div>
              <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-2">
                Sub Title Header
              </Typography>
              <Typography className="tw-text-ink" variant="sub-title-header">Sub Title Header (12px, 700)</Typography>
            </div>

            <div>
              <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-2">
                Sub Title Label
              </Typography>
              <Typography variant="sub-title-label" className="tw-block">Sub Title Label (12px, 600)</Typography>
            </div>

            <div>
              <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-2">
                Sub Body Supporting
              </Typography>
              <Typography variant="sub-body-supporting" className="tw-block">Sub Body Supporting (12px, 500)</Typography>
            </div>

            <div>
              <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-2">
                Caption Metadata
              </Typography>
              <Typography variant="caption-metadata" className="tw-block">Caption Metadata (12px, 400)</Typography>
            </div>

            <div>
              <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-2">
                Code Formula
              </Typography>
              <Typography variant="code-formula" as="code" className="tw-block">
                const x = 42;
              </Typography>
            </div>
          </div>
        </section>

        {/* Elevation & shape (§ 171). The tokens every surface on this page uses, demoed
            for the first time — and the reason this section's own card has a border and
            no shadow. */}
        <section id="elevation" data-testid="showcase-section-elevation" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Elevation &amp; shape
          </Typography>
          <FoundationBlock section="elevation" />
          <Divider className="tw-mb-6" />
          <Typography variant="body-primary" className="tw-mb-6 tw-max-w-[68ch] tw-text-ink-secondary">
            Shadows are tinted with grey-900, not black — a black shadow over blue-grey
            neutrals reads muddy, because the shadow and the surface disagree about hue.
            Flat surfaces take a hairline edge border instead. Elevation is reserved for
            things that genuinely float, which on this page means Dialog, SidePanel, Menu
            and Toast, and nothing else. The stops are `--ds-shadow-*` tokens:
            in dark they deepen to near-black — an ink-tinted shadow is invisible on a dark
            ground, which is why dark depth used to be surface-lightening alone. Flip the
            theme toggle above and the ladder below keeps its depth.
          </Typography>
          <div className="tw-mb-8 tw-grid tw-grid-cols-2 tw-gap-4 md:tw-grid-cols-4">
            {[
              { name: 'border', use: 'flat — cards, sections, tables', cls: 'tw-border tw-border-edge' },
              { name: 'shadow-ds-sm', use: 'raised — a card that lifts', cls: 'tw-shadow-ds-sm' },
              { name: 'shadow-ds-md', use: 'overlay — Menu, Dropdown, Toast', cls: 'tw-shadow-ds-md' },
              { name: 'shadow-ds-lg', use: 'Dialog and SidePanel', cls: 'tw-shadow-ds-lg' },
              /* § 222 — sm cast sideways: what a pinned table column wears while
                 columns actually slide beneath it (Table owns that state). */
              { name: 'shadow-ds-sm-right', use: 'left-pinned column, mid-scroll', cls: 'tw-shadow-ds-sm-right' },
              { name: 'shadow-ds-sm-left', use: 'right-pinned column, mid-scroll', cls: 'tw-shadow-ds-sm-left' },
            ].map((step) => (
              <div key={step.name} data-testid={`showcase-elevation-${step.name}`}>
                <div className={`tw-mb-3 tw-h-20 tw-rounded-srf tw-bg-surface ${step.cls}`} />
                <span className="tw-block tw-text-sub-title-label tw-text-ink">{step.name}</span>
                <span className="tw-block tw-text-caption-metadata tw-text-ink-tertiary">{step.use}</span>
              </div>
            ))}
          </div>

          <Typography variant="title-header" as="h3" className="tw-mb-2 tw-text-ink">
            Radius
          </Typography>
          <Typography variant="body-primary" className="tw-mb-4 tw-max-w-[68ch] tw-text-ink-secondary">
            Differentiated by distance from the page: controls stay on the
            legacy-compatible 4px — they still share toolbars with un-migrated 4px
            fields — while surfaces (8px) and overlays (12px) take back the depth the
            earlier flattening parked. The further a plane floats, the softer its corners.
          </Typography>
          <div className="tw-flex tw-flex-wrap tw-gap-4">
            {[
              { name: 'rounded-ctl', use: '4px — inputs, buttons, chips', cls: 'tw-rounded-ctl' },
              { name: 'rounded-srf', use: '8px — cards, menus, tables', cls: 'tw-rounded-srf' },
              { name: 'rounded-ovl', use: '12px — dialogs, panels', cls: 'tw-rounded-ovl' },
            ].map((r) => (
              <div key={r.name} className="tw-w-44" data-testid={`showcase-radius-${r.name}`}>
                <div className={`tw-mb-3 tw-h-14 tw-border tw-border-edge tw-bg-surface-sunken ${r.cls}`} />
                <span className="tw-block tw-text-sub-title-label tw-text-ink">{r.name}</span>
                <span className="tw-block tw-text-caption-metadata tw-text-ink-tertiary">{r.use}</span>
              </div>
            ))}
          </div>

          {/* The layer ladder (§ 204) — rendered from LAYERS, which layerLadder.test.tsx
              pins to the config, so this display cannot drift from what compiles. */}
          <Typography variant="title-header" as="h3" className="tw-mt-8 tw-mb-2 tw-text-ink">
            The layer ladder
          </Typography>
          <Typography variant="body-primary" className="tw-mb-4 tw-max-w-[68ch] tw-text-ink-secondary">
            Global layers take a NAMED z-tier &mdash; <code>tw-z-overlay</code> &lt;{' '}
            <code>tw-z-popover</code> &lt; <code>tw-z-toast</code> &lt; <code>tw-z-tooltip</code>.
            The <code>popover</code> rung is where every portalled anchored surface sits: over the
            Dialog that opened it, under a toast. Numeric z stays legal only for local tiers inside
            one component&rsquo;s own stacking context &mdash; table sticky cells, an inline popover.
          </Typography>
          <div className="tw-flex tw-flex-wrap tw-gap-4" data-testid="showcase-layer-ladder">
            {LAYER_ORDER.map((name, rung) => {
              /* Full class-name literals, never `tw-z-` + name: the class-integrity
                 scanner reads source tokens, and a spliced prefix registers as a
                 dead `tw-z` class. These four are real, compiled classes. */
              const meta = {
                overlay: { cls: 'tw-z-overlay', use: 'scrims and floating surfaces — Dialog, SidePanel, inline popovers' },
                popover: { cls: 'tw-z-popover', use: 'portalled anchored surfaces — Dropdown, Combobox, Popover, DropdownButton' },
                toast: { cls: 'tw-z-toast', use: 'above the dialog it was raised from' },
                tooltip: { cls: 'tw-z-tooltip', use: 'pointer-attached, on top of everything' },
              }[name];
              return (
                <div key={name} className="tw-w-56" data-testid={`showcase-layer-${name}`}>
                  <div className="tw-relative tw-mb-3 tw-h-20 tw-rounded-srf tw-border tw-border-edge tw-bg-surface-sunken tw-overflow-hidden">
                    {/* One card per rung below this tier, offset like a hand of cards —
                        the higher the tier, the taller the stack it sits on. */}
                    {Array.from({ length: rung + 1 }).map((_, i) => (
                      <div
                        key={i}
                        aria-hidden="true"
                        className="tw-absolute tw-h-10 tw-w-24 tw-rounded-ctl tw-border tw-border-edge tw-bg-surface-raised tw-shadow-ds-sm"
                        style={{ left: `${12 + i * 28}px`, top: `${34 - i * 7}px` }}
                      />
                    ))}
                  </div>
                  <code className="tw-block tw-text-sub-title-label tw-text-ink">
                    {meta.cls} &mdash; {LAYERS[name]}
                  </code>
                  <span className="tw-block tw-text-caption-metadata tw-text-ink-tertiary">{meta.use}</span>
                </div>
              );
            })}
          </div>
        </section>

        {/* Iconography (§ 171) — the set had never been shown anywhere. Listed from the
            module itself so the grid cannot fall behind it. */}
        <section id="iconography" data-testid="showcase-section-iconography" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Iconography
          </Typography>
          <DocBlock section="iconography" />
          <Divider className="tw-mb-6" />
          <Typography variant="body-primary" className="tw-mb-4 tw-max-w-[68ch] tw-text-ink-secondary">
            Every icon, from one import: <code>Components/DesignSystem/Iconography</code>. The core
            set is drawn to one grid and one stroke &mdash; reach for it first. The app set holds
            the product&rsquo;s own glyphs, grouped by job, on the same props. Both are enumerated
            from their modules rather than hand-listed, so this page cannot fall behind them.
          </Typography>
          <div className="tw-mb-6 tw-max-w-sm">
            <Search
              value={iconQuery}
              onChange={setIconQuery}
              placeholder="Find an icon by name"
              aria-label="Find an icon by name"
              data-testid="showcase-icon-search"
            />
          </div>
          <Typography variant="heading-3" as="h3" className="tw-mb-3 tw-text-ink">
            Core set
          </Typography>
          <div className="tw-grid tw-grid-cols-3 tw-gap-px tw-overflow-hidden tw-rounded-srf tw-border tw-border-edge tw-bg-edge sm:tw-grid-cols-4 lg:tw-grid-cols-6">
            {iconEntries(DsIcons as unknown as Record<string, unknown>)
              .filter(([name]) => matchesIconQuery(name))
              .map(([name, value]) => {
                const Icon = value as unknown as React.ComponentType;
                return (
                  <div
                    key={name}
                    className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-bg-surface tw-p-4"
                    data-testid={`showcase-icon-${name}`}
                  >
                    <span className="tw-flex tw-h-6 tw-items-center tw-text-ink-secondary">
                      <Icon />
                    </span>
                    <span className="tw-text-caption-metadata tw-text-ink-tertiary">{name}</span>
                  </div>
                );
              })}
          </div>
          {APP_ICON_GROUPS.map((group) => {
            const shown = iconEntries(group.icons).filter(([name]) => matchesIconQuery(name));
            if (!shown.length) return null;
            return (
              <div key={group.id} className="tw-mt-8" data-testid={`showcase-app-icons-${group.id}`}>
                <Typography variant="heading-3" as="h3" className="tw-mb-3 tw-text-ink">
                  App set &middot; {group.label}
                </Typography>
                <div className="tw-grid tw-grid-cols-2 tw-gap-px tw-overflow-hidden tw-rounded-srf tw-border tw-border-edge tw-bg-edge sm:tw-grid-cols-4 lg:tw-grid-cols-6">
                  {shown.map(([name, Icon]) => (
                    <div
                      key={name}
                      className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-bg-surface tw-p-4"
                      data-testid={`showcase-app-icon-${name}`}
                    >
                      <span className="tw-flex tw-h-8 tw-items-center tw-text-ink-secondary">
                        <Icon aria-hidden color="currentColor" />
                      </span>
                      <span className="tw-text-caption-metadata tw-text-ink-tertiary tw-text-center tw-break-all">{name}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {iconQuery.trim() && !iconEntries(DsIcons as unknown as Record<string, unknown>).some(([n]) => matchesIconQuery(n))
            && APP_ICON_GROUPS.every((g) => !iconEntries(g.icons).some(([n]) => matchesIconQuery(n))) && (
            <Typography variant="body-primary" className="tw-mt-6 tw-text-ink-tertiary" data-testid="showcase-icon-search-empty">
              No icon is called &ldquo;{iconQuery.trim()}&rdquo;. Try a job word &mdash; edit, arrow, chart.
            </Typography>
          )}
        </section>

        {/* Control grid Section */}
        <section id="control-sizing" data-testid="showcase-section-control-sizing" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Control sizing
          </Typography>
          <FoundationBlock section="control-sizing" />
          <Divider className="tw-mb-6" />
          <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mb-6 tw-block">
            One height ladder shared by every row-height control &mdash; 28 / 36 / 44px. Before
            2026-08-24 these five had four different heights and only `Button` declared one, so a
            button sat 6px shorter than the field beside it. Each row below must line up exactly.
          </Typography>
          <div className="tw-space-y-6">
            {CONTROL_SIZES.map(({ size, px }) => (
              <div key={size}>
                <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mb-2 tw-block">
                  {`size="${size}" — ${px}px`}
                </Typography>
                <div
                  className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap"
                  data-testid={`showcase-grid-row-${size}`}
                >
                  <Button variant="primary" size={size} onClick={() => {}} data-testid={`showcase-grid-button-${size}`}>
                    Promote
                  </Button>
                  <DropdownButton
                    label="Actions"
                    size={size}
                    options={[{ id: 'a', label: 'Duplicate' }, { id: 'b', label: 'Archive' }]}
                    data-testid={`showcase-grid-dropdownbutton-${size}`}
                  />
                  <div className="tw-w-44">
                    {/* label="" is how these two opt out — `label` is required and
                        Dropdown's guard is `label !== ''`. */}
                    <TextField label="" value="UI-Model-1" size={size} onChange={() => {}} data-testid={`showcase-grid-textfield-${size}`} />
                  </div>
                  <div className="tw-w-44">
                    <Dropdown
                      label=""
                      value=""
                      size={size}
                      onChange={() => {}}
                      options={[{ label: 'Production', value: 'p' }]}
                      placeholder="Environment"
                      data-testid={`showcase-grid-dropdown-${size}`}
                    />
                  </div>
                  <div className="tw-w-44">
                    <Search value="" size={size} onChange={() => {}} data-testid={`showcase-grid-search-${size}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>

        </section>

        <section id="density" data-testid="showcase-section-density" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Density
          </Typography>
          <DocBlock section="density" />

          <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-2">
            A region can set the rung, so a dense screen says it once
          </Typography>
          <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-4">
            The same three controls, written identically in all three rows &mdash; no size prop
            anywhere. Only the region around them differs. An explicit size on a control still wins,
            because density is a default and not an instruction.
          </Typography>
          <div className="tw-space-y-4">
            {(['compact', 'default', 'comfortable'] as const).map((d) => (
              <div key={d} className="tw-flex tw-items-center tw-gap-4 tw-flex-wrap">
                <Typography
                  variant="caption-metadata"
                  className="tw-text-ink-tertiary tw-w-28 tw-shrink-0"
                >
                  {d}
                </Typography>
                <DensityProvider density={d}>
                  <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                    <TextField
                      label=""
                      aria-label={`Name, ${d}`}
                      placeholder="Name"
                      data-testid={`showcase-density-${d}-field`}
                    />
                    <Dropdown
                      label=""
                      aria-label={`Environment, ${d}`}
                      options={[{ label: 'Production', value: 'prod' }, { label: 'Staging', value: 'stage' }]}
                      data-testid={`showcase-density-${d}-dropdown`}
                    />
                    <Button data-testid={`showcase-density-${d}-button`}>Save</Button>
                  </div>
                </DensityProvider>
              </div>
            ))}
          </div>

        </section>

        {/* Keyboard, focus and motion — what these components do without being asked */}
        <section id="motion" data-testid="showcase-section-motion" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Keyboard, focus &amp; motion
          </Typography>
          <FoundationBlock section="motion" />
          <Divider className="tw-mb-6" />

          <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-6">
            Behaviour every component above shares. None of it needs a prop.
          </Typography>

          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-border-collapse tw-border tw-border-edge tw-rounded-sm tw-overflow-hidden">
              <thead className="tw-bg-surface-sunken">
                <tr>
                  {['Component', 'Keys', 'Notes'].map((heading) => (
                    <th
                      key={heading}
                      scope="col"
                      className="tw-px-3 tw-py-2 tw-text-title-header tw-text-ink tw-text-left"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['Checkbox, Radio', 'Tab, Space', 'Native inputs. Pass Radio a `name` for arrow keys and one tab stop.'],
                  ['Menu, Dropdown', 'Up / Down / Home / End, type-ahead', 'One tab stop; arrows move within. Disabled items are skipped. Type a name to jump to it: keys typed together are one search.'],
                  ['Menu submenu', 'Right / Left, Enter, Escape', 'Right or Enter opens it and moves in, Left leaves; Escape on a child closes just the submenu.'],
                  ['Dropdown trigger', 'Down / Up', 'Opens and enters the list at the selected option.'],
                  ['Tab', 'Left / Right / Home / End', 'Selection follows focus — the panel is already built, so it is instant.'],
                  ['Table headers', 'Tab, Enter, Space', 'Sortable headers are buttons; the header cell carries aria-sort.'],
                  ['Table rows', 'Up / Down / Home / End, Enter, Space', 'Clickable rows are one tab stop: arrows move between rows, Enter or Space opens one.'],
                  ['Dialog, SidePanel', 'Escape, Tab', 'Escape closes, Tab cycles inside, focus returns to the trigger. A confirm opens on Cancel. With a select open inside, the first Escape closes the select.'],
                  ['Tooltip', 'Escape', 'Closes the bubble from anywhere. Inside a dialog the first Escape closes the tooltip, the next the dialog.'],
                  ['Everything focusable', '—', 'A visible focus ring on keyboard focus only, never on a mouse click.'],
                  ['Overlay motion', '—', 'Animate in and out; nothing moves under prefers-reduced-motion.'],
                ].map(([component, keys, note]) => (
                  <tr key={component} className="tw-border-b tw-border-edge">
                    <td className="tw-px-3 tw-py-2 tw-text-body-primary tw-text-ink tw-whitespace-nowrap">
                      {component}
                    </td>
                    <td className="tw-px-3 tw-py-2 tw-text-body-primary tw-text-ink-secondary tw-whitespace-nowrap">
                      {keys}
                    </td>
                    <td className="tw-px-3 tw-py-2 tw-text-body-primary tw-text-ink-secondary">{note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Component API conventions (§ 331) — the names every component shares. */}
        <section id="api-conventions" data-testid="showcase-section-api-conventions" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Component API
          </Typography>
          <FoundationBlock section="api-conventions" />
          <Divider className="tw-mb-6" />

          <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-6">
            One name per idea, across the whole library. The older spellings still work and are
            struck through in each component&rsquo;s props table, so no screen has to change to
            keep running &mdash; new code uses the name on the left.
          </Typography>

          <div className="tw-overflow-x-auto">
            <table
              className="tw-w-full tw-border-collapse tw-border tw-border-edge tw-rounded-sm tw-overflow-hidden"
              data-testid="showcase-api-conventions-table"
            >
              <thead className="tw-bg-surface-sunken">
                <tr>
                  {['Idea', 'Write', 'Older spelling, still honoured'].map((heading) => (
                    <th
                      key={heading}
                      scope="col"
                      className="tw-px-3 tw-py-2 tw-text-title-header tw-text-ink tw-text-left"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['Is the overlay showing?', 'open', 'isOpen (Dialog, SidePanel, ConfirmDialog)'],
                  ['Is it busy?', 'loading', 'isLoading (SidePanel)'],
                  ['How big?', "size: 'sm' | 'md' | 'lg'", "'small' | 'medium' | 'large' (Button iconSize, Search size)"],
                  ['What is it called, for a screen reader?', 'aria-label — the native attribute, on every component', 'ariaLabel'],
                  ['A label the layout has no room for', 'hideLabel — hidden, and still the name', 'label="" (left the field with no name)'],
                  ['The line under a field', 'helperText: a node — text, or text with a link', 'a plain string (still fine)'],
                  ['What went wrong with a field', 'errorMessage — a line of its own; the hint stays', 'error + helperText (the hint turns into the error)'],
                  ['A control that keeps its own value', 'defaultValue · defaultChecked · defaultActiveTabId', '— (every control was controlled only)'],
                  ['No tone', "tone: 'neutral'", "'default' (Menu items, ChoiceChip)"],
                  ['Which edge a menu lines up with', "align: 'start' | 'end'", "'left' | 'right' (DropdownButton)"],
                  ['A card that opens something', 'action: { href } or { onClick } — the whole card, one named target', '—'],
                  ['Extra classes', 'className — merged on every component; the caller wins a conflict', 'appended (the stylesheet picked the winner)'],
                  ['Busy, but still focused', 'loading on Button — aria-disabled, never native disabled', '—'],
                ].map(([idea, write, older]) => (
                  <tr key={idea} className="tw-border-b tw-border-edge">
                    <td className="tw-px-3 tw-py-2 tw-text-body-primary tw-text-ink">{idea}</td>
                    <td className="tw-px-3 tw-py-2 tw-text-body-primary tw-text-ink">
                      <code>{write}</code>
                    </td>
                    <td className="tw-px-3 tw-py-2 tw-text-body-primary tw-text-ink-tertiary">{older}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <GroupHeading
          id="actions"
          label="Actions"
          blurb="Things a user presses. Exactly one primary action per surface."
          first={false}
        />

        {/* Button Section */}
        <section id="buttons" data-testid="showcase-section-buttons" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Buttons
          </Typography>
          <DocBlock section="buttons" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8">
            {/* Primary Buttons */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Primary
              </Typography>
              <div className="tw-flex tw-gap-4 tw-flex-wrap">
                <Button variant="primary">Default</Button>
                <Button variant="primary" startIcon={<NewGearIcon />} iconSize="sm">
                  With Icon
                </Button>
                <Button variant="primary" startIcon={<NewGearIcon />} iconSize="sm" badge="8">
                  With Icon & Badge
                </Button>
                {/* The § 219 top-light candidate was ADOPTED (§ 221) — every filled
                    Button above wears `shadow-ds-topline` now, so the mock retired. */}
                <Button variant="primary" disabled>
                  Disabled
                </Button>
                <Button variant="primary" disabled startIcon={<NewGearIcon />} iconSize="sm">
                  Disabled with Icon
                </Button>
              </div>
            </div>

            <Divider />

            {/* Secondary Buttons */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Secondary
              </Typography>
              <div className="tw-flex tw-gap-4 tw-flex-wrap">
                <Button variant="secondary">Default</Button>
                <Button variant="secondary" startIcon={<NewGearIcon />} iconSize="sm">
                  With Icon
                </Button>
                <Button variant="secondary" startIcon={<NewGearIcon />} iconSize="sm" badge="5">
                  With Icon & Badge
                </Button>
                <Button variant="secondary" disabled>
                  Disabled
                </Button>
                <Button variant="secondary" disabled startIcon={<NewGearIcon />} iconSize="sm">
                  Disabled with Icon
                </Button>
              </div>
            </div>

            <Divider />

            {/* Tertiary Buttons */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Tertiary
              </Typography>
              <div className="tw-flex tw-gap-4 tw-flex-wrap">
                <Button variant="tertiary">Default</Button>
                <Button variant="tertiary" startIcon={<NewGearIcon />} iconSize="sm">
                  With Icon
                </Button>
                <Button variant="tertiary" startIcon={<NewGearIcon />} iconSize="sm" badge="3">
                  With Icon & Badge
                </Button>
                <Button variant="tertiary" disabled>
                  Disabled
                </Button>
                <Button variant="tertiary" disabled startIcon={<NewGearIcon />} iconSize="sm">
                  Disabled with Icon
                </Button>
              </div>
            </div>

            <Divider />

            {/* Danger Buttons (§ 84) */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Danger — the destructive outline ACTION
              </Typography>
              <Typography variant="body-primary" className="tw-mb-4 tw-text-ink-secondary">
                Delete sitting beside a primary Save, where a second fill would compete for
                &quot;the&quot; action. This is not the confirm button — see below.
              </Typography>
              <div className="tw-flex tw-gap-4 tw-flex-wrap">
                <Button variant="danger" data-testid="showcase-danger-btn">Delete</Button>
                <Button variant="danger" disabled>
                  Disabled
                </Button>
              </div>
            </div>

            {/* Danger-primary (§ 167) */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Danger-primary — the destructive CONFIRM
              </Typography>
              <Typography variant="body-primary" className="tw-mb-4 tw-text-ink-secondary">
                The Delete inside a delete dialog. It was a blue primary — the same object as
                every safe confirm in the app, in a dialog whose every other signal said danger.
                The fill is tone-danger-bold, the same #ad2a21 as the dialog&apos;s header glyph,
                so the surface speaks one red. Red is reserved for delete: Stop, Promote and
                Discard stay primary.
              </Typography>
              <div className="tw-flex tw-gap-4 tw-flex-wrap">
                <Button variant="danger-primary" data-testid="showcase-danger-primary-btn">Delete</Button>
                <Button variant="danger-primary" disabled>
                  Disabled
                </Button>
              </div>
            </div>

            {/* Subtle Buttons (§ 65) */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Subtle — the quiet toolbar trigger
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                Ink, not accent: a toolbar of Sort / Filter / Bulk Action in accent blue would read
                as three links. Transparent at rest, neutral ground on hover.
              </Typography>
              <div className="tw-flex tw-gap-2 tw-flex-wrap">
                <Button variant="subtle" data-testid="showcase-subtle-btn">Sort</Button>
                <Button variant="subtle">Filter</Button>
                <Button variant="subtle">Bulk Action</Button>
                <Button variant="subtle" disabled>
                  Disabled
                </Button>
              </div>
            </div>

            <Divider />

            {/* Loading (§ 209) */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Loading &mdash; the async-action state
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                A spinner takes the icon slot, the label stays, and the click is blocked
                (<code>aria-disabled</code> + <code>aria-busy</code>) &mdash; the guard every
                submit otherwise hand-rolls. It is <em>not</em> natively disabled: a
                focused button that turns <code>disabled</code> throws focus to the page, so the
                keyboard user who pressed Save would lose their place. A second press, and a
                form submit, are still refused. The button keeps its own fill while it spins
                (the spinner on a fill is the inverse one), so a pressed Save still looks
                like Save; the cursor says <em>wait</em>, not <em>never</em>.
              </Typography>
              <div className="tw-flex tw-gap-4 tw-flex-wrap">
                <Button variant="primary" loading data-testid="showcase-loading-btn">
                  Saving&hellip;
                </Button>
                <Button variant="secondary" loading data-testid="showcase-loading-secondary">
                  Generating&hellip;
                </Button>
                <Button variant="danger-primary" loading data-testid="showcase-loading-danger">
                  Deleting&hellip;
                </Button>
                <Button variant="primary" loading startIcon={<NewGearIcon />} iconSize="sm" data-testid="showcase-loading-icon">
                  Icon yields the slot
                </Button>
              </div>
              <div className="tw-mt-4 tw-flex tw-items-center tw-gap-4 tw-flex-wrap">
                <Button variant="primary" loading={demoSaving} onClick={handleDemoSave} data-testid="showcase-loading-focus-btn">
                  {demoSaving ? 'Saving\u2026' : 'Save'}
                </Button>
                <Typography variant="caption-metadata" as="p" className="tw-text-ink-tertiary">
                  Tab to Save and press Enter: the focus ring stays on the button for the whole
                  save, and pressing again does nothing until it finishes.
                </Typography>
              </div>
              <Typography variant="caption-metadata" as="p" className="tw-mt-4 tw-text-ink-tertiary">
                <code>className</code> is merged, not appended: a caller&rsquo;s class replaces
                the conflicting Button class &mdash; <code>tw-px-2</code> replaces the size&rsquo;s
                <code> tw-px-4</code> &mdash; instead of losing to stylesheet order.
              </Typography>
            </div>
            {/* § 391 — one slot per side. */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Slots &mdash; a glyph on either side, or both
              </Typography>
              <div className="tw-flex tw-items-center tw-gap-4 tw-flex-wrap">
                <Button variant="secondary" endIcon={<ArrowRight size={16} />} iconSize="sm" data-testid="showcase-button-end-icon">
                  Next
                </Button>
                <Button
                  variant="secondary"
                  startIcon={<NewGearIcon />}
                  endIcon={<ExternalLink size={16} />}
                  iconSize="sm"
                  data-testid="showcase-button-both-icons"
                >
                  Open settings
                </Button>
                <Button variant="subtle" startIcon={<NewGearIcon />} iconSize="sm" badge={2} data-testid="showcase-button-badge">
                  Queue
                </Button>
              </div>
              <Typography variant="caption-metadata" as="p" className="tw-mt-4 tw-text-ink-tertiary">
                <code>startIcon</code> and <code>endIcon</code> show whenever they are given, and
                <code> badge</code> whenever it has a value. The older <code>icon</code> +
                <code> iconPosition</code> + <code>showIcon</code> still draw exactly the same;
                <code> node scripts/ds-api-codemod.mjs --write &lt;path&gt;</code> folds them.
              </Typography>
            </div>
            {/* § 387 — ButtonLink: navigation in a button's clothes. */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                ButtonLink &mdash; navigation that looks like a button
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                A real link in Button&rsquo;s variants: it can be middle-clicked, opened in a new tab
                and is announced as a link. For somewhere to GO; a Button is for something to DO.
              </Typography>
              <div className="tw-flex tw-items-center tw-gap-4 tw-flex-wrap">
                <ButtonLink
                  href="/gms/rules/new"
                  onClick={(event) => { event.preventDefault(); toast.info('Would open /gms/rules/new'); }}
                  data-testid="showcase-buttonlink-primary"
                >
                  Create rule
                </ButtonLink>
                <ButtonLink
                  href="/gms/rules"
                  variant="secondary"
                  startIcon={<ArrowLeft size={16} />}
                  onClick={(event) => { event.preventDefault(); toast.info('Would open /gms/rules'); }}
                  data-testid="showcase-buttonlink-back"
                >
                  Back to rules
                </ButtonLink>
                <ButtonLink href="/gms/export" variant="tertiary" disabled data-testid="showcase-buttonlink-disabled">
                  Export (unavailable)
                </ButtonLink>
              </div>
            </div>
          </div>
        </section>

        {/* IconButton (§ 212) */}
        <section id="icon-button" data-testid="showcase-section-icon-button" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-2 tw-text-ink">
            Icon button
          </Typography>
          <DocBlock section="icon-button" />
          <Divider className="tw-mb-6" />
          <div className="tw-space-y-6">
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Subtle &mdash; the toolbar and row default
              </Typography>
              <div className="tw-flex tw-gap-1">
                <IconButton aria-label="Edit" data-testid="showcase-iconbutton-edit"><EditIcon /></IconButton>
                <IconButton aria-label="Copy"><CopyIcon /></IconButton>
                <IconButton aria-label="Refresh"><RefreshIcon /></IconButton>
                <IconButton aria-label="Settings"><Settings /></IconButton>
                <IconButton aria-label="Edit" disabled><EditIcon /></IconButton>
              </div>
            </div>
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Secondary and danger-subtle
              </Typography>
              <div className="tw-flex tw-items-center tw-gap-2">
                <IconButton aria-label="Refresh" variant="secondary"><RefreshIcon /></IconButton>
                <IconButton aria-label="Refresh" variant="secondary" disabled><RefreshIcon /></IconButton>
                <IconButton aria-label="Delete row" variant="danger-subtle" data-testid="showcase-iconbutton-delete"><DeleteIcon /></IconButton>
              </div>
            </div>
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Sizes &mdash; square on the control grid
              </Typography>
              <div className="tw-flex tw-items-center tw-gap-2">
                <IconButton aria-label="Edit" size="sm"><EditIcon /></IconButton>
                <IconButton aria-label="Edit"><EditIcon /></IconButton>
                <IconButton aria-label="Edit" size="lg"><EditIcon /></IconButton>
              </div>
            </div>
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Loading &mdash; the spinner takes the glyph, the shape stays
              </Typography>
              <div className="tw-flex tw-items-center tw-gap-2">
                <IconButton aria-label="Refresh" loading data-testid="showcase-iconbutton-loading"><RefreshIcon /></IconButton>
                <IconButton aria-label="Refresh" variant="secondary" loading><RefreshIcon /></IconButton>
                <IconButton aria-label="Delete row" variant="danger-subtle" loading><DeleteIcon /></IconButton>
              </div>
            </div>
            {/* § 388 — the name, shown. */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Tooltip &mdash; the name, shown to the eye
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                <code>tooltip</code> shows the <code>aria-label</code> on hover and keyboard focus.
                It is already the button&rsquo;s name, so a screen reader hears it once.
              </Typography>
              <div className="tw-flex tw-items-center tw-gap-2">
                <IconButton aria-label="Edit rule" tooltip data-testid="showcase-iconbutton-tooltip"><EditIcon /></IconButton>
                <IconButton aria-label="Copy rule" tooltip><CopyIcon /></IconButton>
                <IconButton aria-label="Delete rule" variant="danger-subtle" tooltip><DeleteIcon /></IconButton>
              </div>
            </div>
          </div>
        </section>

        {/* DropdownButton Section */}
        <section id="dropdown-button" data-testid="showcase-section-dropdown-button" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Dropdown button
          </Typography>
          <DocBlock section="dropdown-button" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8">
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Primary Dropdown Button
              </Typography>
              <DropdownButton
                label="Actions"
                options={dropdownButtonOptions}
                variant="primary"
                onSelect={setSelectedDropdownOption}
              />
            </div>

            <Divider />

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Secondary Dropdown Button
              </Typography>
              <DropdownButton
                label="Options"
                options={dropdownButtonOptions}
                variant="secondary"
                onSelect={setSelectedDropdownOption}
              />
            </div>

            <Divider />

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Tertiary Dropdown Button
              </Typography>
              <DropdownButton
                label="More"
                options={dropdownButtonOptions}
                variant="tertiary"
                onSelect={setSelectedDropdownOption}
              />
            </div>

            <Divider />

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Disabled Dropdown Button
              </Typography>
              <DropdownButton
                label="Disabled"
                options={dropdownButtonOptions}
                disabled
                onSelect={setSelectedDropdownOption}
              />
            </div>

            <Divider />

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Right-Aligned Dropdown Button
              </Typography>
              <div className="tw-flex tw-justify-end">
                <DropdownButton
                  label="Align Right"
                  options={dropdownButtonOptions}
                  align="end"
                  onSelect={setSelectedDropdownOption}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Segmented control (§ 171) — imported by this page for a toolbar demo but never
            given a section of its own. */}
        <section id="segmented-control" data-testid="showcase-section-segmented-control" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-2 tw-text-ink">
            Segmented control
          </Typography>
          <DocBlock section="segmented-control" />
          <Divider className="tw-mb-6" />
          <SegmentedControl
            options={[
              { id: 'all', label: 'All' },
              { id: 'parent', label: 'Parent-child' },
              { id: 'individual', label: 'Individual' },
            ]}
            value={viewModeDemo}
            onChange={setViewModeDemo}
            aria-label="Product view"
            data-testid="showcase-segmented"
          />
          {/* § 297 — the control rung. `sm` (28) is the default and the height this control
              always had; `md` (36) sits beside buttons in a toolbar; `lg` (44) is the touch
              rung. One type token at every size. */}
          <div className="tw-mt-6 tw-flex tw-flex-col tw-gap-3">
            <Typography variant="title-header" className="tw-text-ink-tertiary">
              Sizes &mdash; the control rung, one type token
            </Typography>
            {(['sm', 'md', 'lg'] as const).map((size) => (
              <div key={size} className="tw-flex tw-items-center tw-gap-4">
                <Typography variant="caption-metadata" className="tw-w-16 tw-shrink-0 tw-text-ink-tertiary tw-tabular-nums">
                  {size} · {{ sm: 28, md: 36, lg: 44 }[size]}px
                </Typography>
                <SegmentedControl
                  size={size}
                  options={[
                    { id: 'all', label: 'All' },
                    { id: 'parent', label: 'Parent-child' },
                    { id: 'individual', label: 'Individual' },
                  ]}
                  value={viewModeDemo}
                  onChange={setViewModeDemo}
                  aria-label={`Product view (${size})`}
                  data-testid={`showcase-segmented-${size}`}
                />
              </div>
            ))}
          </div>
        </section>

        <GroupHeading
          id="inputs"
          label="Inputs"
          blurb="Everything that collects a value. All of it shares one control grid and one field chrome."
          first={false}
        />

        {/* Text fields: TextField, FieldTitle and Dropdown — the field family */}
        <section id="text-fields" data-testid="showcase-section-text-fields" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Text fields
          </Typography>
          <DocBlock section="text-fields" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8">
            {/* Field Title */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Field Title
              </Typography>
              <div className="tw-space-y-2">
                <FieldTitle label="Default Label" />
                <FieldTitle label="Required Field" required />
              </div>
            </div>

            <Divider />

            {/* TextField */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Text Field
              </Typography>
              <div className="tw-space-y-6">
                <TextField
                  label="Default Text Field"
                  placeholder="Enter text"
                  value={textFieldValue}
                  onChange={setTextFieldValue}
                />

                <TextField
                  label="Text Field with Helper"
                  placeholder="Enter email"
                  type="email"
                  helperText="Enter a valid email address"
                  value={textFieldValue}
                  onChange={setTextFieldValue}
                />

                <TextField
                  label="Required Field"
                  placeholder="This is required"
                  required
                  value={textFieldValue}
                  onChange={setTextFieldValue}
                />

                <TextField
                  label="Disabled Field"
                  placeholder="Cannot edit"
                  disabled
                  value="Disabled value"
                />

                <TextField
                  label="Field with Copy Button"
                  placeholder="Click copy button"
                  value="copyable-value-123"
                  showCopyButton
                  onChange={setTextFieldValue}
                  data-testid="showcase-textfield-copy"
                />

                {/* § 303 — a chosen value has a way back to the placeholder. */}
                <Dropdown
                  label="Environment (clearable)"
                  value={clearableDropdown}
                  onChange={setClearableDropdown}
                  onClear={() => setClearableDropdown(undefined)}
                  options={[{ label: 'Production', value: 'p' }, { label: 'Staging', value: 's' }]}
                  placeholder="Choose an environment"
                  helperText="The × beside the chevron clears the value."
                  data-testid="showcase-dropdown-clearable"
                />

                <TextField
                  label="Error Field"
                  placeholder="Invalid input"
                  error
                  helperText="This field has an error"
                  value={textFieldValue}
                  onChange={setTextFieldValue}
                />

                <TextField
                  label="Hint and error together"
                  placeholder="name@company.com"
                  type="email"
                  helperText="Use your work address."
                  errorMessage="That address is already registered."
                  value={textFieldValue}
                  onChange={setTextFieldValue}
                  data-testid="showcase-textfield-error-message"
                />

                <TextField
                  label="With Info Icon"
                  placeholder="Hover over the info icon"
                  infoMessage="This field requires a valid email address format"
                  value={textFieldValue}
                  onChange={setTextFieldValue}
                />

                <TextField
                  label="Info Icon with Copy Button"
                  placeholder="Click copy or hover info"
                  infoMessage="Copy this identifier for reference"
                  value="field-identifier-789"
                  showCopyButton
                  onChange={setTextFieldValue}
                />

                <TextField
                  label="Required with Info Icon"
                  placeholder="This is required"
                  required
                  infoMessage="Please provide the required information"
                  value={textFieldValue}
                  onChange={setTextFieldValue}
                />

                <TextField
                  label="Info Icon with Helper Text"
                  placeholder="Enter data"
                  infoMessage="Additional help available"
                  helperText="Use alphanumeric characters only"
                  value={textFieldValue}
                  onChange={setTextFieldValue}
                />

                <TextField
                  label="Disabled with Info Icon"
                  placeholder="Cannot edit"
                  disabled
                  infoMessage="This field is currently disabled"
                  value="Disabled value"
                />

                <TextField
                  label="Error with Info Icon"
                  placeholder="Invalid input"
                  error
                  infoMessage="Click to learn more about this error"
                  helperText="This field has an error"
                  value={textFieldValue}
                  onChange={setTextFieldValue}
                />
              </div>
            </div>

            <Divider />

            {/* Dropdown */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Dropdown
              </Typography>
              <div className="tw-space-y-6">
                <Dropdown
                  label="Default Dropdown"
                  options={dropdownOptions}
                  value={dropdownValue}
                  onChange={setDropdownValue}
                  placeholder="Select an option"
                />

                <Dropdown
                  label="Dropdown with Helper"
                  options={dropdownOptions}
                  value={dropdownValue}
                  onChange={setDropdownValue}
                  helperText="Choose one from the list"
                />

                <Dropdown
                  label="Required Dropdown"
                  options={dropdownOptions}
                  value={dropdownValue}
                  onChange={setDropdownValue}
                  required
                />

                <Dropdown
                  label="Disabled Dropdown"
                  options={dropdownOptions}
                  disabled
                  value="option1"
                />

                <Dropdown
                  label="Error Dropdown"
                  options={dropdownOptions}
                  value={dropdownValue}
                  onChange={setDropdownValue}
                  error
                  helperText="Please select a valid option"
                />

                {/* § 65: the category-switcher pattern — grouped options + a filter input. */}
                <Dropdown
                  label="Grouped + searchable"
                  data-testid="showcase-grouped-dropdown"
                  searchable
                  searchPlaceholder="Search categories"
                  helperText="Groups come from the options' group field; the search input filters the list and resets on close."
                  options={[
                    { value: 'mobile', label: 'Mobile Case', group: 'Accessories New' },
                    { value: 'charger', label: 'Wall Charger', group: 'Accessories New' },
                    { value: 'commend', label: 'Commend', group: 'Subscription' },
                    { value: 'guide', label: 'Guide', group: 'Subscription' },
                    { value: 'dmt', label: 'DMT', group: 'DM' },
                  ]}
                  value={groupedCategory}
                  onChange={setGroupedCategory}
                />

                <Dropdown
                  label="With Additional Info"
                  options={dropdownOptions}
                  value={dropdownValue}
                  onChange={setDropdownValue}
                  additionalInfo="This is additional information about this dropdown"
                />

                <Dropdown
                  label="With Info Icon"
                  options={dropdownOptions}
                  value={dropdownValue}
                  onChange={setDropdownValue}
                  infoMessage="Select an option from the dropdown"
                  placeholder="Select an option"
                />

                <Dropdown
                  label="Required with Info Icon"
                  options={dropdownOptions}
                  value={dropdownValue}
                  onChange={setDropdownValue}
                  required
                  infoMessage="This field is required"
                />

                <Dropdown
                  label="Info Icon with Helper"
                  options={dropdownOptions}
                  value={dropdownValue}
                  onChange={setDropdownValue}
                  infoMessage="Additional help available"
                  helperText="Choose one from the list"
                />

                <Dropdown
                  label="Disabled with Info Icon"
                  options={dropdownOptions}
                  disabled
                  value="option1"
                  infoMessage="This dropdown is currently disabled"
                />

                <Dropdown
                  label="Error with Info Icon"
                  options={dropdownOptions}
                  value={dropdownValue}
                  onChange={setDropdownValue}
                  error
                  infoMessage="Click to learn more about this error"
                  helperText="Please select a valid option"
                />
              </div>
            </div>

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Number field
              </Typography>
              <Typography variant="body-primary" className="tw-mb-4 tw-max-w-[68ch] tw-text-ink-secondary">
                A text input wearing <code>role=&quot;spinbutton&quot;</code>, not a native number
                input: arrows step (Shift for ten), Home and End reach the bounds, the value
                is clamped and rounded on blur, and what leaves the field is always a number
                or <code>null</code>. The &minus; / + buttons are the mouse&rsquo;s route and
                stay out of the tab order.
              </Typography>
              <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2 tw-max-w-2xl">
                <NumberField
                  label="Quantity"
                  value={quantity}
                  onChange={setQuantity}
                  min={0}
                  max={999}
                  unit="pcs"
                  helperText="0 to 999, whole units"
                  data-testid="showcase-numberfield"
                />
                <NumberField
                  label="Weight"
                  value={weight}
                  onChange={setWeight}
                  min={0}
                  step={0.5}
                  unit="kg"
                  helperText="Steps by 0.5"
                  data-testid="showcase-numberfield-weight"
                />
                <NumberField
                  label="Disabled"
                  value={40}
                  onChange={() => {}}
                  unit="%"
                  disabled
                  data-testid="showcase-numberfield-disabled"
                />
                <NumberField
                  label="Over the limit"
                  value={1200}
                  onChange={() => {}}
                  max={999}
                  error
                  helperText="Maximum 999"
                  steppers={false}
                  data-testid="showcase-numberfield-error"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Textarea — the one field where py-* is correct */}
        <section id="textarea" data-testid="showcase-section-textarea" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Textarea
          </Typography>
          <DocBlock section="textarea" />
          <Divider className="tw-mb-6" />

          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6 tw-max-w-3xl">
            <Textarea
              label="Description"
              value={textareaValue}
              onChange={setTextareaValue}
              placeholder="What does this submodel do?"
              helperText="Shown to merchants in the catalogue."
              data-testid="showcase-textarea"
            />
            <Textarea
              label="Notes"
              value={textareaCounted}
              onChange={setTextareaCounted}
              maxLength={120}
              showCount
              rows={4}
              data-testid="showcase-textarea-count"
            />
            <Textarea
              label="Required, in error"
              value=""
              onChange={() => {}}
              required
              error
              helperText="This field is required."
              data-testid="showcase-textarea-error"
            />
            <Textarea
              label="Disabled"
              value="Read-only content."
              onChange={() => {}}
              disabled
              resize="none"
              data-testid="showcase-textarea-disabled"
            />
          </div>

          <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-4 tw-block">
            The counter is deliberately <strong>not</strong> a live region — announcing on every
            keystroke would make the field unusable with a screen reader. It sits in{' '}
            <span className="tw-text-code-formula">aria-describedby</span>, so it is read when the
            field takes focus. This is also the one field that <em>keeps</em>{' '}
            <span className="tw-text-code-formula">py-*</span>: the others declare a fixed height
            from the control grid, and a textarea has none.
          </Typography>
        </section>

        {/* Search Section */}
        <section id="search" data-testid="showcase-section-search" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Search
          </Typography>
          <DocBlock section="search" />
          <Divider className="tw-mb-6" />

          {/*
            Constrained to `max-w-md`. These rendered edge to edge — ~1830px wide on
            a desktop — which is not a width anybody ships a search field at, and it
            made the component look broken rather than merely unpolished.

            Each example now uses `label`, which `Search` gained on 2026-08-24. The
            old headings above each field were plain `Typography`, associated with
            nothing: a sighted user read "Default Search" and a screen-reader user
            heard the placeholder.
          */}
          <div className="tw-space-y-8 tw-max-w-md">
            <div>
              <Search
                label="Default search"
                placeholder="Search here..."
                value={searchValue}
                onChange={setSearchValue}
                clearable
                data-testid="showcase-search-default"
              />
            </div>

            <Divider />

            <div>
              <Search
                label="Search with helper"
                placeholder="Enter search term..."
                value={searchValue}
                onChange={setSearchValue}
                helperText="Type to search and press Enter"
                clearable
                data-testid="showcase-search-helper"
              />
            </div>

            <Divider />

            <div>
              <Search
                label="Disabled search"
                placeholder="Disabled search..."
                value=""
                onChange={() => {}}
                disabled
                data-testid="showcase-search-disabled"
              />
            </div>

            <Divider />

            <div>
              <Search
                label="Search with error"
                placeholder="Enter valid search..."
                value=""
                onChange={() => {}}
                error
                helperText="Invalid search term"
                data-testid="showcase-search-error"
              />
            </div>

            <Divider />

            {/* § 288 — type in this one. The magnifier becomes a spinner for as long
                as the fake 900ms fetch runs, and the field keeps taking keystrokes the
                whole time: `loading` here is an ANNOUNCEMENT, not a gate. */}
            <div>
              <Search
                label="Search-as-you-type (loading)"
                placeholder="Type to fire a 900ms fetch..."
                value={asyncSearchValue}
                onChange={handleAsyncSearch}
                loading={searchBusy}
                loadingLabel="Searching products"
                clearable
                data-testid="showcase-search-loading"
              />
              <Typography variant="caption-metadata" as="p" className="tw-mt-2 tw-text-ink-tertiary">
                The spinner takes the glyph&rsquo;s slot, so nothing reflows &mdash; and unlike
                <code> Button</code>&rsquo;s <code>loading</code>, it blocks nothing:
                keep typing, press Enter, or clear it while it spins.
              </Typography>
            </div>

            <Divider />

            {/* § 297 — the shortcut slot: keycaps at the trailing edge while the field is
                empty, the Menu § 247 arrangement (the ARIA spelling is displayed AND told). */}
            <div>
              <Search
                label="Search with a shortcut"
                placeholder="Search products, rules, categories…"
                value={shortcutSearchValue}
                onChange={setShortcutSearchValue}
                shortcut="Meta+K"
                clearable
                data-testid="showcase-search-shortcut"
              />
              <Typography variant="caption-metadata" as="p" className="tw-mt-2 tw-text-ink-tertiary">
                The caps advertise the key that focuses the field and step aside for the clear
                button once there is a query. Display only &mdash; binding the key is the page&rsquo;s.
              </Typography>
            </div>

            <div>
              <Search
                label="Search that paces itself"
                placeholder="Type — the field reports after you pause"
                value={debouncedQuery}
                onChange={setDebouncedQuery}
                onSearch={setLastSearched}
                debounceMs={DEBOUNCE.search}
                clearable
                data-testid="showcase-search-debounced"
              />
              <Typography variant="caption-metadata" as="p" className="tw-mt-2 tw-text-ink-tertiary" data-testid="showcase-search-debounced-readout">
                {lastSearched === null
                  ? 'onSearch has not fired yet.'
                  : `onSearch last received “${lastSearched}” — once, ${DEBOUNCE.search} ms after the last keystroke, or at once on Enter.`}
              </Typography>
            </div>

            <Divider />

            {/* The three sizes differ in DENSITY only — the type token is fixed at
                `body-primary`, so a Search never out-sizes a TextField beside it. */}
            <div className="tw-space-y-4">
              <Typography variant="title-header" className="tw-text-ink-tertiary">
                Sizes — padding only, one type token
              </Typography>
              {(['sm', 'md', 'lg'] as const).map((size) => (
                <Search
                  key={size}
                  label={`size="${size}"`}
                  labelSize="sm"
                  size={size}
                  placeholder="Search products..."
                  value=""
                  onChange={() => {}}
                  data-testid={`showcase-search-${size}`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Combobox (§ 210) */}
        <section id="combobox" data-testid="showcase-section-combobox" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Combobox
          </Typography>
          <DocBlock section="combobox" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8 tw-max-w-md">
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Single &mdash; type to filter
              </Typography>
              <Combobox
                label="Part"
                options={PART_OPTIONS}
                value={comboValue}
                onChange={setComboValue}
                placeholder="Type a part name…"
                helperText="One option is disabled, and the keyboard skips it."
                data-testid="showcase-combobox-single"
              />
            </div>

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Clearable &mdash; the &times; while something is chosen; Backspace clears too
              </Typography>
              <Combobox
                label="Match on"
                options={[{ value: 'mpn', label: 'MPN' }, { value: 'upc', label: 'UPC' }, { value: 'sku', label: 'SKU' }]}
                value={clearableCombo}
                onChange={setClearableCombo}
                clearable
                data-testid="showcase-combobox-clearable"
              />
            </div>

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Multiple &mdash; chips in the field
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                Picking keeps the list open, picking again removes, Backspace in an empty query
                removes the last chip. The chips are the design system&rsquo;s own <code>Tag</code>.
              </Typography>
              <Combobox
                label="Parts"
                multiple
                options={PART_OPTIONS}
                values={comboValues}
                onValuesChange={setComboValues}
                placeholder="Add parts…"
                data-testid="showcase-combobox-multi"
              />
            </div>

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Async &mdash; the server does the matching
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                <code>onSearch</code> reports every keystroke and the local filter is SKIPPED
                &mdash; re-filtering the server&rsquo;s answer is a silent narrowing. This demo
                fakes a 450ms round trip.
              </Typography>
              <Combobox
                label="Part (remote)"
                options={comboAsyncOptions}
                onSearch={handleComboSearch}
                loading={comboAsyncLoading}
                value={null}
                onChange={() => {}}
                placeholder="Searches a fake server…"
                data-testid="showcase-combobox-async"
              />
            </div>

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                States
              </Typography>
              <div className="tw-space-y-4">
                <Combobox
                  label="With an error"
                  options={PART_OPTIONS}
                  value={null}
                  onChange={() => {}}
                  error
                  helperText="Pick at least one part."
                  data-testid="showcase-combobox-error"
                />
                <Combobox
                  label="Disabled"
                  options={PART_OPTIONS}
                  value="hex-bolt"
                  onChange={() => {}}
                  disabled
                  data-testid="showcase-combobox-disabled"
                />
              </div>
            </div>

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Creatable
              </Typography>
              <Typography variant="body-primary" className="tw-mb-4 tw-max-w-[68ch] tw-text-ink-secondary">
                Type a material that is not listed and the last row offers to create it. The
                caller adds the option and selects it &mdash; the list is the caller&rsquo;s.
              </Typography>
              <Combobox
                label="Material"
                options={creatableOptions}
                value={creatableValue}
                onChange={setCreatableValue}
                creatable
                onCreate={(query) => {
                  const value = query.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                  setCreatableOptions((prev) => [...prev, { value, label: query }]);
                  setCreatableValue(value);
                }}
                placeholder="Search or add a material"
                data-testid="showcase-combobox-creatable"
              />
            </div>
          </div>
        </section>

        {/* Toggle, Checkbox, Radio Section */}
        <section id="choice-controls" data-testid="showcase-section-choice-controls" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Toggle, checkbox &amp; radio
          </Typography>
          <DocBlock section="choice-controls" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8">
            {/* Toggle Section */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Toggle
              </Typography>
              <div className="tw-space-y-4">
                <Toggle
                  label="Default Toggle (Unchecked)"
                  checked={toggleChecked}
                  onChange={setToggleChecked}
                />
                <Toggle
                  label="Default Toggle (Checked)"
                  checked={true}
                  onChange={() => {}}
                />
                <Toggle
                  data-testid="showcase-toggle-disabled-off"
                  label="Disabled Toggle (Unchecked)"
                  checked={false}
                  onChange={() => {}}
                  disabled={true}
                />
                <Toggle
                  data-testid="showcase-toggle-disabled-on"
                  label="Disabled Toggle (Checked)"
                  checked={true}
                  onChange={() => {}}
                  disabled={true}
                />
              </div>
            </div>

            <Divider />

            {/* Checkbox Section */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Checkbox
              </Typography>
              <div className="tw-space-y-4">
                <Checkbox
                  label="Default (Unchecked)"
                  checked={checkboxChecked}
                  onChange={setCheckboxChecked}
                />
                <Checkbox
                  label="Selected (Checked)"
                  checked={true}
                  onChange={() => {}}
                />
                <Checkbox
                  label="Mixed (Indeterminate)"
                  checked={false}
                  indeterminate={checkboxIndeterminate}
                  onChange={() => setCheckboxIndeterminate(!checkboxIndeterminate)}
                  data-testid="showcase-checkbox-mixed"
                />
                <Checkbox
                  label="Disabled (Unchecked)"
                  checked={false}
                  onChange={() => {}}
                  disabled
                />
                <Checkbox
                  label="Disabled (Checked)"
                  checked={true}
                  onChange={() => {}}
                  disabled
                />
                <Checkbox
                  label="Disabled (Mixed)"
                  checked={false}
                  indeterminate={true}
                  onChange={() => {}}
                  disabled
                />
              </div>

              {/* `size` used to be computed and then ignored — sm and md rendered
                  identically at 16px. It is honoured now. */}
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mt-6 tw-mb-3">
                Sizes
              </Typography>
              <div className="tw-space-y-4">
                <Checkbox
                  label='size="md" — 20px, the default, matching Radio'
                  size="md"
                  checked={mediumBoxChecked}
                  onChange={setMediumBoxChecked}
                  data-testid="showcase-checkbox-md"
                />
                <Checkbox
                  label='size="sm" — 16px'
                  size="sm"
                  checked={smallBoxChecked}
                  onChange={setSmallBoxChecked}
                  data-testid="showcase-checkbox-sm"
                />
              </div>
            </div>

            <Divider />

            {/* Radio Section */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Radio Group
              </Typography>
              <div className="tw-space-y-4">
                <Radio
                  label="Default (Unselected)"
                  value="default"
                  checked={false}
                  onChange={setRadioValue}
                />
                <Radio
                  label="Selected (Checked)"
                  value="selected"
                  checked={true}
                  onChange={setRadioValue}
                />
                <Radio
                  label="Disabled (Unselected)"
                  value="disabled-unselected"
                  checked={false}
                  onChange={() => {}}
                  disabled
                />
                <Radio
                  label="Disabled (Checked)"
                  value="disabled-selected"
                  checked={true}
                  onChange={() => {}}
                  disabled
                />
              </div>

              {/* `label` is a ReactNode, matching Checkbox. An option whose meaning
                  needs a sentence keeps that sentence inside the <label>, so clicking
                  the explanation still selects the option it explains. Note the
                  control sits at the TOP of a multi-line label — Radio aligns
                  `items-start` like Checkbox, not `items-center`. */}
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mt-6 tw-mb-3">
                Option with a description
              </Typography>
              <div
                role="radiogroup"
                aria-label="Restrictions"
                className="tw-space-y-3"
                data-testid="showcase-radio-rich-group"
              >
                <Radio
                  name="showcase-rich-radio"
                  value="recommended"
                  checked={richRadioValue === 'recommended'}
                  onChange={setRichRadioValue}
                  data-testid="showcase-radio-rich-recommended"
                  label={(
                    <span className="tw-block">
                      <span className="tw-block tw-font-medium">Recommended</span>
                      <span className="tw-block tw-text-caption-metadata tw-text-ink-secondary">
                        Shows a * indicator. Soft guidance only — no hard gates.
                      </span>
                    </span>
                  )}
                />
                <Radio
                  name="showcase-rich-radio"
                  value="required"
                  checked={richRadioValue === 'required'}
                  onChange={setRichRadioValue}
                  data-testid="showcase-radio-rich-required"
                  label={(
                    <span className="tw-block">
                      <span className="tw-block tw-font-medium">Required</span>
                      <span className="tw-block tw-text-caption-metadata tw-text-ink-secondary">
                        Shows a * indicator and enforces fulfilment before checkout.
                      </span>
                    </span>
                  )}
                />
              </div>

              {/* `labelClamp={false}` — the default two-line clamp is right for a
                  checkbox in a table row, wrong where the extra lines ARE the content. */}
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mt-6 tw-mb-3">
                Checkbox with an unclamped label
              </Typography>
              <Checkbox
                labelClamp={false}
                checked={checkboxChecked}
                onChange={setCheckboxChecked}
                data-testid="showcase-checkbox-unclamped"
                label={(
                  <span className="tw-block">
                    <span className="tw-block tw-font-medium">Block Add to Cart</span>
                    <span className="tw-block tw-text-caption-metadata tw-text-ink-secondary">
                      Disables Add to Cart until fulfilled. Hover: &quot;Add to cart disabled
                      until Required fields and products are selected.&quot;
                    </span>
                  </span>
                )}
              />

              {/* `labelPlacement="top"` stacks the caption above the switch. A row of
                  four captioned toggles is roughly three times as wide inline. */}
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mt-6 tw-mb-3">
                Toggle with the label above
              </Typography>
              <div className="tw-flex tw-items-start tw-gap-10">
                <Toggle
                  label="Auto add to build"
                  labelPlacement="top"
                  checked={stackedToggle}
                  onChange={setStackedToggle}
                  data-testid="showcase-toggle-stacked"
                />
                <Toggle
                  label="Auto add to build"
                  checked={stackedToggle}
                  onChange={setStackedToggle}
                  data-testid="showcase-toggle-inline"
                />
              </div>

              {/* § 303 — the group Radio always needed: one name (arrow keys, one tab
                  stop), the group role, a label, requiredness and the error line. */}
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mt-6 tw-mb-3">
                RadioGroup &mdash; the radios as a field
              </Typography>
              <RadioGroup
                label="Promotion"
                required
                value={groupValue}
                onChange={setGroupValue}
                options={[
                  { value: 'immediate', label: 'Promote immediately' },
                  { value: 'scheduled', label: 'Schedule for later' },
                  { value: 'never', label: 'Keep as draft', disabled: true },
                ]}
                helperText="Arrow keys move between the options; the group is one tab stop."
                data-testid="showcase-radiogroup"
              />
              <div className="tw-mt-4">
                <RadioGroup
                  label="Compare by"
                  orientation="horizontal"
                  size="sm"
                  value={groupValue === 'never' ? 'immediate' : groupValue}
                  onChange={setGroupValue}
                  options={[
                    { value: 'immediate', label: 'MPN' },
                    { value: 'scheduled', label: 'UPC' },
                  ]}
                  error
                  helperText="Pick one to continue."
                  data-testid="showcase-radiogroup-horizontal"
                />
              </div>

              {/* § 371 — the checkboxes as a field: the group role, a label, the error line, and a
                  select-all that never flips a disabled option. */}
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mt-6 tw-mb-3">
                CheckboxGroup &mdash; the checkboxes as a field
              </Typography>
              <CheckboxGroup
                label="Materials"
                selectAllLabel="All materials"
                defaultValue={['steel']}
                options={[
                  { value: 'steel', label: 'Stainless steel' },
                  { value: 'brass', label: 'Brass' },
                  { value: 'nylon', label: 'Nylon' },
                  { value: 'ptfe', label: 'PTFE (discontinued)', disabled: true },
                ]}
                helperText="Select all ticks every enabled option and leaves a disabled one alone."
                data-testid="showcase-checkboxgroup"
              />
              <div className="tw-mt-4">
                <CheckboxGroup
                  label="Export as"
                  required
                  orientation="horizontal"
                  size="sm"
                  defaultValue={[]}
                  options={[
                    { value: 'csv', label: 'CSV' },
                    { value: 'xlsx', label: 'Excel' },
                    { value: 'json', label: 'JSON' },
                  ]}
                  errorMessage="Choose at least one format"
                  data-testid="showcase-checkboxgroup-horizontal"
                />
              </div>

              {/* § 303 — Toggle joins the two-rung family Checkbox and Radio already share. */}
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mt-6 tw-mb-3">
                Toggle sizes &mdash; sm beside md
              </Typography>
              <div className="tw-flex tw-items-center tw-gap-8">
                <Toggle label="Dense row" size="sm" checked={smToggle} onChange={setSmToggle} data-testid="showcase-toggle-sm" />
                <Toggle label="Default" checked={smToggle} onChange={setSmToggle} data-testid="showcase-toggle-md" />
                <Toggle label="Locked" size="sm" checked disabled onChange={() => {}} data-testid="showcase-toggle-sm-disabled" />
              </div>
            </div>
          </div>
        </section>

        {/* Slider Section */}
        <section id="slider" data-testid="showcase-section-slider" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Slider
          </Typography>
          <DocBlock section="slider" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8 tw-max-w-lg">
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                With a visible label and a readout
              </Typography>
              <Slider
                label="Traffic share"
                value={trafficShare}
                onChange={setTrafficShare}
                showValue
                formatValue={(percentage) => `${percentage}%`}
                data-testid="showcase-slider-traffic"
              />
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-2 tw-block">
                The track fills to the value. Its predecessor painted the whole track one
                blue, so the bar itself carried no progress information — only the thumb did.
              </Typography>
            </div>

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Bounded, with a unit — min 4, max 24
              </Typography>
              <Slider
                label="Page spacing"
                value={spacing}
                onChange={setSpacing}
                min={4}
                max={24}
                showValue
                formatValue={(px) => `${px}px`}
                size="sm"
                data-testid="showcase-slider-spacing"
              />
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-2 tw-block">
                `formatValue` drives the readout AND `aria-valuetext`, so the announcement
                carries the unit instead of a bare number the two cannot disagree on.
              </Typography>
            </div>

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Name without a visible label
              </Typography>
              <Slider
                label="Traffic share — Model B (Variant)"
                hideLabel
                value={trafficShare}
                onChange={setTrafficShare}
                formatValue={(percentage) => `${percentage}%`}
                data-testid="showcase-slider-hidden-label"
              />
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-2 tw-block">
                For a slider in a grid whose column caption renders once above the column.
                The label row is gone; the accessible name is not.
              </Typography>
            </div>

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Disabled
              </Typography>
              <Slider
                label="Locked width"
                value={70}
                onChange={() => undefined}
                disabled
                showValue
                formatValue={(percentage) => `${percentage}%`}
                data-testid="showcase-slider-disabled"
              />
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-2 tw-block">
                Greyed, but still legible as a track with a position — the rule from
                Toggle: exempt from SC 1.4.11 does not license invisibility.
              </Typography>
            </div>
          </div>
        </section>

        {/* Date & time (§ 171). Both replaced hand-rolled controls that shipped byte-identical
            copies of the same zero-padding formatter; the DS pair hands the ISO string back. */}
        <section id="date-time" data-testid="showcase-section-date-time" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Date &amp; time
          </Typography>
          <DocBlock section="date-time" />
          <Divider className="tw-mb-6" />
          <div className="tw-flex tw-flex-wrap tw-items-start tw-gap-6">
            <DatePicker
              label="Start date"
              value={demoDate}
              onChange={(_date, formatted) => setDemoDate(formatted)}
              helperText={`Stored as ${demoDate || '—'}`}
              data-testid="showcase-datepicker"
            />
            {/* § 290 — the field reads 12-hour and OWNS its "stored as" line now, so
                the demo's hand-written helper is gone rather than duplicated. Type
                `17:06` or `5:06 pm` into it; both land on the same stored value. */}
            <TimePicker
              label="Run at"
              value={demoTime}
              onChange={setDemoTime}
              data-testid="showcase-timepicker"
            />
          </div>
        </section>

        {/* FileUpload (§ 214) */}
        <section id="file-upload" data-testid="showcase-section-file-upload" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            File upload
          </Typography>
          <DocBlock section="file-upload" />
          <Divider className="tw-mb-6" />
          <div className="tw-space-y-6 tw-max-w-md">
            <div>
              <FileUpload
                label="Import products"
                accept=".csv,.xlsx"
                multiple
                helperText="The demo lists what the component delivered."
                onFilesSelected={(files) => setUploadNames((prev) => [...prev, ...files.map((f) => f.name)])}
                data-testid="showcase-fileupload"
              />
              {uploadNames.length > 0 && (
                <div className="tw-flex tw-flex-wrap tw-gap-1 tw-mt-2">
                  {uploadNames.map((n, i) => (
                    <Tag
                      // Demo-only: duplicate names are legal here, so the key needs the index.
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${n}-${i}`}
                      label={n}
                      variant="meta"
                      onRemove={() => setUploadNames((prev) => prev.filter((_, j) => j !== i))}
                    />
                  ))}
                </div>
              )}
            </div>
            <FileUpload
              label="With an error"
              error
              helperText="CSV only — that file is an image."
              onFilesSelected={() => {}}
              data-testid="showcase-fileupload-error"
            />
            <FileUpload
              label="Disabled"
              disabled
              onFilesSelected={() => {}}
              data-testid="showcase-fileupload-disabled"
            />
          </div>
        </section>

        <GroupHeading
          id="navigation"
          label="Navigation"
          blurb="Moving between views, pages and records."
          first={false}
        />

        {/* Tab Section */}
        <section id="tabs" data-testid="showcase-section-tabs" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Tabs
          </Typography>
          <DocBlock section="tabs" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8">
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Default Tabs
              </Typography>
              <Tab
                tabs={tabItems}
                activeTabId={activeTabId}
                onChange={setActiveTabId}
              />
            </div>

            <Divider />

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Tabs with Icons
              </Typography>
              <Tab
                tabs={tabItemsWithIcons}
                activeTabId="settings"
              />
            </div>

            <Divider />

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Tabs with Badges
              </Typography>
              <Tab
                tabs={tabItemsWithBadges}
                activeTabId="inbox"
              />
            </div>

            <Divider />

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Tabs with Icons and Badges
              </Typography>
              <Tab
                tabs={tabItemsWithIconsAndBadges}
                activeTabId="notifications"
              />
            </div>

            <Divider />

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Tabs without Icons (showIcons=false)
              </Typography>
              <Tab
                tabs={tabItemsWithIcons}
                activeTabId="settings"
                showIcons={false}
              />
            </div>

            <Divider />

            {/* § 395 — the row that outgrows its column. */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                More tabs than the column holds
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                The row scrolls inside its column instead of running past it, the selected tab
                is kept in view, and the focus ring has room at both ends. A row that fits is
                the plain row above, pixel for pixel.
              </Typography>
              <div className="tw-max-w-sm">
                <Tab
                  tabs={OVERFLOW_TAB_ITEMS}
                  defaultActiveTabId="history"
                  data-testid="showcase-tabs-overflow"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Menu Section */}
        <section id="menu" data-testid="showcase-section-menu" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Menu
          </Typography>
          <DocBlock section="menu" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8">
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Default Menu
              </Typography>
              <Menu
                items={menuItems}
                onItemClick={setSelectedMenuId}
              />
            </div>

            {/* § 386 — the Menu that anchors itself. */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                With its own trigger
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                Hand Menu the element that opens it and it anchors itself: it opens beside the
                trigger and stays on screen, Enter, Space or ArrowDown open it on the first item,
                ArrowUp on the last, and Escape, Tab or a pick close it and return focus.
              </Typography>
              <div className="tw-flex tw-items-center tw-gap-4">
                <Menu
                  trigger={<Button variant="secondary" data-testid="showcase-menu-trigger">Rule actions</Button>}
                  items={[
                    { id: 'duplicate', label: 'Duplicate', 'data-testid': 'showcase-menu-trigger-duplicate' },
                    { id: 'rename', label: 'Rename' },
                    { id: 'delete', label: 'Delete', tone: 'danger' },
                  ]}
                  onItemClick={(id) => toast.info(`Picked ${String(id)}`)}
                  data-testid="showcase-menu-anchored"
                />
                <Menu
                  trigger={(
                    <IconButton aria-label="More actions" tooltip data-testid="showcase-menu-trigger-icon">
                      <MoreVertical />
                    </IconButton>
                  )}
                  align="end"
                  sections={[
                    { label: 'Sort', items: [
                      { id: 'name', label: 'By name', selected: true },
                      { id: 'updated', label: 'By last update', selected: false },
                    ] },
                    { items: [{ id: 'export', label: 'Export CSV', shortcut: 'Meta+E' }] },
                  ]}
                  onItemClick={(id) => toast.info(`Picked ${String(id)}`)}
                  data-testid="showcase-menu-anchored-icon"
                />
              </div>
            </div>

            <Divider />

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Compact Menu
              </Typography>
              <Menu
                items={menuItems}
                variant="compact"
                onItemClick={setSelectedMenuId}
              />
            </div>

            <Divider />

            {/* § 247 — the Linear/Raycast grammar: a keycap row after the label, a
                second line saying what the action does. */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Shortcuts and descriptions
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                <code>shortcut</code> renders a keycap row after the label and lands verbatim in{' '}
                <code>aria-keyshortcuts</code>; <code>description</code> is a second line in caption
                ink, announced as the item&rsquo;s description while the name stays the label. The menu
                advertises the shortcut — the caller binds it.
              </Typography>
              <div className="tw-w-[340px]">
                <Menu
                  data-testid="showcase-shortcut-menu"
                  items={[
                    { id: 'sc-new', label: 'New rule', description: 'Start from a blank condition set', shortcut: 'Meta+N', 'data-testid': 'showcase-menu-shortcut-new' },
                    { id: 'sc-dup', label: 'Duplicate', description: 'Copy this rule and its conditions', shortcut: 'Meta+D', 'data-testid': 'showcase-menu-shortcut-dup' },
                    { id: 'sc-search', label: 'Search rules', shortcut: 'Control+K' },
                    { id: 'sc-del', label: 'Delete', description: 'Removes the rule from every environment', shortcut: 'Shift+Delete', tone: 'danger' as const },
                  ]}
                  onItemClick={setSelectedMenuId}
                />
              </div>
            </div>

            <Divider />

            {/* `onDismiss` is opt-in: Menu is presentational, so the caller owns
                whether it is mounted. Supply it and the menu closes itself on
                Escape and on an outside pointer-down. */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Self-dismissing Menu (onDismiss)
              </Typography>
              <Button
                onClick={() => setDismissibleMenuOpen(true)}
                data-testid="showcase-open-menu"
              >
                Open menu
              </Button>
              {dismissibleMenuOpen && (
                <div className="tw-mt-3 tw-w-64" data-testid="showcase-dismissible-menu">
                  <Menu
                    items={menuItems}
                    onItemClick={(id) => {
                      setSelectedMenuId(id);
                      setDismissibleMenuOpen(false);
                    }}
                    onDismiss={() => setDismissibleMenuOpen(false)}
                  />
                </div>
              )}
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-3">
                Press Escape, or click outside it. Arrow keys move between items; type a name to
                jump to it.
              </Typography>
            </div>

            <Divider />

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Submenu (expands in place)
              </Typography>
              <div className="tw-w-64">
                <Menu
                  items={SHOWCASE_SUBMENU_ITEMS}
                  onItemClick={setSelectedMenuId}
                  data-testid="showcase-submenu-menu"
                />
              </div>
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-3">
                The children open under their row, as a group it names. Right or Enter opens it and
                moves in; Left leaves; Escape on a child closes just the submenu.
              </Typography>
            </div>

            <Divider />

            {/* § 65: the toolbar grammar — sections, radio items, a non-menu footer. */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Sections, radio items and a footer
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                Labelled sections group related actions; items with a selected state render as
                menu radios in the one selection grammar (accent on its tint); the footer holds
                what ARIA forbids between menu items — here, a SegmentedControl. A disabled item
                can carry a tooltip that says why. Rendered compact — the toolbar-menu density
                (28px rows).
              </Typography>
              <div className="tw-w-[280px]">
                <Menu
                  data-testid="showcase-sectioned-menu"
                  variant="compact"
                  sections={[
                    {
                      items: [
                        { id: 'asc', label: 'Ascending', selected: sortDirection === 'asc', onClick: () => setSortDirection('asc'), 'data-testid': 'showcase-menu-radio-asc' },
                        { id: 'desc', label: 'Descending', selected: sortDirection === 'desc', onClick: () => setSortDirection('desc'), 'data-testid': 'showcase-menu-radio-desc' },
                      ],
                    },
                    {
                      label: 'Bulk actions',
                      items: [
                        { id: 'export', label: 'Download as XLS' },
                        { id: 'recategorize', label: 'Recategorize', disabled: true, tooltip: 'Recategorize is limited to 250 products' },
                      ],
                    },
                  ]}
                  footer={
                    <div>
                      <Typography variant="sub-title-label" className="tw-block tw-px-1 tw-pb-1 tw-text-ink-tertiary">
                        Product type
                      </Typography>
                      <SegmentedControl
                        aria-label="Product type"
                        data-testid="showcase-menu-segmented"
                        options={[
                          { id: 'all', label: 'All' },
                          { id: 'parent', label: 'Parent/Child' },
                          { id: 'individual', label: 'Individual' },
                        ]}
                        value={productTypeDemo}
                        onChange={setProductTypeDemo}
                      />
                    </div>
                  }
                />
              </div>
            </div>
          </div>
        </section>

        {/* Kbd (§ 247) */}
        <section id="kbd" data-testid="showcase-section-kbd" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Kbd
          </Typography>
          <DocBlock section="kbd" />
          <Divider className="tw-mb-6" />
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-6" data-testid="showcase-kbd">
            {/* § 303 — the SAME ARIA string, printed per platform: the cap speaks the
                keyboard's language, the announcement keeps the ARIA one. */}
            <span className="tw-flex tw-items-center tw-gap-1" data-testid="showcase-kbd-mac"><Kbd platform="mac">Meta</Kbd><Kbd platform="mac">K</Kbd></span>
            <span className="tw-flex tw-items-center tw-gap-1" data-testid="showcase-kbd-other"><Kbd platform="other">Meta</Kbd><Kbd platform="other">K</Kbd></span>
            <span className="tw-flex tw-items-center tw-gap-1"><Kbd>Control</Kbd><Kbd>Shift</Kbd><Kbd>P</Kbd></span>
            <span className="tw-flex tw-items-center tw-gap-1"><Kbd platform="mac">Alt</Kbd><Kbd platform="mac">ArrowUp</Kbd></span>
            <span className="tw-flex tw-items-center tw-gap-1"><Kbd>Escape</Kbd></span>
            <span className="tw-flex tw-items-center tw-gap-1 tw-text-body-primary tw-text-ink-secondary">
              Press <Kbd>/</Kbd> to search
            </span>
          </div>
        </section>

        {/* ── Breadcrumbs ─────────────────────────────────────────────────────
            § 52 — extracted from the shape Products screens hand-roll. Named nav
            landmark; the last item is the current page and links nowhere.
        */}
        <section id="breadcrumbs" data-testid="showcase-section-breadcrumbs" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Breadcrumbs
          </Typography>
          <DocBlock section="breadcrumbs" />
          <Divider className="tw-mb-6" />

          <div>
            <h4 className="tw-text-title-header tw-text-ink-tertiary tw-mb-4">
              A category path — the last item is the page you are on
            </h4>
            <Breadcrumbs
              items={[
                { label: 'Categories', href: '#', 'data-testid': 'showcase-bc-root' },
                { label: 'Bearings', href: '#', 'data-testid': 'showcase-bc-mid' },
                /* § 69: neither href nor onClick — a grouping level with no page. */
                { label: 'Radial (grouping)', 'data-testid': 'showcase-bc-waypoint' },
                { label: 'Deep groove ball bearing 6204-2RS', 'data-testid': 'showcase-bc-current' },
              ]}
              data-testid="showcase-breadcrumbs"
            />
            <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-4 tw-block">
              A named nav landmark. Ancestors are real links, so middle-click and copy-link
              work; the current page is a plain span with aria-current — a page does not link
              to itself. An item with neither href nor onClick is a non-navigable waypoint:
              rendered as a span, never a focusable no-op. Long labels truncate with
              the full text on hover.
            </Typography>
          </div>
        </section>

        {/* § 373 — TreeView: the ARIA APG tree, for category hierarchies. */}
        <section id="tree-view" data-testid="showcase-section-tree-view" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Tree view
          </Typography>
          <DocBlock section="tree-view" />
          <Divider className="tw-mb-6" />
          <TreeViewDemo />
        </section>

        {/* Link (§ 213) */}
        <section id="link" data-testid="showcase-section-link" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Link
          </Typography>
          <DocBlock section="link" />
          <Divider className="tw-mb-6" />
          <div className="tw-space-y-4 tw-max-w-[68ch]">
            <Typography variant="body-primary" as="p" className="tw-text-ink-secondary">
              Inline, mid-sentence: review the{' '}
              <Link href="#colour" data-testid="showcase-link-inline">colour foundations</Link>
              {' '}before inventing a hex.
            </Typography>
            <Typography variant="body-primary" as="p" className="tw-text-ink-secondary">
              External, announced: the{' '}
              <Link href="https://www.w3.org/WAI/ARIA/apg/patterns/combobox/" external data-testid="showcase-link-external">
                ARIA combobox pattern
              </Link>
              {' '}this system&rsquo;s Combobox follows.
            </Typography>
          </div>
        </section>

        {/* ── Pagination ──────────────────────────────────────────────────────
            Replaces Common/Paginator (react-paginate + off-palette hexes inside
            inline SVGs + data slicing inside a presentation component). First and
            last page always reachable; constant slot count; aria-current="page".
        */}
        <section id="pagination" data-testid="showcase-section-pagination" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Pagination
          </Typography>
          <DocBlock section="pagination" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8">
            <div>
              <h4 className="tw-text-title-header tw-text-ink-tertiary tw-mb-4">
                Interactive — 312 items, 25 per page
              </h4>
              <Pagination
                page={demoPage}
                totalItems={312}
                pageSize={25}
                onPageChange={setDemoPage}
                data-testid="showcase-pagination"
              />
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-4 tw-block">
                The slot count never changes as you move — Previous/Next stay put under the
                pointer. Page 1 and the last page are always one click away. The current page
                is announced as &ldquo;current page&rdquo; and the whole row is a nav landmark.
              </Typography>
            </div>

            <div>
              <h4 className="tw-text-title-header tw-text-ink-tertiary tw-mb-4">
                size=&quot;sm&quot; — for dense table footers
              </h4>
              <Pagination
                page={2}
                totalItems={97}
                pageSize={10}
                onPageChange={() => {}}
                size="sm"
                data-testid="showcase-pagination-sm"
              />
            </div>

            <div>
              <h4 className="tw-text-title-header tw-text-ink-tertiary tw-mb-4">
                One page — the summary still informs, the buttons do not appear
              </h4>
              <Pagination
                page={1}
                totalItems={8}
                pageSize={25}
                onPageChange={() => {}}
                data-testid="showcase-pagination-single"
              />
            </div>

            <div>
              <h4 className="tw-text-title-header tw-text-ink-tertiary tw-mb-4">
                Rows per page &mdash; the control the footers were composing by hand
              </h4>
              <Pagination
                page={pageSizePage}
                totalItems={312}
                pageSize={pageSizeDemo}
                onPageChange={setPageSizePage}
                pageSizeOptions={[10, 25, 50, 100]}
                onPageSizeChange={(next) => {
                  setPageSizeDemo(next);
                  setPageSizePage(1);
                }}
                data-testid="showcase-pagination-pagesize"
              />
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-4 tw-block">
                Changing the size resets to page 1 in the handler &mdash; row 26 of 25-per-page is
                row 1 of 50-per-page.
              </Typography>
            </div>
          </div>
        </section>

        <GroupHeading
          id="data"
          label="Data display"
          blurb="Presenting records the user reads rather than edits."
          first={false}
        />

        {/* Table Section */}
        <section id="table" data-testid="showcase-section-table" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Table
          </Typography>
          <DocBlock section="table" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8">
            {/* Basic Table */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Basic Table with Mixed Cell Types
              </Typography>
              <Table
                columns={tableColumns}
                data={tableData}
                rowKey="id"
                sortState={tableSortState}
                onSort={handleTableSort}
                hoverable
                data-testid="showcase-table"
              />
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-3">
                Sortable columns are real buttons: Tab to one and press Enter. The header cell
                carries <code>aria-sort</code>. Selectors are{' '}
                <code>showcase-table-header-&lt;columnId&gt;</code> and{' '}
                <code>showcase-table-sort-&lt;columnId&gt;</code>.
              </Typography>
            </div>

            <Divider />

            {/* § 299 — row selection + the SelectionBar. Adopted from the Open decisions
                mock: controlled keys in, the whole next array out; the bar carries the
                count and the bulk actions and floats at the viewport edge while the table
                is above the fold. */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Row selection and the selection bar
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                Tick a row: it takes the selection tint under full ink and drops its hover, the
                header box selects the page (indeterminate on a partial set), and a bar rises with
                the count and the bulk actions. The table holds no selection state &mdash;{' '}
                <code>selectedKeys</code> in, <code>onSelectionChange</code> out, like sort and
                expansion. Clicking a box never fires <code>onRowClick</code>. The rows are one Tab stop:
                arrow keys move between them and Enter opens one.
              </Typography>
              <Table
                columns={tableColumns.slice(0, 4)}
                data={tableData}
                rowKey="id"
                hoverable
                selectable
                selectedKeys={selectedRowKeys}
                onSelectionChange={setSelectedRowKeys}
                selectionLabel={(row) => `Select ${row.name}`}
                onRowClick={(row) => toast.info(`Opened ${row.name}`)}
                data-testid="showcase-table-selectable"
              />
              {selectedRowKeys.length > 0 && (
                <SelectionBar
                  count={selectedRowKeys.length}
                  onClear={() => setSelectedRowKeys([])}
                  className="tw-mt-4"
                  data-testid="showcase-selection-bar"
                >
                  <Button variant="subtle" size="sm" onClick={() => toast.info(`Exporting ${selectedRowKeys.length} rows`)} data-testid="showcase-selection-bar-export">Export</Button>
                  <Button variant="subtle" size="sm" onClick={() => toast.info(`Recategorizing ${selectedRowKeys.length} rows`)} data-testid="showcase-selection-bar-recategorize">Recategorize</Button>
                  <Button variant="danger" size="sm" onClick={() => toast.warning(`${selectedRowKeys.length} rows would be deleted`)} data-testid="showcase-selection-bar-delete">Delete</Button>
                </SelectionBar>
              )}
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-3 tw-block">
                Selectors: <code>showcase-table-selectable-select-all</code>,{' '}
                <code>showcase-table-selectable-row-&lt;id&gt;-select</code>; a selected row carries{' '}
                <code>data-selected=&quot;true&quot;</code>. The bar is <code>showcase-selection-bar</code>{' '}
                with <code>-count</code> and <code>-clear</code>; Left/Right walk its actions.
              </Typography>
            </div>

            <Divider />

            {/* § 299 — the bar on its own, in place, so its anatomy can be read without
                ticking anything. */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                SelectionBar &mdash; the anatomy, in place
              </Typography>
              <SelectionBar
                count={3}
                placement="static"
                onClear={() => toast.info('Selection cleared')}
                data-testid="showcase-selection-bar-static"
              >
                <Button variant="subtle" size="sm" onClick={() => toast.info('Exporting 3 rows')} data-testid="showcase-selection-bar-static-export">Export</Button>
                <Button variant="subtle" size="sm" onClick={() => toast.info('Recategorizing 3 rows')} data-testid="showcase-selection-bar-static-recategorize">Recategorize</Button>
                <Button variant="danger" size="sm" onClick={() => toast.warning('3 rows would be deleted')} data-testid="showcase-selection-bar-static-delete">Delete</Button>
              </SelectionBar>
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-3 tw-block">
                Count as the selected Badge, a rule, the actions as ordinary Buttons, and the way
                out. <code>placement=&quot;sticky&quot;</code> (the default, above) floats it at the
                viewport&rsquo;s bottom edge inside the page flow &mdash; no layer rung, always under a
                dialog. Mount it only while something is selected.
              </Typography>
            </div>

            <Divider />

            {/* Striped Table */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Striped Table
              </Typography>
              <Table
                columns={tableColumns.slice(0, 4)}
                data={tableData}
                rowKey="id"
                striped
                data-testid="showcase-table-striped"
              />
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-3 tw-block">
                A striped table keeps the dark head band by default: the stripe is the
                sunken ground, so the light head would land on the stripe&rsquo;s own colour and read
                as one more row. Plain tables take the light head; an explicit{' '}
                <code>headerVariant</code> always wins.
              </Typography>
            </div>

            <Divider />

            {/* § 245 — the head steps back; density arrives */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Dark header band — opt-in
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                The sunken <code>light</code> head is the default now: a header labels the data and
                sits behind it, the way a field label sits behind its value. <code>dark</code> stays
                for a head that must read as a band — a nested grid inside an already-sunken region.
              </Typography>
              <Table
                columns={tableColumns.slice(0, 4)}
                data={tableData.slice(0, 3)}
                rowKey="id"
                headerVariant="dark"
                hoverable
                data-testid="showcase-table-dark-header"
              />
            </div>

            <Divider />

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Density
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                <code>compact</code> is 32px rows for a review grid; <code>comfortable</code> is 44px
                for a short list that is read more than scanned. The default stays 36 — the control
                grid&rsquo;s md. Type never changes with density: size is density, the token is fixed.
              </Typography>
              <div className="tw-grid tw-gap-6 lg:tw-grid-cols-2">
                <div>
                  <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-2">
                    density=&quot;compact&quot;
                  </Typography>
                  <Table
                    columns={tableColumns.slice(0, 3)}
                    data={tableData.slice(0, 4)}
                    rowKey="id"
                    density="compact"
                    data-testid="showcase-table-compact"
                  />
                </div>
                <div>
                  <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-2">
                    density=&quot;comfortable&quot;
                  </Typography>
                  <Table
                    columns={tableColumns.slice(0, 3)}
                    data={tableData.slice(0, 4)}
                    rowKey="id"
                    density="comfortable"
                    data-testid="showcase-table-comfortable"
                  />
                </div>
              </div>
            </div>

            <Divider />

            {/* Table with Footer */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Table with Footer
              </Typography>
              <Table
                columns={tableColumns}
                data={tableData}
                rowKey="id"
                footer={
                  <div className="tw-flex tw-justify-between tw-items-center">
                    <span className="tw-text-ink-secondary">Total: 5 items</span>
                    <span className="tw-font-semibold">
                      Total Revenue: ${tableData.reduce((sum, row) => sum + row.revenue, 0).toLocaleString()}
                    </span>
                  </div>
                }
              />
            </div>

            <Divider />

            {/* Empty State */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Empty State
              </Typography>
              <Table
                columns={tableColumns.slice(0, 4)}
                data={[]}
                rowKey="id"
                emptyContent={
                  <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                    <Typography variant="body-primary" className="tw-text-ink-tertiary">
                      No data available
                    </Typography>
                    <Button variant="secondary">Add New Item</Button>
                  </div>
                }
              />
            </div>

            <Divider />

            {/* Meta cells (§ 66): low-emphasis columns — dates, counts nobody acts on. */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Meta cells
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                cellType=&quot;meta&quot; renders ink-secondary with tabular numerals, so date
                columns align and stop shouting as loudly as the names beside them.
              </Typography>
              <Table
                columns={[
                  { id: 'name', header: 'Product', accessor: 'name' },
                  { id: 'created', header: 'Created', accessor: 'created', cellType: 'meta' },
                ]}
                data={[
                  { id: 1, name: 'MOCA_07', created: 'Apr-28-2026' },
                  { id: 2, name: 'MN_PROD_09', created: 'May-07-2026' },
                ]}
                rowKey="id"
                data-testid="showcase-meta-table"
              />
            </div>

            <Divider />

            {/* Expandable rows (§ 120) */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Expandable rows
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                Supplying renderExpandedRow adds the expand column — there is no
                &quot;expandable&quot; boolean, so a table cannot declare expansion and render
                nothing. Expansion is controlled: the caller owns the open set, because the
                common case fetches the children on first open. A row with nothing to open gets
                an empty cell rather than a dead chevron. Click the second row to see the busy
                state: while a lazy fetch is in flight the chevron itself becomes a disabled
                spinner, since the expanded row does not exist yet.
              </Typography>
              <Table
                columns={[
                  { id: 'name', header: 'Channel Name', accessor: 'name' },
                  { id: 'count', header: 'No. of Environment', accessor: 'count', cellType: 'numerical' },
                ]}
                data={[
                  { id: 'ch-1', name: 'ACME Channel', count: 2 },
                  { id: 'ch-2', name: 'Lazy Channel', count: 3 },
                  { id: 'ch-3', name: 'acme_test_channel', count: 0 },
                ]}
                rowKey="id"
                /* Only a channel WITH environments has anything to open. */
                isRowExpandable={(row: any) => row.count > 0}
                expandedRowKeys={showcaseExpandedRows}
                isRowExpandLoading={(row: any) => showcaseExpandLoadingRow === row.id}
                expandLabel={(row: any, expanded: boolean) =>
                  `${expanded ? 'Hide' : 'Show'} environments for ${row.name}`
                }
                onExpandChange={(rowKey, expanded) => {
                  if (!expanded) {
                    setShowcaseExpandedRows(prev => prev.filter(key => key !== rowKey));
                    return;
                  }
                  /* `ch-2` stands in for the real lazy path: show the wait on the chevron,
                     then open the row once the "request" resolves. */
                  if (rowKey === 'ch-2') {
                    setShowcaseExpandLoadingRow(rowKey);
                    window.setTimeout(() => {
                      setShowcaseExpandLoadingRow(null);
                      setShowcaseExpandedRows(prev => [...prev, rowKey]);
                    }, 900);
                    return;
                  }
                  setShowcaseExpandedRows(prev => [...prev, rowKey]);
                }}
                renderExpandedRow={(row: any) => (
                  <div className="tw-p-4">
                    <Typography variant="sub-title-label" as="div" className="tw-text-ink-tertiary tw-mb-2">
                      Added Environment
                    </Typography>
                    <Typography variant="body-primary" className="tw-text-ink-secondary">
                      {row.count} environment{row.count === 1 ? '' : 's'} under {row.name}. The
                      expanded row spans the whole grid, so it can hold anything — including a
                      nested Table.
                    </Typography>
                  </div>
                )}
                data-testid="showcase-expandable-table"
              />
            </div>

            <Divider />

            {/* Loading State */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Loading State
              </Typography>
              <Table
                columns={tableColumns.slice(0, 4)}
                data={tableData.slice(0, 2)}
                rowKey="id"
                loading
              />
            </div>
          </div>
        </section>

        {/* Card — the first consumer of ds-sm, which had none */}
        <section id="card" data-testid="showcase-section-card" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Card
          </Typography>
          <DocBlock section="card" />
          <Divider className="tw-mb-6" />

          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-6">
            <Card
              title="Body only"
              description="No footer, so no band."
              data-testid="showcase-card-plain"
            >
              <Typography variant="body-primary" className="tw-text-ink-secondary">
                A card with a header and a body.
              </Typography>
            </Card>

            <Card
              title="With a footer"
              description="The band reaches the card's edges."
              footer={<Button variant="primary" onClick={() => toast.success('Saved')}>Save</Button>}
              data-testid="showcase-card-footer"
            >
              <Typography variant="body-primary" className="tw-text-ink-secondary">
                Actions live in the band, right-aligned.
              </Typography>
            </Card>

            <Card elevation="none" title="elevation=&quot;none&quot;" data-testid="showcase-card-flat">
              <Typography variant="body-primary" className="tw-text-ink-secondary">
                Border only — for a card inside an already-raised surface, where a second shadow
                reads as a mistake.
              </Typography>
            </Card>

            <Card
              interactive
              title="interactive"
              description="Hover lifts it: the shadow steps sm to md, the card rises 1px, the edge firms."
              data-testid="showcase-card-interactive"
            >
              <Typography variant="body-primary" className="tw-text-ink-secondary">
                For cards the user can act on. Visual only — the click semantics belong to
                whatever the caller puts inside. A card at md stays put: ds-lg is overlay-only.
              </Typography>
            </Card>

            <Card
              title="Spring catalogue"
              description="1,204 products, updated today"
              action={{
                href: '#cards',
                onClick: (event) => {
                  event.preventDefault();
                  toast.success('Opened Spring catalogue');
                },
              }}
              footer={<Button variant="secondary" onClick={() => toast.success('Duplicated')}>Duplicate</Button>}
              data-testid="showcase-card-action"
            >
              <Typography variant="body-primary" className="tw-text-ink-secondary">
                action: the whole card is one target, named by its title — a real link, one
                Tab stop, and the focus ring outlines the card. The footer&rsquo;s button stays its own.
              </Typography>
            </Card>
          </div>

          <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-4 tw-block">
            Elevation is <strong>sm</strong> by default. <strong>md</strong> is for a card that
            genuinely floats — a dragged item, a pinned summary. The overlay tier
            (<span className="tw-text-code-formula">ds-lg</span>) belongs to Dialog, SidePanel and
            Toast; a card claiming it competes with the modal layer. The title renders as a real
            heading (<span className="tw-text-code-formula">h3</span> by default) because
            screen-reader users navigate by heading.
          </Typography>

          <Divider className="tw-my-6" />

          <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-2">
            The card asks how wide IT is, not how wide the window is
          </Typography>
          <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-4">
            Drag the handle in the bottom-right corner. Below 276px the footer actions stack and
            stretch; above it they return to a right-aligned row. The browser window never changes,
            so a viewport breakpoint could not do this.
          </Typography>
          <div
            /* `resize` needs a non-visible overflow to take effect. The wrapper is the
               thing being resized; the Card inside it is the container that reacts. */
            className="tw-resize-x tw-overflow-auto tw-max-w-full tw-w-[420px] tw-min-w-[200px] tw-p-1 tw-border tw-border-dashed tw-border-edge-strong tw-rounded-srf"
            data-testid="showcase-card-container-demo"
          >
            <Card
              title="Nightly import"
              description="Runs at 02:00 UTC."
              footer={(
                <>
                  <Button variant="secondary" size="sm" data-testid="showcase-card-cq-cancel">Cancel</Button>
                  <Button size="sm" data-testid="showcase-card-cq-save">Save changes</Button>
                </>
              )}
              data-testid="showcase-card-cq"
            >
              <Typography variant="body-primary" className="tw-text-ink-secondary">
                The footer below is the part that reflows.
              </Typography>
            </Card>
          </div>

        </section>

        {/* Accordion (§ 68) — the DS disclosure, from the Filter panel's attribute sections. */}
        <section id="accordion" data-testid="showcase-section-accordion" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Accordion
          </Typography>
          <DocBlock section="accordion" />
          <Divider className="tw-mb-6" />
          <div className="tw-max-w-md tw-border-b tw-border-edge">
            <Accordion title="brand" defaultOpen data-testid="showcase-accordion-brand">
              <div className="tw-flex tw-flex-wrap tw-gap-2 tw-pb-3">
                <ChoiceChip label="5.11" selected={false} data-testid="showcase-accordion-chip" />
                <ChoiceChip label="ADO" selected />
                <ChoiceChip label="Aeratis" selected={false} />
              </div>
            </Accordion>
            <Accordion title="color1" data-testid="showcase-accordion-color">
              <Typography variant="body-primary" className="tw-pb-3 tw-text-ink-secondary">
                Expanded on demand.
              </Typography>
            </Accordion>
            {/* § 192: header-row actions — siblings of the disclosure, so clicking
                one never toggles the section. The category accordions' shape. */}
            <Accordion
              title="Associated Attributes"
              data-testid="showcase-accordion-actions"
              actions={
                <Button
                  variant="subtle"
                  size="sm"
                  data-testid="showcase-accordion-actions-edit"
                  onClick={() => toast.info('Edit clicked — the section did not toggle')}
                >
                  Edit
                </Button>
              }
            >
              <Typography variant="body-primary" className="tw-pb-3 tw-text-ink-secondary">
                The Edit above sits in the header row but outside the disclosure button.
              </Typography>
            </Accordion>
          </div>
          {/* § 341: `boxed` — a standalone section, the Attribute and Category editors'
              shape, which 15 call sites used to draw with className. § 405: with
              `headingLevel` — this section's title is an h2, so its sections are h3s; the row
              renders exactly as the ones above, and a screen reader can jump to it by heading. */}
          <div className="tw-max-w-md tw-mt-6">
            <Accordion title="Attribute values" variant="boxed" headingLevel={3} data-testid="showcase-accordion-boxed">
              <Typography variant="body-primary" className="tw-pb-3 tw-text-ink-secondary">
                A standalone section: its own border, radius, ground and inset.
              </Typography>
            </Accordion>
          </div>
        </section>

        {/* Divider Section */}
        <section id="divider" data-testid="showcase-section-divider" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Divider
          </Typography>
          <DocBlock section="divider" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8">
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Horizontal Solid
              </Typography>
              <Divider orientation="horizontal" variant="solid" />
            </div>

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Horizontal Dashed
              </Typography>
              <Divider orientation="horizontal" variant="dashed" />
            </div>

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Vertical Solid
              </Typography>
              <div className="tw-h-24 tw-flex tw-items-center">
                <Divider orientation="vertical" variant="solid" />
              </div>
            </div>
          </div>
        </section>

        {/* ── Tag ─────────────────────────────────────────────────────────────
            § 52 — the removable value chip. A tag is a SELECTED value, so it wears
            the § 49 selection grammar: blue-100 ground, accent ink.
        */}
        <section id="tag" data-testid="showcase-section-tag" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Tag &amp; choice chip
          </Typography>
          <DocBlock section="tag" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8">
            <div>
              <h4 className="tw-text-title-header tw-text-ink-tertiary tw-mb-4">
                Removable — selected attribute values
              </h4>
              <div className="tw-flex tw-flex-wrap tw-gap-2 tw-items-center">
                {demoTags.map((t) => (
                  <Tag
                    key={t}
                    label={t}
                    onRemove={() => setDemoTags((prev) => prev.filter((x) => x !== t))}
                    data-testid={`showcase-tag-${t.toLowerCase().replace(/\s+/g, '-')}`}
                  />
                ))}
                {demoTags.length === 0 && (
                  <Button
                    variant="secondary"
                    onClick={() => setDemoTags(['Voltage', 'Amperage', 'Phase', 'Frame material'])}
                    data-testid="showcase-tag-reset"
                  >
                    Reset tags
                  </Button>
                )}
              </div>
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-4 tw-block">
                Remove is a named 24px target (&ldquo;Remove Voltage&rdquo;) whose glyph turns
                error-red on hover — removal is destructive and is named as one.
              </Typography>
            </div>

            <div>
              <h4 className="tw-text-title-header tw-text-ink-tertiary tw-mb-4">
                Read-only and disabled
              </h4>
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                <Tag label="Read-only — no onRemove, no button" data-testid="showcase-tag-readonly" />
                <Tag label="Disabled" onRemove={() => {}} disabled data-testid="showcase-tag-disabled" />
              </div>
            </div>

            {/* § 203, exhibited 2026-09-08 — it shipped straight to the Performance
                chips without a store demo, which is how those chips later drifted onto
                the selection token unnoticed. A prop the store never shows is a prop
                nobody can question (§ 184's cousin). */}
            <div>
              <h4 className="tw-text-title-header tw-text-ink-tertiary tw-mb-4">
                Outlined — the bordered 4px chip
              </h4>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                Outlined swaps the pill for a 4px rectangle bordered in the chip&apos;s own ink
                (border-current), so every variant and tone borders itself with no second colour
                table. The first pair is the Performance filter-chip recipe — outlined plus
                tone=&ldquo;info&rdquo;, the pinned chip blue, never the selection tint.
              </Typography>
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                <Tag label="Compatio" outlined tone="info" onRemove={() => {}} data-testid="showcase-tag-outlined-info" />
                <Tag label="SmartBuilder" outlined tone="info" onRemove={() => {}} />
                <Tag label="ANODIZED-BLACK" outlined variant="meta" data-testid="showcase-tag-outlined-meta" />
                <Tag label="removed" outlined tone="danger" showToneGlyph stateLabel="removed" data-testid="showcase-tag-outlined-danger" />
                <Tag label="Disabled" outlined disabled onRemove={() => {}} data-testid="showcase-tag-outlined-disabled" />
              </div>
            </div>

            {/* § 66: the quiet chip for values that describe, not values the user chose. */}
            <div>
              <h4 className="tw-text-title-header tw-text-ink-tertiary tw-mb-4">
                Meta — metadata values
              </h4>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                The selection tint claims a choice; an attribute value in a table cell was never
                chosen. Meta wears ink-secondary on the sunken ground (measured 6.57:1 light,
                7.08:1 dark) and keeps the truncation-with-title contract.
              </Typography>
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                <Tag label="BLACK/GRAY" variant="meta" data-testid="showcase-tag-meta" />
                <Tag label="Individual" variant="meta" />
                <Tag label="ANTIQUE_BRONZE/ANODIZED-BLACK" variant="meta" data-testid="showcase-tag-meta-truncated" />
              </div>
            </div>

            {/* § 67: the third chip grammar — the CHOOSING control. */}
            <div>
              <h4 className="tw-text-title-header tw-text-ink-tertiary tw-mb-4">
                ChoiceChip — the choosing control
              </h4>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                Tag displays a choice already made; ChoiceChip IS the choosing — a real toggle
                button with aria-pressed, from the Filter panel&apos;s value cloud. Unselected is
                the quiet pair; selected is the one selection grammar plus an inner outline, so
                the choice reads without its colour. The outline takes no room, so a toggle
                never nudges its neighbours.
              </Typography>
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                {[
                  { id: 'acorn', label: 'ACORN' },
                  { id: 'bark', label: 'BARK' },
                  { id: 'black-gray', label: 'BLACK/GRAY' },
                  { id: 'anodized', label: 'ANODIZED-BLACK' },
                ].map((value) => (
                  <ChoiceChip
                    key={value.id}
                    label={value.label}
                    selected={chosenValues.includes(value.id)}
                    onClick={() => toggleChosen(value.id)}
                    data-testid={`showcase-choice-chip-${value.id}`}
                  />
                ))}
                <ChoiceChip label="Disabled" selected={false} disabled data-testid="showcase-choice-chip-disabled" />
                {/* § 193: a resting chip whose choice state is a destructive pending
                    change — the category editors' pending-removal marker. */}
                <ChoiceChip
                  label="Pending removal"
                  selected={false}
                  tone="danger"
                  data-testid="showcase-choice-chip-danger"
                />
              </div>
            </div>

            {/* § 225: the stacked case the split exists for. */}
            <div>
              <h4 className="tw-text-title-header tw-text-ink-tertiary tw-mb-4">
                Chips on a selection ground
              </h4>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                A blue chip sitting on an accent-tint selected row — the Products delta-table
                combination. Light split the two grounds long before dark did; dark stayed on ONE
                value (#1e3844) until this exact strip measured 1.00:1 — no pill
                at all. Judge in DARK: the chip should keep a visible pill (1.16:1 plus the
                hue step — selection is desaturated steel, the chip royal navy).
              </Typography>
              <div
                data-testid="showcase-chip-on-selection"
                className="tw-inline-flex tw-items-center tw-gap-3 tw-rounded-ctl tw-bg-accent-tint tw-px-4 tw-py-3"
              >
                <Typography variant="body-primary" className="tw-text-ink">
                  Selected row
                </Typography>
                <Tag label="Aluminum" tone="info" data-testid="showcase-chip-on-selection-tag" />
                <Tag label="+3 more" tone="info" data-testid="showcase-chip-on-selection-more" />
                <Status tone="info" label="Generating" data-testid="showcase-chip-on-selection-status" />
              </div>
            </div>

          </div>
        </section>

        {/* Avatar Section */}
        <section id="avatar" data-testid="showcase-section-avatar" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Avatar
          </Typography>
          <DocBlock section="avatar" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8">
            <div>
              <h4 className="tw-text-title-header tw-text-ink-tertiary tw-mb-4">
                Sizes — one rung below the control ladder
              </h4>
              <div className="tw-flex tw-items-end tw-gap-6">
                {(['sm', 'md', 'lg'] as const).map((size) => (
                  <div key={size} className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                    <Avatar name="Jane Doe" size={size} />
                    <Typography variant="caption-metadata" className="tw-text-ink-tertiary">
                      {size} · {{ sm: 24, md: 32, lg: 40 }[size]}px
                    </Typography>
                  </div>
                ))}
              </div>
              <Typography variant="caption-metadata" className="tw-mt-3 tw-block tw-text-ink-tertiary">
                Not <code>ControlSize</code>. 28/36/44 is the height of a control; an avatar
                is a mark that sits inside one, so 32 centred in a 36px button is the header
                arrangement.
              </Typography>
            </div>

            <div>
              <h4 className="tw-text-title-header tw-text-ink-tertiary tw-mb-4">
                Tones — palette roles, so they follow the theme
              </h4>
              <div className="tw-flex tw-items-center tw-gap-6">
                {(['accent', 'neutral', 'info', 'success', 'purple'] as const).map((tone) => (
                  <div key={tone} className="tw-flex tw-items-center tw-gap-2">
                    <Avatar name="Jane Doe" tone={tone} data-testid={`showcase-avatar-tone-${tone}`} />
                    <Typography variant="caption-metadata" className="tw-text-ink-tertiary">
                      {tone}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>

            {/* § 297 — `auto`: the name picks one of four calm tones, deterministically, so a
                row of people is not a row of identical discs. The default stays `accent`
                for the one avatar in the header. */}
            <div>
              <h4 className="tw-text-title-header tw-text-ink-tertiary tw-mb-4">
                Auto &mdash; the name picks the tone, for a list of people
              </h4>
              <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                {['Zakir Husain', 'Jane Doe', 'Ana Maria Perez', 'Wei Chen', 'Priya Nair', 'Tom Okafor'].map((n, i) => (
                  <div key={n} className="tw-flex tw-items-center tw-gap-2">
                    <Avatar name={n} tone="auto" data-testid={`showcase-avatar-auto-${i}`} />
                    <Typography variant="caption-metadata" className="tw-text-ink-tertiary">
                      {n}
                    </Typography>
                  </div>
                ))}
              </div>
              <Typography variant="caption-metadata" className="tw-mt-3 tw-block tw-text-ink-tertiary">
                Hashed from the name, so the same person is the same colour everywhere. Never
                warning or danger &mdash; those tones mean something.
              </Typography>
            </div>

            <div>
              <h4 className="tw-text-title-header tw-text-ink-tertiary tw-mb-4">
                Initials — first letter of the first two words
              </h4>
              <div className="tw-flex tw-items-center tw-gap-6">
                {['Zakir', 'Jane Doe', 'Ana Maria Perez', ''].map((n, i) => (
                  <div key={i} className="tw-flex tw-items-center tw-gap-2">
                    <Avatar name={n} />
                    <Typography variant="caption-metadata" className="tw-text-ink-tertiary">
                      {n === '' ? 'no name yet' : n}
                    </Typography>
                  </div>
                ))}
              </div>
              <Typography variant="caption-metadata" className="tw-mt-3 tw-block tw-text-ink-tertiary">
                An empty name renders <code>?</code> rather than an empty circle — the profile
                can resolve after the session does, and a blank ring reads as a loading bug.
              </Typography>
            </div>

            <div>
              <h4 className="tw-text-title-header tw-text-ink-tertiary tw-mb-4">
                Picture, with the initials as the fallback
              </h4>
              <div className="tw-flex tw-items-center tw-gap-6">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <Avatar name="Jane Doe" src={AVATAR_SAMPLE} size="lg" data-testid="showcase-avatar-image" />
                  <Typography variant="caption-metadata" className="tw-text-ink-tertiary">
                    src loads
                  </Typography>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                  <Avatar name="Jane Doe" src="/img/this-file-does-not-exist.png" size="lg" data-testid="showcase-avatar-broken" />
                  <Typography variant="caption-metadata" className="tw-text-ink-tertiary">
                    src fails → initials, never a broken-image glyph
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Data expression — the layer that renders a QUANTITY rather than a state.
            Every mark here paints through a dataviz role, so all four follow the theme
            with no second token and no observer. */}
        <section id="data-expression" data-testid="showcase-section-data-expression" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Data expression
          </Typography>
          <DocBlock section="data-expression" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8">
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Stat &mdash; the figure, its name and its comparison
              </Typography>
              <div className="tw-flex tw-flex-wrap tw-gap-8">
                <Stat
                  label="Active guides"
                  value="1,284"
                  caption={<Delta value={6.2} label="Active guides" data-testid="showcase-stat-guides-delta" />}
                  data-testid="showcase-stat-guides"
                />
                <Stat
                  label="Promotion success"
                  value="97.4%"
                  caption={<Delta value={0.8} format="point" label="Promotion success" />}
                  data-testid="showcase-stat-promotion"
                />
                <Stat
                  label="Median build time"
                  value="4.1s"
                  caption={
                    <Delta
                      value={0.6}
                      sentiment="up-is-bad"
                      format="absolute"
                      unit="s"
                      label="Median build time"
                      data-testid="showcase-stat-build-delta"
                    />
                  }
                  data-testid="showcase-stat-build"
                />
                <Stat
                  label="Impressions"
                  value="18,402"
                  trend={<Sparkline values={[12, 15, 13, 19, 18, 24, 27]} decorative />}
                  caption="Last 7 days"
                  data-testid="showcase-stat-impressions"
                />
              </div>
              <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mt-3">
                Example figures. Build time rises as bad news &mdash; the same arithmetic as the
                rise beside it, and the opposite verdict.
              </Typography>
            </div>

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Delta &mdash; a rise is not always good news
              </Typography>
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                <Delta value={6.2} label="Conversions" data-testid="showcase-delta-good" />
                <Delta value={-2.5} label="Conversions" data-testid="showcase-delta-fall" />
                <Delta value={0.6} sentiment="up-is-bad" format="absolute" unit="s" label="Latency" data-testid="showcase-delta-bad" />
                <Delta value={12} sentiment="neutral" format="absolute" label="Headcount" data-testid="showcase-delta-neutral" />
                <Delta value={0} label="Unchanged" data-testid="showcase-delta-zero" />
                <Delta value={4.4} quiet label="Quiet, for a column" data-testid="showcase-delta-quiet" />
              </div>
            </div>

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Sparkline &mdash; the six series, and the data that breaks one
              </Typography>
              <div className="tw-flex tw-flex-wrap tw-gap-4 tw-items-center">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <Sparkline
                    key={i}
                    series={i}
                    values={[4, 7, 5, 11, 9, 14, 12 + i]}
                    decorative
                    data-testid={`showcase-sparkline-series-${i}`}
                  />
                ))}
              </div>
              <div className="tw-flex tw-flex-wrap tw-gap-6 tw-items-center tw-mt-4">
                <div className="tw-flex tw-flex-col tw-gap-1">
                  <Sparkline values={[4, 7, 5, 11, 9, 14, 17]} area decorative data-testid="showcase-sparkline-area" />
                  <Typography variant="caption-metadata" className="tw-text-ink-tertiary">area</Typography>
                </div>
                <div className="tw-flex tw-flex-col tw-gap-1">
                  <Sparkline values={[7, 7, 7, 7]} decorative data-testid="showcase-sparkline-flat" />
                  <Typography variant="caption-metadata" className="tw-text-ink-tertiary">every value equal</Typography>
                </div>
                <div className="tw-flex tw-flex-col tw-gap-1">
                  <Sparkline values={[42]} decorative data-testid="showcase-sparkline-single" />
                  <Typography variant="caption-metadata" className="tw-text-ink-tertiary">one reading</Typography>
                </div>
                <div className="tw-flex tw-flex-col tw-gap-1">
                  <Sparkline values={[]} decorative data-testid="showcase-sparkline-empty" />
                  <Typography variant="caption-metadata" className="tw-text-ink-tertiary">no data &mdash; keeps its slot</Typography>
                </div>
              </div>
            </div>

            <div className="tw-max-w-xl">
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                DistributionBar &mdash; how a whole divides
              </Typography>
              <div className="tw-space-y-6">
                <DistributionBar
                  label="Catalogue by category"
                  segments={[
                    { label: 'Fasteners', value: 4820 },
                    { label: 'Fittings', value: 2910 },
                    { label: 'Bearings', value: 1460 },
                  ]}
                  total={12000}
                  showLegend
                  data-testid="showcase-distribution-rest"
                />
                <DistributionBar
                  label="Row sync"
                  size="sm"
                  segments={[
                    { label: 'Synced', value: 78 },
                    { label: 'Pending', value: 22 },
                  ]}
                  data-testid="showcase-distribution-inline"
                />
              </div>
              <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mt-3">
                Example figures. The first bar names a total the segments do not reach, so the
                shortfall renders as a quiet rest slice rather than rescaling the shares.
              </Typography>
            </div>
          </div>
        </section>


        <GroupHeading
          id="feedback"
          label="Status & feedback"
          blurb="Telling the user what happened, what is happening, and what is missing."
          first={false}
        />

        {/* Status Section */}
        <section id="status" data-testid="showcase-section-status" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Status
          </Typography>
          <DocBlock section="status" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8">
            {/*
              Organised by TONE, not by domain state. The six-name domain enum
              (`generated` / `notPromoted` / …) is deprecated but still works, and is
              demonstrated further down so the migration path is visible on the page
              rather than only in a docblock.
            */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Tones
              </Typography>
              <div className="tw-flex tw-gap-6 tw-flex-wrap tw-items-start">
                {STATUS_TONES.map(({ tone, label }) => (
                  <div key={tone} className="tw-flex tw-flex-col tw-gap-2">
                    <Typography variant="caption-metadata" className="tw-text-ink-tertiary">
                      {tone}
                    </Typography>
                    <Status tone={tone} label={label} data-testid={`showcase-status-${tone}`} />
                  </div>
                ))}
              </div>
            </div>

            <Divider />

            {/* Alert */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Alert
              </Typography>
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mb-4 tw-block">
                `showAlert` overrides the tone with `danger` and appends a triangle. The
                triangle is `aria-hidden`; the `alertLabel` text beside it is what a screen
                reader receives — without it the alert was red-plus-a-glyph and announced
                nothing at all.
              </Typography>
              <div className="tw-flex tw-gap-6 tw-flex-wrap tw-items-start">
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <Typography variant="caption-metadata" className="tw-text-ink-tertiary">
                    default alertLabel
                  </Typography>
                  <Status
                    tone="success"
                    label="Promoted"
                    showAlert
                    data-testid="showcase-status-alert"
                  />
                </div>
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <Typography variant="caption-metadata" className="tw-text-ink-tertiary">
                    custom alertLabel
                  </Typography>
                  <Status
                    tone="success"
                    label="Promoted"
                    showAlert
                    alertLabel="Has unpromoted changes"
                    data-testid="showcase-status-alert-custom"
                  />
                </div>
              </div>
            </div>

            <Divider />

            {/* Live pulse (§ 223) */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                In flight (`pulse`)
              </Typography>
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mb-4 tw-block">
                Opt-in for a state that is still MOVING: a dot in the chip&apos;s own ink
                with a halo breathing out of it. Purely visual and `aria-hidden` — the
                label already says &quot;Generating&quot;. Under reduced motion the halo
                holds as a static dot. Never inferred from the label: only the caller
                knows the pipeline is still running.
              </Typography>
              <div className="tw-flex tw-gap-6 tw-flex-wrap tw-items-start">
                <Status tone="info" label="Generating" pulse data-testid="showcase-status-pulse-info" />
                <Status tone="accent" label="Promoting" pulse data-testid="showcase-status-pulse-accent" />
                <Status tone="neutral" label="Queued" pulse data-testid="showcase-status-pulse-neutral" />
              </div>
            </div>

            <Divider />

            {/* § 299 — the dot form, adopted from the Open decisions mock. */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                As a dot (`form=&quot;dot&quot;`)
              </Typography>
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mb-4 tw-block">
                For the dense column where every row has a state and a pill per row is a wall
                of tint: a 6px disc in the tone&rsquo;s glyph colour beside the label in plain ink.
                The word carries the meaning, the dot carries the tone. <code>pulse</code>{' '}
                breathes the dot itself; <code>showAlert</code> still turns it danger. The chip
                stays the default.
              </Typography>
              <div className="tw-inline-flex tw-flex-col tw-gap-2 tw-rounded-srf tw-border tw-border-edge tw-bg-surface tw-px-4 tw-py-3">
                <Status form="dot" tone="success" label="Promoted" data-testid="showcase-status-dot-success" />
                <Status form="dot" tone="info" label="Generating" pulse data-testid="showcase-status-dot-info" />
                <Status form="dot" tone="accent" label="Promoting" data-testid="showcase-status-dot-accent" />
                <Status form="dot" tone="warning" label="Generated" data-testid="showcase-status-dot-warning" />
                <Status form="dot" tone="danger" label="Failed" data-testid="showcase-status-dot-danger" />
                <Status form="dot" tone="neutral" label="Not promoted" data-testid="showcase-status-dot-neutral" />
              </div>
            </div>

            <Divider />

            {/* Timestamp */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                With timestamp
              </Typography>
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mb-4 tw-block">
                The timestamp is `caption-metadata` (12px). It was an arbitrary
                `tw-text-[10px]` until 2026-08-24 — below Material&apos;s 11px floor, the
                HIG&apos;s 11pt and GOV.UK&apos;s 14px, and off the type scale entirely.
              </Typography>
              <div className="tw-flex tw-gap-6 tw-flex-wrap tw-items-start">
                <Status
                  tone="warning"
                  label="Generated"
                  timestamp="13 Nov 2025, 11:11 AM UTC"
                  data-testid="showcase-status-timestamp"
                />
                <Status
                  tone="success"
                  label="Promoted"
                  timestamp="13 Nov 2025, 11:11 AM UTC"
                  showAlert
                  data-testid="showcase-status-timestamp-alert"
                />
              </div>
            </div>

            <Divider />

            {/* Live region */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Announced in place (`live`)
              </Typography>
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mb-4 tw-block">
                Opt-in `role="status"`, for a single chip whose state mutates where it
                stands. Deliberately off by default: a table that renders fifty chips after
                a fetch would insert fifty live regions.
              </Typography>
              <Status tone="info" label="Generating" live data-testid="showcase-status-live" />
            </div>

            <Divider />

            {/* Deprecated domain aliases */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Domain aliases (deprecated)
              </Typography>
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mb-4 tw-block">
                `variant` still resolves to a tone plus a default label so the migration can
                be incremental. Note that `notGenerated` and `notPromoted` land on the same
                tone — they always rendered identically and differed only in copy, which is
                why the prop was two props wearing one name.
              </Typography>
              <div className="tw-flex tw-gap-6 tw-flex-wrap tw-items-start">
                {STATUS_VARIANTS.map((variant) => (
                  <div key={variant} className="tw-flex tw-flex-col tw-gap-2">
                    <Typography variant="caption-metadata" className="tw-text-ink-tertiary">
                      {variant}
                    </Typography>
                    <Status variant={variant} data-testid={`showcase-status-variant-${variant}`} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Badge Section */}
        <section id="badge" data-testid="showcase-section-badge" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Badge
          </Typography>
          <DocBlock section="badge" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-6">
            {/* Basic Badges */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Warning (used in Primary)
              </Typography>
              <Badge variant="warning">8</Badge>
            </div>

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Primary (used in Primary Hover)
              </Typography>
              <Badge variant="primary">1</Badge>
            </div>

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Secondary (used in Secondary/Tertiary)
              </Typography>
              <Badge variant="secondary">5</Badge>
            </div>

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Disabled
              </Typography>
              <Badge variant="disabled">2</Badge>
            </div>

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Neutral &mdash; the quiet count
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                For a number that informs rather than demands &mdash; a per-section total in a
                filter menu, a result count. Sunken ground, hairline edge, secondary ink: the
                pill the Applications menu had to hand-token before this existed.
              </Typography>
              <Badge variant="neutral">12</Badge>
            </div>

            {/* § 300 — the count TICKS. A real Badge whose number the two buttons move:
                the figure rises in when it goes up, drops in when it goes down. */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                The count ticks
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                A count that changes in place is seen to change: the new figure rises in from
                below when the number went up and drops in from above when it went down. Only a
                numeric badge ticks, never on first mount, and <code>tabular-nums</code> holds
                the pill&rsquo;s width while it does.
              </Typography>
              <div className="tw-flex tw-items-center tw-gap-3">
                <Badge variant="secondary" data-testid="showcase-badge-ticker">{tickerCount}</Badge>
                <Button variant="secondary" size="sm" onClick={() => setTickerCount((n) => n + 1)} data-testid="showcase-badge-ticker-up">+1</Button>
                <Button variant="secondary" size="sm" onClick={() => setTickerCount((n) => Math.max(0, n - 1))} data-testid="showcase-badge-ticker-down">&minus;1</Button>
              </div>
            </div>

            <Divider />

            {/* § 303 — a count is formatted, and can be capped. */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Large counts &mdash; formatted, and capped with <code>max</code>
              </Typography>
              <div className="tw-flex tw-items-center tw-gap-3">
                <Badge variant="neutral" data-testid="showcase-badge-formatted">{1240}</Badge>
                <Badge variant="secondary" max={99} data-testid="showcase-badge-capped">{1240}</Badge>
                <Badge variant="selected" size="sm" max={9} data-testid="showcase-badge-capped-sm">{12}</Badge>
              </div>
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-3 tw-block">
                The full figure rides <code>title</code> and is read in full to assistive technology; only the pill is short.
              </Typography>
            </div>

            <Divider />

            {/* Badges with Icons */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Badges with Icons
              </Typography>
              <div className="tw-flex tw-gap-4 tw-flex-wrap tw-items-center">
                <Badge variant="warning" icon={<CrossCircle />}>
                  Warning
                </Badge>
                <Badge variant="primary" icon={<Cross />}>
                  Primary
                </Badge>
                <Badge variant="secondary" icon={<CrossCircle />}>
                  Secondary
                </Badge>
                <Badge variant="disabled" icon={<Cross />}>
                  Disabled
                </Badge>
              </div>
            </div>

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Badge with Icon Hidden
              </Typography>
              <div className="tw-flex tw-gap-4 tw-flex-wrap tw-items-center">
                <Badge variant="secondary" icon={<CrossCircle />} showIcon={false}>
                  Icon Hidden
                </Badge>
              </div>
            </div>
          </div>
        </section>

        {/* Alert — SectionMessage grammar (§ 63): neutral words, toned mark, no border */}
        <section id="alert" data-testid="showcase-section-alert" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Alert
          </Typography>
          <DocBlock section="alert" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-4">
            {SEMANTIC_TONES.map((tone) => (
              <Alert
                key={tone}
                tone={tone}
                title={`${tone.charAt(0).toUpperCase()}${tone.slice(1)}`}
                data-testid={`showcase-alert-${tone}`}
              >
                Body text is <span className="tw-text-code-formula">grey-700</span>, not
                {' '}<span className="tw-text-code-formula">grey-600</span> — the documented
                tertiary floor is 4.52:1 <em>on white</em> and only 3.75–4.19:1 on these tints.
              </Alert>
            ))}
          </div>

          <Divider className="tw-my-8" />

          <div className="tw-space-y-4">
            <Typography variant="title-header" className="tw-text-ink-tertiary">
              Dismissible, and with an action
            </Typography>
            <Alert
              tone="warning"
              title="3 categories have no mapping"
              actions={[
                {
                  label: 'Review them',
                  onClick: () => toast.info('Opening review'),
                  'data-testid': 'showcase-alert-actionable-action',
                },
                {
                  label: 'Skip publish',
                  onClick: () => toast.show('Publish skipped'),
                  'data-testid': 'showcase-alert-actionable-skip',
                },
              ]}
              onDismiss={() => toast.show('Alert dismissed')}
              data-testid="showcase-alert-actionable"
            >
              They will be skipped on publish.
            </Alert>
            <Alert tone="info" data-testid="showcase-alert-bodyonly">
              A one-line alert needs no title.
            </Alert>
            <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-block">
              An action does <strong>not</strong> dismiss the banner, unlike a Toast&apos;s: the
              condition it reports is usually still true while the action runs, and hiding it would
              hide the thing being fixed. There is no dismiss button unless you pass{' '}
              <span className="tw-text-code-formula">onDismiss</span>, and no{' '}
              <span className="tw-text-code-formula">role</span> unless you pass{' '}
              <span className="tw-text-code-formula">live</span> — most banners render with the page,
              and announcing on mount talks over the page load.
            </Typography>
          </div>
        </section>

        {/* Error summary (§ 365) — one announcement for a failed submit */}
        <section id="error-summary" data-testid="showcase-section-error-summary" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Error summary
          </Typography>
          <DocBlock section="error-summary" />
          <Divider className="tw-mb-6" />
          <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-6">
            Send the invite with the fields empty. The summary takes focus and is announced once;
            each line moves you to its field, and the fields do not announce their own errors.
            Correct one and the list follows without moving you.
          </Typography>
          <ErrorSummaryDemo />
        </section>

        {/*
          Toast. Note what this section does NOT do: render a `ToastRegion`.
          The viewport is mounted once in `Templates/Layout.tsx` for the whole app, so
          a second one here would double every toast AND every screen-reader
          announcement. These buttons only raise them.
        */}
        <section id="toast" data-testid="showcase-section-toast" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Toast
          </Typography>
          <DocBlock section="toast" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8">
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Tones
              </Typography>
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mb-4 tw-block">
                The surface stays white for every tone and only the glyph is coloured. Tinting
                each tone&apos;s ground is what made five of the reference kit&apos;s pairings fail
                AA — you cannot eyeball the contrast of a computed tint. Every glyph here clears
                4.5:1 on white.
              </Typography>
              <div className="tw-flex tw-gap-3 tw-flex-wrap">
                <Button variant="secondary" onClick={() => toast.show('Draft saved')} data-testid="showcase-toast-neutral">
                  neutral
                </Button>
                <Button variant="secondary" onClick={() => toast.info('Generating model…')} data-testid="showcase-toast-info">
                  info
                </Button>
                <Button variant="secondary" onClick={() => toast.success('Model promoted')} data-testid="showcase-toast-success">
                  success
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => toast.warning('3 categories have no mapping', {
                    description: 'They will be skipped on publish.',
                  })}
                  data-testid="showcase-toast-warning"
                >
                  warning
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => toast.error('Could not save', {
                    description: 'The submodel is locked by another user.',
                  })}
                  data-testid="showcase-toast-danger"
                >
                  danger (sticky)
                </Button>
              </div>
            </div>

            <Divider />

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Timing and dismissal
              </Typography>
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mb-4 tw-block">
                Hover or focus a toast and its timer pauses, resuming from the remainder rather
                than restarting — which is what makes an auto-dismissing message allowed under
                SC 2.2.1. <strong>danger</strong> never auto-dismisses at all: an error that
                vanishes before it is read is the case that criterion exists for.
              </Typography>
              <div className="tw-flex tw-gap-3 tw-flex-wrap">
                <Button
                  variant="secondary"
                  onClick={() => toast.error('Version conflict', {
                    description: 'This model changed since you opened it.',
                    action: { label: 'Reload', onClick: () => toast.success('Reloaded') },
                  })}
                  data-testid="showcase-toast-action"
                >
                  with an action
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    toast.info('Saving…', { id: 'showcase-save', duration: null });
                    window.setTimeout(() => toast.success('Saved', { id: 'showcase-save' }), 1200);
                  }}
                  data-testid="showcase-toast-replace"
                >
                  replace in place
                </Button>
                <Button variant="secondary" onClick={() => toast.dismissAll()} data-testid="showcase-toast-dismiss-all">
                  dismiss all
                </Button>
                {/* § 303 — loading → success | error as one call, replaced in place. */}
                <Button
                  variant="secondary"
                  onClick={() => {
                    toast.promise(new Promise<number>((resolve) => { setTimeout(() => resolve(12), 1500); }), {
                      loading: 'Promoting model…',
                      success: (n) => `Promoted ${n} products`,
                      error: 'Promotion failed',
                    });
                  }}
                  data-testid="showcase-toast-promise"
                >
                  promise → success
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    toast.promise(new Promise((_, reject) => { setTimeout(() => reject(new Error('locked')), 1500); }), {
                      loading: 'Promoting model…',
                      success: 'Promoted',
                      error: (e) => `Promotion failed: ${e instanceof Error ? e.message : 'unknown'}`,
                    }).catch(() => {});
                  }}
                  data-testid="showcase-toast-promise-error"
                >
                  promise → error
                </Button>
              </div>
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-4 tw-block">
                &ldquo;Replace in place&rdquo; reuses one id, so the toast is updated rather than
                stacked — and it still announces the second message, which keying the announcer on
                the id alone silently broke. At most three are shown; a fourth drops the oldest one that would
                have left on its own &mdash; never an unread error, and a repeated message is announced again.
              </Typography>
            </div>
          </div>
        </section>

        {/* Tooltip (§ 171) — used across the app, demoed nowhere. */}
        <section id="tooltip" data-testid="showcase-section-tooltip" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Tooltip
          </Typography>
          <DocBlock section="tooltip" />
          <Divider className="tw-mb-6" />
          <div className="tw-flex tw-flex-wrap tw-gap-4">
            {(['top', 'bottom', 'left', 'right'] as const).map((placement) => (
              <Tooltip
                key={placement}
                placement={placement}
                content={`Placed ${placement} of the trigger`}
                data-testid={`showcase-tooltip-${placement}`}
              >
                <Button variant="secondary" data-testid={`showcase-tooltip-${placement}-trigger`}>
                  {placement}
                </Button>
              </Tooltip>
            ))}
            {/* § 303 — a tooltip that names the key: caps after the words, told on the trigger. */}
            <Tooltip content="Save changes" shortcut="Meta+S" data-testid="showcase-tooltip-shortcut">
              <Button variant="secondary" data-testid="showcase-tooltip-shortcut-trigger">
                with a shortcut
              </Button>
            </Tooltip>
          </div>
          <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-3 tw-block">
            The bubble renders into the page body, so a table or a card cannot clip it. Near an edge
            it flips to the side with room rather than covering its trigger, and Escape closes it
            from anywhere &mdash; including a bubble opened by hover.
          </Typography>
        </section>

        {/* Loading (§ 62) — the three-tier architecture, tiers 1 and 3 demoed.
            Tier 2 (a component's own `loading` state) is in the Table section above. */}
        <section id="loading" data-testid="showcase-section-loading" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Loading
          </Typography>
          <DocBlock section="loading" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8">
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                PageSkeleton — list preset (breadcrumb, title, toolbar, table)
              </Typography>
              <PageSkeleton
                preset="list"
                rows={3}
                label="Loading products"
                data-testid="showcase-page-skeleton-list"
              />
            </div>
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                PageSkeleton — detail preset (breadcrumb, title, fields)
              </Typography>
              <PageSkeleton
                preset="detail"
                rows={3}
                label="Loading product details"
                data-testid="showcase-page-skeleton-detail"
              />
            </div>
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                PageSkeleton — summary-list preset (breadcrumb, summary card, split toolbar, table, pagination)
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                The Admin party/domain/channel shape: a tinted card of the parent record above the
                child list. No title row — the card carries the title — and the toolbar is split,
                search on one side and the create action on the other. Added because Manage Domain
                was reaching for the detail preset, which drew five form fields that never arrived.
              </Typography>
              <PageSkeleton
                preset="summary-list"
                rows={3}
                label="Loading party details"
                data-testid="showcase-page-skeleton-summary-list"
              />
            </div>
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                PageSkeleton &mdash; explorer preset (a 30% rail beside a detail card)
              </Typography>
              <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                The two-column workspace: the ISR Data Model and UI Builder tab shape. It is the
                first preset that is not a whole page &mdash; it renders inside a shell whose header
                and tabs stay on screen through the fetch, so it draws neither breadcrumb nor title.
                It fills the height it is given rather than sizing itself, so pass the same height
                class the real content uses.
              </Typography>
              <PageSkeleton
                preset="explorer"
                rows={5}
                label="Loading data model"
                className="tw-h-96 tw-border tw-border-edge tw-rounded-srf tw-px-3"
                data-testid="showcase-page-skeleton-explorer"
              />
            </div>
          </div>
        </section>

        {/* Spinner & Skeleton (§ 171). Tier 1 and tier 3 of the loading architecture had a
            section; the two primitives underneath them did not. */}
        <section id="skeleton" data-testid="showcase-section-skeleton" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Spinner &amp; skeleton
          </Typography>
          <DocBlock section="skeleton" />
          <Divider className="tw-mb-6" />
          <div className="tw-mb-8 tw-flex tw-flex-wrap tw-items-center tw-gap-8">
            {(['sm', 'md', 'lg'] as const).map((size) => (
              <Spinner key={size} size={size} label={`Loading ${size}`} data-testid={`showcase-spinner-${size}`} />
            ))}
            <Spinner size="md" label="Loading products" showLabel data-testid="showcase-spinner-labelled" />
            {/* The § 219 arc candidate was ADOPTED (§ 220) — the Spinners above ARE
                the rounded-cap arc now, so the side-by-side mock retired. */}
          </div>
          <div className="tw-grid tw-gap-6 md:tw-grid-cols-3">
            <div data-testid="showcase-skeleton-text">
              <span className="tw-mb-2 tw-block tw-text-sub-title-label tw-text-ink">text</span>
              <Skeleton variant="text" lines={3} />
            </div>
            <div data-testid="showcase-skeleton-rect">
              <span className="tw-mb-2 tw-block tw-text-sub-title-label tw-text-ink">rect</span>
              <Skeleton variant="rect" height="64px" />
            </div>
            <div data-testid="showcase-skeleton-circle">
              <span className="tw-mb-2 tw-block tw-text-sub-title-label tw-text-ink">circle</span>
              <Skeleton variant="circle" width="64px" height="64px" />
            </div>
          </div>
        </section>

        {/* Progress (§ 211) */}
        <section id="progress" data-testid="showcase-section-progress" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Progress &amp; stepper
          </Typography>
          <DocBlock section="progress" />
          <Divider className="tw-mb-6" />
          <div className="tw-space-y-6 tw-max-w-md">
            <div className="tw-space-y-1">
              <Progress value={importPct} label="Importing products" showValue data-testid="showcase-progress-live" />
              <Button
                variant="subtle"
                size="sm"
                onClick={() => setImportPct((p) => (p >= 100 ? 0 : Math.min(100, p + 13)))}
                data-testid="showcase-progress-advance"
              >
                Advance the fake import
              </Button>
            </div>
            <Progress value={100} label="Import complete" tone="success" showValue data-testid="showcase-progress-success" />
            <Progress value={80} label="Import degraded" tone="warning" showValue data-testid="showcase-progress-warning" />
            <Progress value={45} label="Import failed" tone="danger" showValue data-testid="showcase-progress-danger" />
            <div>
              <Typography variant="caption-metadata" className="tw-block tw-text-ink-tertiary tw-mb-2">
                sm &mdash; inline, table cells
              </Typography>
              <Progress value={62} label="Row sync" size="sm" data-testid="showcase-progress-sm" />
            </div>
          </div>
          {/* § 372 — where the user is in a fixed sequence; only completed steps navigate. */}
          <div className="tw-mt-8">
            <h4 className="tw-text-title-header tw-text-ink-tertiary tw-mb-4">
              Stepper &mdash; named steps, the current one marked
            </h4>
            <StepperDemo />
          </div>
        </section>

        {/* Empty states (§ 64) — EmptyState existed since § 23 but was never demoed,
            which is how it came to be re-requested as "missing". The md tier carries the
            house illustration, tokenized (navy in light, cyan in dark). */}
        <section id="empty-states" data-testid="showcase-section-empty-states" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Empty states
          </Typography>
          <DocBlock section="empty-states" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8">
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Page level — md, illustration, action
              </Typography>
              <EmptyState
                title="No products yet"
                description="Products appear here once a category has been imported for this environment."
                illustration={<NoDataIllustration />}
                action={
                  <Button data-testid="showcase-empty-state-page-action">Create product</Button>
                }
                data-testid="showcase-empty-state-page"
              />
            </div>
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Panel level — sm, copy only
              </Typography>
              <div className="tw-border tw-border-edge tw-rounded-srf">
                <EmptyState
                  size="sm"
                  title="No attributes match this filter"
                  description="Try widening the search."
                  data-testid="showcase-empty-state-panel"
                />
              </div>
            </div>
            {/* ImagePlaceholder (§ 191) — a single media slot whose value is absent.
                Not an EmptyState: the page has content, one thumbnail doesn't. */}
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Image placeholder — a media slot, not a page state
              </Typography>
              <div className="tw-flex tw-items-end tw-gap-6">
                <ImagePlaceholder
                  width="150px"
                  height="150px"
                  data-testid="showcase-image-placeholder"
                />
                <ImagePlaceholder
                  width="64px"
                  height="64px"
                  showLabel={false}
                  data-testid="showcase-image-placeholder-thumb"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Error state (§ 171) — the fourth observable state every module doc demands, and
            the only one this page never showed. */}
        <section id="error-state" data-testid="showcase-section-error-state" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Error state
          </Typography>
          <DocBlock section="error-state" />
          <Divider className="tw-mb-6" />
          <div className="tw-grid tw-gap-6 lg:tw-grid-cols-2">
            <div className="tw-rounded-srf tw-border tw-border-edge tw-p-4">
              <span className="tw-mb-3 tw-block tw-text-sub-title-label tw-text-ink-tertiary">
                size=&quot;sm&quot; — inside a panel or table
              </span>
              <ErrorState
                size="sm"
                title="Could not load attributes"
                description="The catalog service did not respond."
                action={<Button variant="secondary" size="sm">Retry</Button>}
                data-testid="showcase-error-sm"
              />
            </div>
            <div className="tw-rounded-srf tw-border tw-border-edge tw-p-4">
              <span className="tw-mb-3 tw-block tw-text-sub-title-label tw-text-ink-tertiary">
                size=&quot;md&quot; — a whole route
              </span>
              <ErrorState
                size="md"
                title="Something went wrong"
                description="Reload the page, or come back in a few minutes."
                detail="TRACE 4f21a9c0"
                action={<Button>Reload</Button>}
                data-testid="showcase-error-md"
              />
            </div>
          </div>
        </section>

        <GroupHeading
          id="overlays"
          label="Overlays"
          blurb="Surfaces that sit above the page and take focus with them."
          first={false}
        />

        {/* Overlays Section — Dialog and SidePanel were never demoed here */}
        <section id="overlays" data-testid="showcase-section-overlays" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Dialog &amp; side panel
          </Typography>
          <DocBlock section="overlays" />
          <Divider className="tw-mb-6" />

          <div className="tw-space-y-8">
            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Dialog
              </Typography>
              <div className="tw-flex tw-gap-2">
                <Button onClick={() => setDialogOpen(true)} data-testid="showcase-open-dialog">
                  Open dialog
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setDangerDialogOpen(true)}
                  data-testid="showcase-open-danger-dialog"
                >
                  Open danger confirm
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setConfirmOpen(true)}
                  data-testid="showcase-open-confirm"
                >
                  Open ConfirmDialog
                </Button>
              </div>
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-3">
                Escape closes it and focus returns to this button. Tab cycles inside the panel rather
                than escaping behind it. Outside-click is deliberately off — its real consumer holds
                unsaved edits. The danger confirm shows the tone glyph beside the title, and its long
                body demonstrates the scroll-aware chrome: the header&rsquo;s shadow appears once
                content has scrolled under it, the footer&rsquo;s while more waits below.
              </Typography>
            </div>

            <Divider />

            <div>
              <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-4">
                Side Panel
              </Typography>
              <Button onClick={() => setSidePanelOpen(true)} data-testid="showcase-open-sidepanel">
                Open side panel
              </Button>
              <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-3">
                Slides in from the right and slides back out. Escape or the backdrop closes it; its
                three tabs move with the arrow keys.
              </Typography>
            </div>
          </div>
        </section>

        {/* Popover (§ 248) */}
        <section id="popover" data-testid="showcase-section-popover" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Popover
          </Typography>
          <DocBlock section="popover" />
          <Divider className="tw-mb-6" />
          <Popover
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
            aria-label="Filter products"
            trigger={<Button variant="secondary" data-testid="showcase-open-popover">Filters</Button>}
            data-testid="showcase-popover"
          >
            <div className="tw-flex tw-flex-col tw-gap-3 tw-w-64">
              <TextField
                label="Name contains"
                value={popoverDraft}
                onChange={setPopoverDraft}
                placeholder="e.g. bolt"
                data-testid="showcase-popover-field"
              />
              <div className="tw-flex tw-justify-end tw-gap-2">
                <Button variant="tertiary" onClick={() => setPopoverOpen(false)}>Cancel</Button>
                <Button onClick={() => setPopoverOpen(false)} data-testid="showcase-popover-apply">Apply</Button>
              </div>
            </div>
          </Popover>
          <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-3 tw-block">
            Open it, then scroll: the surface follows its trigger and flips when the viewport runs out.
            Escape closes it and returns focus to the button.
          </Typography>

          {/* § 304 — the four anchored surfaces are PORTALLED by default: no overflow:hidden
              ancestor can clip them. The box below IS that ancestor — a DS Table cell, a
              Dialog body, a Card — and every surface opened from inside it escapes. */}
          <Typography variant="title-header" as="h3" className="tw-mt-8 tw-mb-2 tw-text-ink">
            Nothing clips a portalled surface
          </Typography>
          <Typography variant="body-primary" className="tw-mb-4 tw-max-w-[68ch] tw-text-ink-secondary">
            The dashed box is <code>overflow: hidden</code> and 96px tall. Dropdown, Combobox,
            DropdownButton and Popover all render into <code>document.body</code> at a fixed
            position on the <code>popover</code> rung, re-measured while open &mdash; so each one
            opens past the box&rsquo;s edge. Tab out of any of them returns focus to its trigger.
            <code>usePortal={'{false}'}</code> is the inline surface, which this box would clip.
          </Typography>
          <div
            data-testid="showcase-portal-clip"
            className="tw-relative tw-h-24 tw-overflow-hidden tw-rounded-srf tw-border tw-border-dashed tw-border-edge-strong tw-bg-surface-sunken tw-px-4 tw-py-3"
          >
            <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-4">
              <div className="tw-w-52">
                <Dropdown
                  label="Supplier"
                  options={PORTAL_SUPPLIERS}
                  value={portalSupplier}
                  onChange={setPortalSupplier}
                  data-testid="showcase-portal-dropdown"
                />
              </div>
              <div className="tw-w-60">
                <Combobox
                  label="Part"
                  options={PART_OPTIONS}
                  value={portalPart}
                  onChange={setPortalPart}
                  placeholder="Search parts"
                  data-testid="showcase-portal-combobox"
                />
              </div>
              <DropdownButton
                label="Export"
                variant="subtle"
                options={[
                  { id: 'csv', label: 'CSV' },
                  { id: 'xlsx', label: 'Excel workbook' },
                  { id: 'json', label: 'JSON' },
                ]}
                onSelect={() => {}}
                data-testid="showcase-portal-dropdownbutton"
              />
              <Popover
                open={portalPopoverOpen}
                onOpenChange={setPortalPopoverOpen}
                aria-label="Column visibility"
                trigger={<Button variant="secondary" data-testid="showcase-portal-popover-trigger">Columns</Button>}
                data-testid="showcase-portal-popover"
              >
                <div className="tw-flex tw-flex-col tw-gap-3 tw-w-56">
                  <Typography variant="body-primary" className="tw-text-ink">
                    A surface opened from inside a clipped box, rendered whole.
                  </Typography>
                  <div className="tw-flex tw-justify-end">
                    <Button size="sm" onClick={() => setPortalPopoverOpen(false)} data-testid="showcase-portal-popover-done">Done</Button>
                  </div>
                </div>
              </Popover>
            </div>
          </div>
        </section>

        {/* CommandPalette (§ 305) */}
        <section id="command-palette" data-testid="showcase-section-command-palette" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Command palette
          </Typography>
          <DocBlock section="command-palette" />
          <Divider className="tw-mb-6" />
          <div className="tw-flex tw-items-center tw-gap-3">
            <Button variant="secondary" onClick={() => setPaletteOpen(true)} data-testid="showcase-open-command-palette">
              Open command palette
            </Button>
            <Typography variant="caption-metadata" className="tw-text-ink-tertiary" data-testid="showcase-command-palette-readout">
              {paletteLast ? `Last ran: ${paletteLast}` : 'Nothing run yet'}
            </Typography>
          </div>
          <Typography variant="caption-metadata" className="tw-text-ink-tertiary tw-mt-3 tw-block">
            Type to filter &mdash; a label that starts with the query ranks first, then one that
            contains it, then keywords. Arrows move, Enter runs and closes, Escape closes and
            returns focus to the button. The app shell binds the key that opens it; the
            palette does not.
          </Typography>
          <CommandPalette
            open={paletteOpen}
            onOpenChange={setPaletteOpen}
            commands={paletteCommands}
            onSelect={(command) => setPaletteLast(command.label)}
            data-testid="showcase-command-palette"
          />
        </section>

        <GroupHeading
          id="appendix"
          label="Appendix"
          blurb="Choices still open, kept where they can be judged by eye instead of argued from memory."
          first={false}
        />

        {/* The candidate strips, collected. They were inline with the foundations they
            belong to, which put working notes in the middle of reference documentation —
            a reader looking up the radius ramp met three mock cards arguing about it.
            They are still a live decision surface, so they are collapsed, not deleted:
            each one is a comparison the tokens cannot make, because a token can only
            ever show the value it currently holds. */}
        <section id="open-decisions" data-testid="showcase-section-open-decisions" className={SECTION}>
          <Typography variant="heading-1" className="tw-mb-4 tw-text-ink">
            Open decisions
          </Typography>
          <Typography variant="body-primary" className="tw-mb-6 tw-max-w-[68ch] tw-text-ink-secondary">
            Each strip puts a settled value beside the alternatives it beat, so a choice can be
            reopened by looking rather than by remembering. Nothing here is documentation &mdash;
            the live values are in the sections above.
          </Typography>
          <div className="tw-space-y-2">
            <Accordion
              title="Radius candidates"
              data-testid="showcase-decision-radius"
            >
              <Typography variant="body-primary" className="tw-mb-4 tw-max-w-[68ch] tw-text-ink-secondary">
                Mock cards drawn with arbitrary radii, deliberately NOT the tokens &mdash; so the live ramp can be dialled with one config edit if a different row reads better.
              </Typography>
            {/* The ramp CANDIDATES, for eyes-on comparison only — mock cards drawn with
                arbitrary radii, deliberately NOT the tokens, so the live ramp above can
                be dialled with one config edit if a different row reads better. */}
            <Typography variant="title-header" as="h3" className="tw-mt-8 tw-mb-2 tw-text-ink">
              Radius candidates — pick with eyes, not taste memos
            </Typography>
            <div className="tw-flex tw-flex-wrap tw-gap-6" data-testid="showcase-radius-candidates">
              {[
                { label: 'flat 4 / 4 / 4 (the old flat ramp)', ctl: 'tw-rounded-[4px]', srf: 'tw-rounded-[4px]', ovl: 'tw-rounded-[4px]' },
                { label: 'gentle 4 / 6 / 8', ctl: 'tw-rounded-[4px]', srf: 'tw-rounded-[6px]', ovl: 'tw-rounded-[8px]' },
                { label: 'LIVE 4 / 8 / 12', ctl: 'tw-rounded-[4px]', srf: 'tw-rounded-[8px]', ovl: 'tw-rounded-[12px]' },
              ].map((c) => (
                <div key={c.label} className="tw-w-52">
                  <div className={`tw-border tw-border-edge tw-bg-surface tw-shadow-ds-md tw-p-3 ${c.ovl}`}>
                    <div className={`tw-border tw-border-edge tw-bg-surface-sunken tw-p-3 tw-mb-2 ${c.srf}`}>
                      <div className={`tw-h-8 tw-border tw-border-edge-strong tw-bg-surface ${c.ctl}`} />
                    </div>
                    <div className={`tw-h-7 tw-w-20 tw-bg-accent-bold ${c.ctl}`} />
                  </div>
                  <span className="tw-mt-2 tw-block tw-text-caption-metadata tw-text-ink-tertiary">{c.label}</span>
                </div>
              ))}
            </div>
            </Accordion>
            <Accordion
              title="Selection-ground candidates"
              data-testid="showcase-decision-selection"
            >
              <Typography variant="body-primary" className="tw-mb-4 tw-max-w-[68ch] tw-text-ink-secondary">
                The selection treatment on trial. Judge in LIGHT; inline values on purpose, because these are mocks rather than classes.
              </Typography>
              {/* § 226 → § 252: the selection-ground candidates — judged in LIGHT (dark
                  keeps #1e3844, which § 225 already separated from its chips). */}
              <div>
                <h4 className="tw-text-title-header tw-text-ink-tertiary tw-mb-4">
                  Selection-ground candidates
                </h4>
                <Typography variant="body-primary" className="tw-text-ink-secondary tw-mb-4">
                  The selection treatment, on trial three times. navy-100 and navy-150
                  were both rejected by eye as lavender — and the numbers say why: the brand
                  ramp&apos;s hue is locked at OKLCH ~266°, royal when dark, periwinkle once
                  pale, where every published blue ramp rotates toward cyan. The fix therefore
                  changes the HUE, not the strength — first ADS blue-75 at 255° (read too vivid
                  once the tray pill made the fill the loudest element), then the LIVE soft blue
                  #bfdbf7 at 248° with the chroma eased, the quietest blue on Coolors&apos;
                  popular-blue page that clears every floor. Each mock menu shows selected /
                  plain / hovered rows plus the chip stack above; inline values on purpose
                  (mocks, not classes). Judge in LIGHT. navy-200 stays for honesty
                  (ink-secondary 4.27, under AA); bar/keyline/fill are grammar changes
                  if adopted — row selection would split from the token&apos;s chip-and-hover
                  duties.
                </Typography>
                <div className="tw-flex tw-flex-wrap tw-gap-4" data-testid="showcase-selection-candidates">
                  {[
                    { label: 'navy-100 — the original (1.30 vs white)', value: '#d5e2ff' },
                    { label: 'navy-150 — second cut (1.48 · chips 1.23) — read lavender', value: '#c2d4fe' },
                    { label: 'blue-75 — hue rotation, first cut at 255° (1.52 · chips 1.27) — read too vivid', value: '#b3d4ff' },
                    { label: 'soft blue — hue rotation, second cut at 248° (1.43 · chips 1.19) — retired by the retheme', value: '#bfdbf7' },
                    { label: 'LIVE — cobalt-derived, hue 250° (1.41 · chips 1.17 · 2° ink 7.28)', value: '#bcddff' },
                    { label: 'Material blue-100 — runner-up, hue 243° (1.40 · chips 1.17)', value: '#bbdefb' },
                    { label: 'navy-200 — brand rung (ink-secondary 4.27 ✗)', value: '#b0c8fc' },
                    { label: 'keyline — navy-50 wash + accent ring (a grammar change)', value: '#eef4ff', ring: true },
                    /* The structural treatments — the row stops being a lavender slab
                       entirely. Each is a § 51 grammar change if adopted. */
                    { label: 'bar — 3px accent bar + navy-50 wash (ADS/VS Code selected-row)', value: '#eef4ff', bar: true },
                    { label: 'fill — accent-bold + inverse ink (native-menu decisive; heavy on table rows)', value: '#091caa', inverse: true },
                    { label: 'neutral — grey wash, the check carries it (weak vs hover 1.13)', value: '#dadbdd' },
                  ].map((c) => (
                    <div key={c.label} className="tw-w-56">
                      <div className="tw-overflow-hidden tw-rounded-srf tw-border tw-border-edge tw-bg-surface">
                        <div
                          className={`tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-3 tw-py-2 tw-text-body-primary ${
                            c.inverse ? 'tw-text-ink-inverse' : 'tw-text-accent'
                          }`}
                          style={{
                            backgroundColor: c.value,
                            ...(c.ring ? { boxShadow: 'inset 0 0 0 1.5px var(--ds-accent-hover)' } : {}),
                            ...(c.bar ? { boxShadow: 'inset 3px 0 0 var(--ds-accent-bold)' } : {}),
                          }}
                          data-selection={c.value}
                        >
                          <span className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                            <span className="tw-truncate">Compatio</span>
                            {/* The § 225 stack, judged per candidate. */}
                            <Tag label="IAC" tone="info" />
                          </span>
                          <svg width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true" className="tw-shrink-0">
                            <path d="M11 1L4.125 7L1 4.27273" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                        <div className="tw-px-3 tw-py-2 tw-text-body-primary tw-text-ink">Midway-demo</div>
                        <div className="tw-bg-surface-sunken tw-px-3 tw-py-2 tw-text-body-primary tw-text-ink">
                          SunshineBikeShop
                          <span className="tw-ml-2 tw-text-caption-metadata tw-text-ink-tertiary">(hovered)</span>
                        </div>
                      </div>
                      <span className="tw-mt-2 tw-block tw-text-caption-metadata tw-text-ink-tertiary">
                        {c.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Accordion>
            <Accordion
              title="Dark scrim candidates"
              data-testid="showcase-decision-scrim"
            >
              <Typography variant="body-primary" className="tw-mb-4 tw-max-w-[68ch] tw-text-ink-secondary">
                The same mock page under different scrim strengths, with a panel floating on it so separation is part of the judgement. Flip the preview to dark first &mdash; the light scrim is settled.
              </Typography>
              {/* § 218 DARK SCRIM CANDIDATES — the radius-candidates arrangement: each
                  card is the SAME mock page under a different scrim strength (inline
                  backgrounds, deliberately not the token) with a mock panel floating on
                  it, so the strengths compare side by side where the real Dialog can
                  only show one. Judge this IN DARK — light's scrim (#172b4d80) is
                  settled. The measured story (ink-behind residual / panel separation):
                  navy-50% 3.18/1.07 (the old invisible layer), 55% 2.65/1.34,
                  LIVE 65% 2.00/1.38, 76% 1.50/1.42; matching light's 3.0x dimming
                  ratio lands ~2.5:1, between 55% and the live 65%. */}
              <div>
                <Typography variant="title-header" className="tw-text-ink-tertiary tw-mb-2">
                  Dark scrim candidates — flip the preview to dark, then pick
                </Typography>
                <div className="tw-flex tw-flex-wrap tw-gap-4" data-testid="showcase-scrim-candidates">
                  {[
                    { label: 'navy 50% — the original', value: '#172b4d80' },
                    { label: 'soft 55% — matches light’s dimming ratio', value: '#01040e8c' },
                    { label: '65% near-black — retired by the retheme', value: '#01040ea6' },
                    { label: 'LIVE 70% slate sunken', value: '#0b1220b3' },
                    { label: 'strong 76%', value: '#01040ec2' },
                  ].map((c) => (
                    <div key={c.value} className="tw-w-56">
                      <div className="tw-relative tw-overflow-hidden tw-rounded-srf tw-border tw-border-edge tw-bg-surface tw-p-3">
                        {/* The busy page behind: real inks so dimming is judged on
                            what a page actually holds. */}
                        <div className="tw-space-y-1.5">
                          <div className="tw-text-title-header tw-text-ink">Products</div>
                          <div className="tw-text-caption-metadata tw-text-ink-secondary">
                            128 results · updated 2m ago
                          </div>
                          <div className="tw-h-2 tw-rounded tw-bg-surface-sunken" />
                          <div className="tw-h-2 tw-w-4/5 tw-rounded tw-bg-surface-sunken" />
                          <div className="tw-inline-block tw-rounded-full tw-bg-tone-info-tint tw-px-2 tw-text-caption-metadata tw-text-tone-info-ink">
                            Individual
                          </div>
                        </div>
                        {/* The candidate scrim + the same blur the real token pairs with. */}
                        <div
                          aria-hidden="true"
                          className="tw-absolute tw-inset-0 tw-backdrop-blur-sm"
                          style={{ backgroundColor: c.value }}
                          data-scrim={c.value}
                        />
                        {/* A mock floating panel, so separation is part of the judgement. */}
                        <div className="tw-absolute tw-inset-x-6 tw-top-1/2 -tw-translate-y-1/2 tw-rounded-ovl tw-bg-surface-raised tw-shadow-ds-lg tw-p-2">
                          <div className="tw-text-sub-title-label tw-text-ink">Confirm</div>
                          <div className="tw-mt-1 tw-h-1.5 tw-rounded tw-bg-surface-sunken" />
                        </div>
                      </div>
                      <span className="tw-mt-2 tw-block tw-text-caption-metadata tw-text-ink-tertiary">
                        {c.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Accordion>
            {/* § 300 — the GLASS CHROME candidate (a scrolled head that let the rows show
                through at 72% / 55%) was judged here 2026-09-15 and the solid sunken band
                STAYS: in dark the head is already the page colour and there is nothing to see
                through to, so the treatment spent a blur to gain 0.03 of separation. Decided —
                the strip is gone; do not re-propose. */}
            {/* § 301 — the DARK TOOLTIP candidate (inverse / raised / row-hover, mocked over one
                row) was judged the same day on live field-help bubbles, dark first and then
                light: RAISED, in both themes. It lives in Tooltip now — the tooltip-surface /
                tooltip-ink pair and the ds-tooltip chrome token — and Foundations › Roles draws
                the bubble, so this strip is gone too. Hover any ⓘ on this page to see it. */}
          </div>
        </section>
        </main>

      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete this domain?"
        tone="danger"
        message={<>Every channel under <strong>acme-fasteners.com</strong> loses its mapping.</>}
        secondaryMessage="This cannot be undone."
        confirmText="Delete domain"
        busyText="Deleting…"
        loading={confirmLoading}
        onConfirm={() => {
          setConfirmLoading(true);
          window.setTimeout(() => {
            setConfirmLoading(false);
            setConfirmOpen(false);
          }, 1200);
        }}
        onCancel={() => setConfirmOpen(false)}
        data-testid="showcase-confirm"
      />

      <Dialog
        open={dangerDialogOpen}
        onClose={() => setDangerDialogOpen(false)}
        title="Delete 32 attribute values?"
        tone="danger"
        size="sm"
        data-testid="showcase-danger-dialog"
        footer={
          <div className="tw-flex tw-gap-2 tw-justify-end">
            <Button variant="secondary" onClick={() => setDangerDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => setDangerDialogOpen(false)}>Delete values</Button>
          </div>
        }
      >
        <div className="tw-space-y-3">
          <Typography variant="body-primary" className="tw-text-ink-secondary">
            This removes the values from every product that references them. The list below is
            long on purpose — scroll it and watch the header and footer shadows.
          </Typography>
          {Array.from({ length: 32 }, (_, i) => (
            <Typography key={i} variant="caption-metadata" className="tw-text-ink-tertiary tw-block">
              {`Value ${String(i + 1).padStart(2, '0')} — referenced by ${3 + ((i * 7) % 40)} products`}
            </Typography>
          ))}
        </div>
      </Dialog>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Attribute Values"
        data-testid="showcase-dialog"
        footer={
          <div className="tw-flex tw-gap-2 tw-justify-end">
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => setDialogOpen(false)}>Save</Button>
          </div>
        }
      >
        <div className="tw-space-y-4">
          <TextField
            label="Value name"
            value={textFieldValue}
            onChange={setTextFieldValue}
            placeholder="e.g. 6204-2RS"
          />
          {/*
            `usePortal` is not optional here. Without it the menu renders
            `tw-absolute` inside the dialog's `tw-overflow-y-auto` content area,
            which clips it — the open list gets cut off at the footer. Every
            Dropdown that lives inside a scrolling or clipping container needs
            this; the eleven call sites in the app that pass it are all inside
            tables, for the same reason.
          */}
          <Dropdown
            label="Category"
            options={dropdownOptions}
            value={dropdownValue}
            onChange={setDropdownValue}
            usePortal
            data-testid="showcase-dialog-dropdown"
          />
        </div>
      </Dialog>

      <SidePanel
        open={sidePanelOpen}
        onClose={() => setSidePanelOpen(false)}
        title="Environment"
        tabs={SHOWCASE_PANEL_TABS}
        activeTab={sidePanelTab}
        onTabChange={setSidePanelTab}
        data-testid="showcase-sidepanel"
      >
        <Typography variant="body-primary" className="tw-text-ink-secondary">
          Panel content for the “{sidePanelTab}” tab.
        </Typography>
      </SidePanel>
    </div>
  );
};

export default DesignSystemShowcase;
